import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import DatePicker from "../../components/DatePicker";
import StudentDetailModal from "../../components/StudentDetailModal";
import RejectReasonDialog from "../../components/RejectReasonDialog";
import { ChargeInfoButton, ChargePaymentsModal } from "./ChargePaymentsModal";
import PaymentReceiptModal from "./PaymentReceiptModal";
import s from "./Tuition.module.css";

// ── Date-range helpers for the Payments tab filter ─────────────────────────
const PAY_RANGES = [
  { value: "all",    label: "All time" },
  { value: "today",  label: "Today" },
  { value: "7",      label: "Last 7 days" },
  { value: "30",     label: "Last 30 days" },
  { value: "90",     label: "Last 90 days" },
  { value: "365",    label: "Last 12 months" },
  { value: "custom", label: "Custom range…" },
];

function isoToday(dayOffset = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (dayOffset) d.setDate(d.getDate() + dayOffset);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function isoDateToStart(iso) {
  if (!iso || typeof iso !== "string") return NaN;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

function isoDateToEnd(iso) {
  const t = isoDateToStart(iso);
  return Number.isNaN(t) ? NaN : t + 24 * 60 * 60 * 1000;
}

function computePayBounds(range, customFrom, customTo) {
  if (range === "all") return { from: 0, to: Infinity };
  if (range === "today") {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    return { from: d.getTime(), to: Infinity };
  }
  if (range === "custom") {
    const from = isoDateToStart(customFrom);
    const to   = isoDateToEnd(customTo);
    return { from: Number.isNaN(from) ? 0 : from, to: Number.isNaN(to) ? Infinity : to };
  }
  return { from: Date.now() - Number(range) * 24 * 60 * 60 * 1000, to: Infinity };
}

function paymentTs(p, field) {
  if (field === "entered") return new Date(p.paidAt || 0).getTime();
  if (p.paymentReceivedOn) return isoDateToStart(p.paymentReceivedOn);
  return new Date(p.paidAt || 0).getTime();
}
// ───────────────────────────────────────────────────────────────────────────

const TABS = [
  { key: "fee-structures",    label: "Fee Structures" },
  { key: "student-discounts", label: "Discounts" },
  { key: "tuition-invoices",  label: "Invoices" },
  { key: "tuition-payments",  label: "Payments" },
];

const DISCOUNT_TYPES = [
  ["PIONEER",      "Pioneer"],
  ["HONORS",       "With Honors"],
  ["HIGH_HONORS",  "With High Honors"],
  ["SIBLING",      "Sibling"],
  ["REFERRAL",     "Referral"],
  ["FULL_PAYMENT", "Full Payment"],
  ["INVESTOR",     "Investor"],
  ["SHAREHOLDER",  "Shareholder"],
  ["OTHERS",       "Others (one-off)"],
];

// OTHERS may appear more than once per (student, SY) — each row has a
// distinct customName. SIBLING and REFERRAL are also multi-row in the DB but
// are managed as one grouped block in the UI (one block per type, never two
// separate chips in the add-form dropdown).
const MULTI_USE_TYPES = new Set(["OTHERS"]);

const LOOKUPS = {
  schoolYears:   { endpoint: "/api/school-years", label: "label" },
  semesters:     { endpoint: "/api/semesters",    label: "label" },
  gradeLevels:   { endpoint: "/api/grade-levels", label: "name" },
  students:      { endpoint: "/api/students",     label: null },
  sections:      { endpoint: "/api/sections",     label: null },
  invoices:      { endpoint: "/api/tuition-invoices", label: null },
  feeStructures: { endpoint: "/api/fee-structures",   label: null },
};

const SCHEMA = {
  "fee-structures": {
    endpoint: "/api/fee-structures",
    columns: [
      ["gradeLevelId",   "Grade Level"],
      ["schoolYearId",   "School Year"],
      ["tuitionFee",     "Tuition"],
      ["miscFee",        "Misc"],
      ["booksFee",       "Books"],
      ["enrollmentFee",  "Enrollment"],
      ["months",         "Months"],
      ["totalAmount",    "Total"],
    ],
    fields: [
      { key: "gradeLevelId",  label: "Grade Level", type: "lookup", lookup: "gradeLevels", required: true },
      { key: "schoolYearId",  label: "School Year", type: "lookup", lookup: "schoolYears", required: true },
      { key: "tuitionFee",    label: "Tuition fee",       type: "number", placeholder: "14274.46" },
      { key: "miscFee",       label: "Miscellaneous fee", type: "number", placeholder: "4557.87" },
      { key: "booksFee",      label: "Books fee",         type: "number", placeholder: "6800.80" },
      { key: "enrollmentFee", label: "Enrollment fee",    type: "number", placeholder: "1000.00" },
      { key: "months",        label: "Months (installments)", type: "number", placeholder: "9" },
      { key: "totalAmount",   label: "Total (auto-computed)", type: "number",
        readOnly: true, placeholder: "Tuition + Misc + Books" },
      { key: "breakdown",     label: "Notes (free text)", type: "text" },
    ],
    blank: {
      gradeLevelId: "", schoolYearId: "",
      tuitionFee: "", miscFee: "", booksFee: "",
      enrollmentFee: "", months: 9, totalAmount: "", breakdown: "",
    },
  },

  "student-discounts": {
    endpoint: "/api/student-discounts",
    // Table columns (grouped view — one row per student+SY).
    // "discountsSummary" and "totalPct" are virtual group-level values, not
    // fields on the raw discount row; the grouped renderer handles them.
    columns: [
      ["studentId",        "Student"],
      ["studentNumber",    "Student #"],
      ["schoolYearId",     "School Year"],
      ["discountsSummary", "Discounts"],
      ["totalPct",         "Total %"],
    ],
    fields: [
      { key: "studentId",    label: "Student",     type: "lookupStudent", lookup: "students",    required: true },
      { key: "schoolYearId", label: "School Year", type: "lookup",        lookup: "schoolYears", required: true },
      { key: "type",         label: "Discount type", type: "select", required: true, options: DISCOUNT_TYPES },
      { key: "percentage",   label: "Percentage (0-100)", type: "number", required: true, placeholder: "25" },
      { key: "notes",        label: "Notes", type: "text", placeholder: "from public school" },
    ],
    blank: { studentId: "", schoolYearId: "", type: "PIONEER", percentage: "", notes: "" },
  },

  "tuition-invoices": {
    endpoint: "/api/tuition-invoices",
    columns: [
      ["studentId",     "Student"],
      ["studentNumber", "Student #"],
      ["schoolYearId",  "School Year"],
      ["semesterId",    "Semester"],
      ["amount",        "Amount"],
      ["dueDate",       "Due"],
      ["status",        "Status"],
    ],
    fields: [
      { key: "studentId",    label: "Student", type: "lookupStudent", lookup: "students", required: true },
      { key: "schoolYearId", label: "School Year", type: "lookup", lookup: "schoolYears", required: true },
      { key: "semesterId",   label: "Semester", type: "lookup", lookup: "semesters", required: true },
      { key: "amount",       label: "Amount", type: "number", required: true },
      { key: "dueDate",      label: "Due date", type: "date" },
      { key: "status",       label: "Status", type: "select",
        options: [["UNPAID","Unpaid"], ["PARTIAL","Partial"], ["PAID","Paid"]] },
    ],
    blank: { studentId: "", schoolYearId: "", semesterId: "", amount: "", dueDate: "", status: "UNPAID" },
  },

  "tuition-payments": {
    endpoint: "/api/tuition-payments",
    columns: [
      ["invoiceId",   "Invoice"],
      ["amount",      "Amount"],
      ["method",      "Method"],
      ["referenceNo", "Ref #"],
      ["paidAt",      "Paid at"],
    ],
    fields: [
      { key: "invoiceId",   label: "Invoice", type: "lookupInvoice", lookup: "invoices", required: true },
      { key: "amount",      label: "Amount", type: "number", required: true, placeholder: "5000.00" },
      { key: "method",      label: "Method", type: "select",
        options: [["CASH","Cash"], ["BANK","Bank transfer"], ["GCASH","GCash"], ["CARD","Card"], ["OTHER","Other"]] },
      { key: "referenceNo", label: "Reference #", type: "text" },
    ],
    blank: { invoiceId: "", amount: "", method: "CASH", referenceNo: "" },
  },
};

export default function TuitionAdminPage() {
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      if (t && TABS.some(tab => tab.key === t)) return t;
    } catch { /* ignore */ }
    return TABS[0].key;
  });
  const [data, setData]           = useState({});  // tabKey -> rows
  const [lookups, setLookups]     = useState({});  // lookupName -> rows
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(null);
  const [error, setError]         = useState("");
  const [busy, setBusy]           = useState(false);

  // Generate-invoices modal state
  const [genOpen, setGenOpen]     = useState(false);
  const [genFeeId, setGenFeeId]   = useState("");
  const [genSemId, setGenSemId]   = useState("");
  const [genDue, setGenDue]       = useState("");
  const [genResult, setGenResult] = useState(null);

  // Discounts are authorisation decisions — strict Admin-only. Cashier /
  // CashierStaff can view the list but never mutate, matching the backend
  // requireStrictAdmin guard on /api/student-discounts.
  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
    catch { return null; }
  })();
  const canEditDiscounts = currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin";

  // Per-tab filter state. Not every filter applies to every tab — the render
  // below only shows the controls that make sense for the active tab.
  const [syFilter,      setSyFilter]      = useState("");
  const [semFilter,     setSemFilter]     = useState("");
  // Seed the search from ?search=… so the Admissions "View Tuition" shortcut
  // lands with the student's row already filtered in.
  const [search,        setSearch]        = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("search") || "";
    } catch { return ""; }
  });
  // Prevent the tab-switch effect from wiping URL-seeded filters on first mount.
  const isFirstTabLoad = useRef(true);
  // Student ID passed from Master List "Manage Payments / Discounts" shortcut.
  // Stored in a ref so the auto-open effect can clear it without re-rendering.
  const autoStudentId  = useRef((() => {
    try { return new URLSearchParams(window.location.search).get("autoStudentId") || null; }
    catch { return null; }
  })());
  const [statusFilter,  setStatusFilter]  = useState("");  // invoices: UNPAID|PARTIAL|PAID
  const [typeFilter,    setTypeFilter]    = useState("");  // discounts: PIONEER|HONORS|…
  // Payment date-range filter (Payments tab only)
  const [payDateRange, setPayDateRange] = useState("all");
  const [payDateFrom,  setPayDateFrom]  = useState(() => isoToday(-30));
  const [payDateTo,    setPayDateTo]    = useState(() => isoToday(0));
  const [payDateField, setPayDateField] = useState("received");
  // Card-view state for Invoices + Payments. Not per-tab — we reset on tab
  // change (see useEffect below). "table" preserves the pre-existing view.
  const [viewMode,      setViewMode]      = useState("table");  // student|grade|section|table
  const [showEmpty,     setShowEmpty]     = useState(true);

  // Payments tab is now invoice-oriented (one row per invoice) with Pay +
  // History modals. `allPayments` holds every payment so we can aggregate
  // paid/remaining per invoice and render the activity log.
  const [allPayments,   setAllPayments]   = useState([]);
  const [payModal,      setPayModal]      = useState(null);  // { invoice } | null
  const [historyModal,  setHistoryModal]  = useState(null);  // { invoice } | null
  const [refundModal,   setRefundModal]   = useState(null);  // { invoice } | null
  const [modalError,    setModalError]    = useState("");

  // Invoices tab: per-row Breakdown+History modal, per-row Recompute, and
  // the bulk Regenerate-All flow.
  const [breakdownModal,     setBreakdownModal]     = useState(null);  // { invoice } | null
  const [recomputeAllModal,  setRecomputeAllModal]  = useState(null);  // { scope } | null

  // Payments tab: per-row "payment breakdown" modal — shows the per-month
  // ledger (due / paid / remaining) with a status filter so the cashier
  // can answer "how much for September?" without scanning the activity
  // log. Different concern from breakdownModal above (which is the
  // invoice-amount-recomputation breakdown for the Invoices tab).
  const [paymentBreakdownModal, setPaymentBreakdownModal] = useState(null);  // { invoice } | null

  // Per-student "additional charges" manager — summer class, special
  // curriculum, etc. Sits beside the regular tuition cascade and shows
  // up at the bottom of the SOA + rolls into the grand total.
  const [chargesModal, setChargesModal] = useState(null);  // { student, schoolYear } | null

  // Discounts tab: per-student Manage modal. The table shows one row per
  // (student, SY); clicking Manage opens this modal with every discount
  // line for that pair plus an inline add-form. isNew=true for the "+ New"
  // flow, which lets the admin pick student+SY first.
  const [manageDiscounts, setManageDiscounts] = useState(null);
  // { studentId, schoolYearId, isNew } | null

  // Read-only student profile + documents modal. Click any student name
  // anywhere in the Tuition tabs (Invoices, Discounts, Payments) to open it.
  // Mirrors the same UX in Directory → Students.
  const [studentDetailTarget, setStudentDetailTarget] = useState(null);

  /** Resolve a studentId (or a partial student object) to the full lookups
   *  row, then open the read-only profile modal. Used by every student-name
   *  click in the tab views; centralised so the resolution logic stays in
   *  one place. */
  function openStudentProfile(idOrStudent) {
    if (!idOrStudent) return;
    const id = typeof idOrStudent === "object" ? idOrStudent.id : idOrStudent;
    const full = (lookups.students || []).find(x => String(x.id) === String(id))
              || (typeof idOrStudent === "object" ? idOrStudent : null);
    if (full) setStudentDetailTarget(full);
  }

  // Column sort for the shared table view (Fee Structures / Discounts).
  // Defaults to the first column of the active tab, ascending — which is
  // Grade Level for Fee Structures.
  const [sortBy,  setSortBy]  = useState(SCHEMA[TABS[0].key].columns[0][0]);
  const [sortDir, setSortDir] = useState("asc");

  const schema = SCHEMA[activeTab];
  const rows   = data[activeTab] || [];

  // Keep Semester filter consistent with the chosen School Year — if the user
  // switches SY to one that doesn't contain the current semester, clear it.
  useEffect(() => {
    if (!semFilter) return;
    const sem = (lookups.semesters || []).find(x => String(x.id) === String(semFilter));
    if (!sem) return;
    if (syFilter && String(sem.schoolYearId) !== String(syFilter)) setSemFilter("");
  }, [syFilter, lookups.semesters, semFilter]);

  // Student + invoice lookup tables used by the search filter.
  const studentById = useMemo(
    () => Object.fromEntries((lookups.students || []).map(x => [x.id, x])),
    [lookups.students]
  );
  const invoiceById = useMemo(
    () => Object.fromEntries((lookups.invoices || []).map(x => [x.id, x])),
    [lookups.invoices]
  );
  const sectionById = useMemo(
    () => Object.fromEntries((lookups.sections || []).map(x => [x.id, x])),
    [lookups.sections]
  );
  const gradeById = useMemo(
    () => Object.fromEntries((lookups.gradeLevels || []).map(x => [x.id, x])),
    [lookups.gradeLevels]
  );
  const semesterById = useMemo(
    () => Object.fromEntries((lookups.semesters || []).map(x => [x.id, x])),
    [lookups.semesters]
  );

  /** invoiceId -> list of payments, newest last (sorted in the log modal). */
  const paymentsByInvoice = useMemo(() => {
    const map = {};
    for (const p of allPayments) {
      (map[p.invoiceId] ||= []).push(p);
    }
    return map;
  }, [allPayments]);

  /** Sum of payments for an invoice (used for Paid/Remaining figures). */
  function paidForInvoice(invoiceId) {
    return (paymentsByInvoice[invoiceId] || [])
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  }

  function studentMatchesSearch(studentId, q) {
    if (!q) return true;
    const st = studentById[studentId];
    if (!st) return false;
    const hay = [st.lastName, st.firstName, st.middleName, st.studentNumber]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  }

  /** Row filter for the current tab. Only the tabs that display a filter bar
   *  actually do anything non-trivial; Fee Structures falls through to the
   *  unfiltered list. */
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (activeTab === "student-discounts") {
      return rows.filter(r => {
        if (syFilter   && String(r.schoolYearId) !== String(syFilter)) return false;
        if (typeFilter && r.type !== typeFilter)                       return false;
        if (!studentMatchesSearch(r.studentId, q))                     return false;
        return true;
      });
    }
    if (activeTab === "tuition-invoices") {
      return rows.filter(r => {
        if (syFilter     && String(r.schoolYearId) !== String(syFilter))  return false;
        if (semFilter    && String(r.semesterId)   !== String(semFilter)) return false;
        if (statusFilter && r.status !== statusFilter)                    return false;
        if (!studentMatchesSearch(r.studentId, q))                        return false;
        return true;
      });
    }
    if (activeTab === "tuition-payments") {
      const { from: payFrom, to: payTo } = computePayBounds(payDateRange, payDateFrom, payDateTo);
      const applyDateFilter = payDateRange !== "all";
      return rows.filter(r => {
        if (syFilter     && String(r.schoolYearId) !== String(syFilter))  return false;
        if (semFilter    && String(r.semesterId)   !== String(semFilter)) return false;
        if (statusFilter && r.status !== statusFilter)                    return false;
        const invoicePayments = allPayments.filter(p => String(p.invoiceId) === String(r.id));
        // Search matches on student name/# OR any OR # recorded against
        // one of this invoice's payments — a cashier chasing down "who
        // does receipt 4213 belong to" can type the OR # straight into
        // the same box instead of needing a separate field.
        if (q) {
          const matchesStudent = studentMatchesSearch(r.studentId, q);
          const matchesOr = !matchesStudent && invoicePayments.some(p =>
            (p.orNumber || "").toLowerCase().includes(q));
          if (!matchesStudent && !matchesOr) return false;
        }
        if (applyDateFilter) {
          const hasPaymentInRange = invoicePayments.some(p => {
            const t = paymentTs(p, payDateField);
            return !Number.isNaN(t) && t >= payFrom && t < payTo;
          });
          if (!hasPaymentInRange) return false;
        }
        return true;
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, activeTab, syFilter, semFilter, search, statusFilter, typeFilter, studentById, invoiceById,
      allPayments, payDateRange, payDateFrom, payDateTo, payDateField]);

  /** Payments-tab summary bar — lets an admin cross-check the dashboard's
   *  "Collected This Month" tile / financial reports against this exact
   *  filtered list before handing figures to accounting.
   *
   *  - `collected` sums ONLY the in-window payment amounts for the
   *    currently-listed invoices (same School Year/Semester/Status/Search
   *    filters as the table, plus the date window) — this is the figure
   *    that should match a report pulled for the same range. It is NOT
   *    the same as totalPaid below when a window narrower than "All time"
   *    is selected, because an invoice can have older payments outside
   *    the window that shouldn't count toward "collected in this window".
   *  - totalAmount / totalPaid / totalRemaining are lifetime snapshots of
   *    the listed invoices "as of today" — a balance doesn't have a date
   *    range, so these stay constant regardless of the window filter.
   */
  const paySummary = useMemo(() => {
    if (activeTab !== "tuition-payments") return null;
    const { from: payFrom, to: payTo } = computePayBounds(payDateRange, payDateFrom, payDateTo);
    const windowed = payDateRange !== "all";

    let totalAmount = 0, totalPaid = 0, collected = 0, paymentCount = 0;
    for (const r of filteredRows) {
      totalAmount += Number(r.amount) || 0;
      for (const p of (paymentsByInvoice[r.id] || [])) {
        const amt = Number(p.amount) || 0;
        totalPaid += amt;
        if (!windowed) {
          collected += amt;
          paymentCount += 1;
        } else {
          const t = paymentTs(p, payDateField);
          if (!Number.isNaN(t) && t >= payFrom && t < payTo) {
            collected += amt;
            paymentCount += 1;
          }
        }
      }
    }
    return {
      studentCount: filteredRows.length,
      paymentCount,
      totalAmount,
      totalPaid,
      totalRemaining: totalAmount - totalPaid,
      collected,
      windowed,
    };
  }, [activeTab, filteredRows, paymentsByInvoice, payDateRange, payDateFrom, payDateTo, payDateField]);

  /** Discounts tab only: group every discount row by (studentId, schoolYearId)
   *  so the table shows one row per student per SY. The table now also
   *  surfaces students who have NO discounts yet — synthetic empty groups
   *  are seeded from the student catalog so admins can spot uncovered
   *  admissions without leaving the page. The Type filter gets a special
   *  "__none" value to show those empty groups only.
   *
   *  Synthetic-group SY rules:
   *    • If a specific SY filter is active → all synthetic rows use it.
   *    • Else → use the student's currentSection.schoolYearId.
   *    • Else → fall back to the active SY (most schools have one).
   *    • Else → skip the student (no SY context we can show honestly).
   *  Totals are computed across ALL lines in the group so the "Total %"
   *  reflects reality even when a type filter is active.
   */
  const discountGroups = useMemo(() => {
    if (activeTab !== "student-discounts") return [];
    const q = search.trim().toLowerCase();
    const byKey = new Map();
    // Real groups from existing discount rows.
    for (const r of rows) {
      const key = `${r.studentId}::${r.schoolYearId}`;
      let g = byKey.get(key);
      if (!g) {
        g = { key, studentId: r.studentId, schoolYearId: r.schoolYearId, items: [], totalPct: 0 };
        byKey.set(key, g);
      }
      g.items.push(r);
      g.totalPct += Number(r.percentage) || 0;
    }
    // Synthetic empty groups — one row per admitted student that doesn't
    // already have a real group for the target SY. Skipped silently if
    // we can't determine a reasonable SY to display.
    const schoolYears = lookups.schoolYears || [];
    const activeSy = schoolYears.find(y => y.active) || schoolYears[0] || null;
    const targetSyFor = (st) => {
      if (syFilter) return Number(syFilter);
      const sec = st.currentSectionId ? sectionById[st.currentSectionId] : null;
      if (sec?.schoolYearId != null) return sec.schoolYearId;
      return activeSy?.id ?? null;
    };
    for (const st of (lookups.students || [])) {
      const sy = targetSyFor(st);
      if (sy == null) continue;
      const key = `${st.id}::${sy}`;
      if (byKey.has(key)) continue;
      byKey.set(key, {
        key, studentId: st.id, schoolYearId: sy,
        items: [], totalPct: 0,
      });
    }
    let list = Array.from(byKey.values());
    list = list.filter(g => {
      if (syFilter   && String(g.schoolYearId) !== String(syFilter)) return false;
      if (typeFilter === "__none") {
        // "No discount" filter keeps only synthetic / empty groups.
        if (g.items.length !== 0) return false;
      } else if (typeFilter === "__any") {
        // "Has any discount" — inverse of __none. Skip empty groups.
        if (g.items.length === 0) return false;
      } else if (typeFilter && !g.items.some(i => i.type === typeFilter)) {
        return false;
      }
      if (!studentMatchesSearch(g.studentId, q)) return false;
      return true;
    });
    const groupSortValue = (g, key) => {
      if (key === "studentId") {
        const st = studentById[g.studentId];
        return st ? `${st.lastName || ""}, ${st.firstName || ""}` : "";
      }
      if (key === "studentNumber") return studentById[g.studentId]?.studentNumber || "";
      if (key === "schoolYearId") {
        const sy = (lookups.schoolYears || []).find(y => y.id === g.schoolYearId);
        return sy?.label || "";
      }
      if (key === "totalPct") return g.totalPct;
      if (key === "discountsSummary") {
        return g.items.map(i => i.type).sort().join(",");
      }
      return "";
    };
    list.sort((a, b) => {
      const va = groupSortValue(a, sortBy);
      const vb = groupSortValue(b, sortBy);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, activeTab, syFilter, typeFilter, search, sortBy, sortDir, studentById, sectionById, lookups.schoolYears, lookups.students]);

  /** Sorted view of filteredRows for the shared table (fee-structures /
   *  discounts). Clicking a column header sets sortBy/sortDir. */
  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const va = sortValue(a, sortBy);
      const vb = sortValue(b, sortBy);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sortBy, sortDir, schema, lookups]);

  /** Resolve a row (invoice) to its student for grade/section grouping.
   *  Both Invoices and Payments tabs now treat rows as invoices. */
  function studentFor(row) {
    if (activeTab === "tuition-invoices" || activeTab === "tuition-payments") {
      return studentById[row.studentId] || null;
    }
    return null;
  }

  /** Group filteredRows for the card views. Seeds empty groups from the
   *  catalog when `showEmpty` is on so gaps (students/grades/sections with
   *  zero invoices or payments in this filter scope) appear as headers. */
  const groupedRows = useMemo(() => {
    if (viewMode === "table") return [];
    if (activeTab !== "tuition-invoices" && activeTab !== "tuition-payments") return [];

    const map = new Map();

    if (showEmpty) {
      if (viewMode === "grade") {
        for (const g of (lookups.gradeLevels || [])) {
          map.set(`gl-${g.id}`, {
            key: `gl-${g.id}`, label: g.name, ordering: g.ordering ?? 9999, items: [],
          });
        }
      } else if (viewMode === "section") {
        const list = (lookups.sections || [])
          .filter(sec => !syFilter || String(sec.schoolYearId) === String(syFilter));
        for (const sec of list) {
          const grade = sec.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
          map.set(`sec-${sec.id}`, {
            key: `sec-${sec.id}`,
            label: grade ? `${grade.name} · ${sec.name}` : sec.name,
            ordering: (grade?.ordering ?? 9999) * 1000 + (sec.id ?? 0),
            items: [],
          });
        }
      } else if (viewMode === "student") {
        for (const st of (lookups.students || [])) {
          map.set(`st-${st.id}`, {
            key: `st-${st.id}`,
            label: `${st.lastName}, ${st.firstName}${st.studentNumber ? ` · ${st.studentNumber}` : ""}`,
            ordering: st.lastName ? st.lastName.toLowerCase().charCodeAt(0) : 99999,
            sortKey: (st.lastName || "") + (st.firstName || ""),
            items: [],
          });
        }
      }
    }

    for (const row of filteredRows) {
      const st = studentFor(row);
      const sec = st?.currentSectionId ? sectionById[st.currentSectionId] : null;
      const grade = sec ? gradeById[sec.gradeLevelId] : null;

      let key, label, ordering, sortKey;
      if (viewMode === "grade") {
        key      = grade ? `gl-${grade.id}` : "gl-none";
        label    = grade ? grade.name : "No grade level";
        ordering = grade?.ordering ?? 9999;
      } else if (viewMode === "section") {
        key      = sec ? `sec-${sec.id}` : "sec-none";
        label    = sec
          ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
          : "No section assigned";
        ordering = (grade?.ordering ?? 9999) * 1000 + (sec?.id ?? 0);
      } else {
        // student view
        key      = st ? `st-${st.id}` : "st-none";
        label    = st
          ? `${st.lastName}, ${st.firstName}${st.studentNumber ? ` · ${st.studentNumber}` : ""}`
          : "Unknown student";
        sortKey  = st ? ((st.lastName || "") + (st.firstName || "")) : "zzz";
        ordering = 0;
      }
      if (!map.has(key)) map.set(key, { key, label, ordering, sortKey, items: [] });
      map.get(key).items.push(row);
    }

    let out = [...map.values()];
    if (!showEmpty) out = out.filter(g => g.items.length > 0);
    return out.sort((a, b) => {
      if (viewMode === "student") return (a.sortKey || "").localeCompare(b.sortKey || "");
      return a.ordering - b.ordering;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, viewMode, showEmpty, activeTab, syFilter, studentById, invoiceById, sectionById, gradeById, lookups.students, lookups.sections, lookups.gradeLevels]);

  useEffect(() => {
    loadTab(activeTab);
    schema.fields.forEach(f => { if (f.lookup) loadLookup(f.lookup); });
    schema.columns.forEach(([k]) => {
      const f = schema.fields.find(ff => ff.key === k);
      if (f?.lookup) loadLookup(f.lookup);
    });
    // Always pre-load lookups needed by the generate modal when on Invoices tab
    if (activeTab === "tuition-invoices") {
      loadLookup("feeStructures");
      loadLookup("semesters");
    }
    // Payments tab displays one row per invoice with Pay + History modals,
    // so it needs the same ambient lookups the Invoices tab uses.
    if (activeTab === "tuition-payments") {
      loadLookup("students");
      loadLookup("schoolYears");
      loadLookup("semesters");
    }
    // Invoice/Payment card views group by grade level or section — those
    // lookups aren't in the schema fields, so pre-load them manually.
    if (activeTab === "tuition-invoices" || activeTab === "tuition-payments") {
      loadLookup("sections");
      loadLookup("gradeLevels");
      loadLookup("students");
    }
    // Clear filters when switching tabs — avoids a stale SY filter from Invoices
    // surviving onto Discounts, etc.
    // Skip on first mount so URL-seeded ?search= and ?tab= are preserved.
    if (isFirstTabLoad.current) {
      isFirstTabLoad.current = false;
    } else {
      setSyFilter(""); setSemFilter(""); setSearch("");
      setStatusFilter(""); setTypeFilter("");
      setViewMode("table");
      setShowEmpty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function loadTab(tabKey) {
    try {
      // Payments tab rows are INVOICES (we display Total/Paid/Remaining per
      // invoice). The raw payment records are loaded separately so the
      // History modal and paid/remaining aggregation can read them.
      if (tabKey === "tuition-payments") {
        const [invRes, payRes] = await Promise.all([
          fetch(`${API_BASE}/api/tuition-invoices`),
          fetch(`${API_BASE}/api/tuition-payments`),
        ]);
        if (!invRes.ok) throw new Error("Load invoices failed");
        if (!payRes.ok) throw new Error("Load payments failed");
        const [invoices, payments] = await Promise.all([invRes.json(), payRes.json()]);
        setData(prev => ({ ...prev, [tabKey]: invoices }));
        setAllPayments(payments);
        return;
      }
      const res = await fetch(`${API_BASE}${SCHEMA[tabKey].endpoint}`);
      if (!res.ok) throw new Error(`Load ${tabKey} failed`);
      const list = await res.json();
      setData(prev => ({ ...prev, [tabKey]: list }));
    } catch (e) { setError(e.message); }
  }

  // Auto-open payment/discount popup when arriving from Master List shortcut.
  // Fires whenever data or lookups change; bails immediately once triggered.
  useEffect(() => {
    if (!autoStudentId.current) return;
    const sid = String(autoStudentId.current);

    if (activeTab === "tuition-payments") {
      const invoices = data["tuition-payments"];
      const students = lookups.students;
      if (!invoices || !students) return;
      const match = invoices
        .filter(inv => String(inv.studentId) === sid)
        .sort((a, b) => b.id - a.id)[0];
      if (!match) return;
      setModalError("");
      setPayModal({ invoice: match });
      autoStudentId.current = null;

    } else if (activeTab === "student-discounts") {
      const discArr  = data["student-discounts"];
      const syList   = lookups.schoolYears;
      const students = lookups.students;
      if (!discArr || !syList || !students) return;
      const studentDiscs = discArr
        .filter(d => String(d.studentId) === sid)
        .sort((a, b) => b.schoolYearId - a.schoolYearId);
      const syId = studentDiscs.length > 0
        ? studentDiscs[0].schoolYearId
        : [...syList].sort((a, b) => b.id - a.id)[0]?.id || "";
      if (!syId) return;
      setManageDiscounts({ studentId: Number(sid), schoolYearId: syId, isNew: false });
      autoStudentId.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, data, lookups.schoolYears, lookups.students]);

  async function loadLookup(name) {
    if (lookups[name]) return;
    try {
      const res = await fetch(`${API_BASE}${LOOKUPS[name].endpoint}`);
      if (!res.ok) throw new Error(`Load lookup ${name} failed`);
      const list = await res.json();
      setLookups(prev => ({ ...prev, [name]: list }));
    } catch (e) { setError(e.message); }
  }

  function startCreate() { setEditing("new"); setForm({ ...schema.blank }); setError(""); }
  function startEdit(row) { setEditing(row); setForm({ ...schema.blank, ...row }); setError(""); }
  function cancel() { setEditing(null); setForm(null); setError(""); }

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const isNew = editing === "new";
      let url = isNew ? `${API_BASE}${schema.endpoint}` : `${API_BASE}${schema.endpoint}/${editing.id}`;
      const payload = { ...form };
      schema.fields.forEach(f => {
        if (f.type === "number" || f.type === "lookup" || f.type === "lookupStudent" || f.type === "lookupInvoice") {
          if (payload[f.key] === "" || payload[f.key] === undefined) payload[f.key] = null;
          else if (payload[f.key] !== null && f.type !== "number") payload[f.key] = Number(payload[f.key]);
        }
        if (f.type === "date" && payload[f.key] === "") payload[f.key] = null;
      });
      // Fee-structure edit: offer the cascade recompute. Editing the fee
      // rows silently leaves existing invoices at their old totals, which
      // surprises admins — prompt now, and pass `?recompute=true` so the
      // backend updates every invoice at this (grade, SY). Creating a new
      // fee structure is exempt — there are no invoices to cascade into yet.
      if (activeTab === "fee-structures" && !isNew) {
        const confirmed = window.confirm(
          "Save this fee structure and recompute every existing invoice for students " +
          "enrolled at this grade level for this school year?\n\n" +
          "• Click OK to save and cascade the new amounts to all affected invoices " +
          "(payments are preserved; only the invoice totals change).\n\n" +
          "• Click Cancel to save the fee structure only. You can recompute later " +
          "per-student from the Invoices tab, or in bulk with Regenerate All."
        );
        if (confirmed) url += (url.includes("?") ? "&" : "?") + "recompute=true";
      }
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      await loadTab(activeTab);
      // Payment save might have updated an invoice's status — refresh invoices too
      if (activeTab === "tuition-payments") await loadTab("tuition-invoices");
      // Fee-structure cascade changed invoice totals — refresh the invoices cache too
      if (activeTab === "fee-structures") await loadTab("tuition-invoices");
      cancel();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function remove(row) {
    if (!window.confirm("Delete this record?")) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}${schema.endpoint}/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await loadTab(activeTab);
      if (activeTab === "tuition-payments") await loadTab("tuition-invoices");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  /** Open the Pay modal for a specific invoice — replaces the generic form
   *  flow for the Payments tab. */
  function openPayModal(invoice) {
    setModalError("");
    setPayModal({ invoice });
  }

  /** Payments tab [i] — shows the payment stream (receipts, reversals,
   *  refunds) with Delete per entry. */
  function openPaymentHistory(invoice) {
    setHistoryModal({ invoice, mode: "payments" });
  }

  /** Invoices tab 📋 — shows ONLY the invoice-amount adjustment trail
   *  (recomputes, discount-driven changes). Payments live on the other tab.
   *  Adjustments are audit records and are never deletable from the UI. */
  function openInvoiceHistory(invoice) {
    setHistoryModal({ invoice, mode: "adjustments" });
  }

  /** Submit a payment from the Pay modal. Keeps the invoice fresh on the
   *  History modal (if open for the same invoice) so status flips
   *  immediately. */
  async function submitPayment(body) {
    setModalError(""); setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/tuition-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      await loadTab("tuition-payments");
      // Keep the Invoices tab data fresh too so status badges match.
      if (data["tuition-invoices"]) await loadTab("tuition-invoices");
      setPayModal(null);
    } catch (e) { setModalError(e.message); }
    finally { setBusy(false); }
  }

  async function deletePayment(payment) {
    if (!window.confirm("Delete this payment? This will also reverse its effect on the invoice status.")) return;
    setModalError(""); setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/tuition-payments/${payment.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await loadTab("tuition-payments");
      if (data["tuition-invoices"]) await loadTab("tuition-invoices");
    } catch (e) { setModalError(e.message); }
    finally { setBusy(false); }
  }

  // ── Invoice Recompute flow ───────────────────────────────────────
  // The [i] button opens the Breakdown+History modal. The modal itself
  // surfaces the Recompute action (with a reason field and delta preview)
  // so accountants always see what WILL change before committing.

  function openBreakdown(invoice) {
    setModalError("");
    setBreakdownModal({ invoice });
  }

  /** Recompute ONE invoice. Backend writes an InvoiceAdjustment row iff the
   *  amount actually changed, and re-derives UNPAID/PARTIAL/PAID status
   *  against existing payments. */
  async function runRecomputeOne(invoiceId, reason) {
    setModalError(""); setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/tuition-invoices/${invoiceId}/recompute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason || null }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Recompute failed (${res.status})`));
      }
      const result = await res.json();
      await loadTab("tuition-invoices");
      // Keep the Payments tab's invoice figures fresh too, since amount
      // changes flip Paid/Remaining and the status badge.
      if (data["tuition-payments"]) await loadTab("tuition-payments");
      return result;
    } catch (e) { setModalError(e.message); throw e; }
    finally { setBusy(false); }
  }

  /** Recompute EVERY invoice matching the current SY/Semester filter (or all
   *  invoices if no filter is set). Returns { changed, unchanged, skipped }. */
  async function runRecomputeAll(reason) {
    setModalError(""); setBusy(true);
    try {
      const body = {
        reason: reason || null,
        schoolYearId: syFilter ? Number(syFilter) : null,
        semesterId:   semFilter ? Number(semFilter) : null,
      };
      const res = await fetch(`${API_BASE}/api/tuition-invoices/recompute-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Regenerate failed (${res.status})`));
      }
      const result = await res.json();
      await loadTab("tuition-invoices");
      if (data["tuition-payments"]) await loadTab("tuition-payments");
      return result;
    } catch (e) { setModalError(e.message); throw e; }
    finally { setBusy(false); }
  }

  // ── Refund flow (Payments tab, overpaid invoices only) ───────────
  // Refunds are stored as negative tuition_payments rows — same mechanism
  // as a reversal, but triggered by a dedicated button so the accountant
  // doesn't have to remember to type a negative amount.

  function openRefundModal(invoice) {
    setModalError("");
    setRefundModal({ invoice });
  }

  /** Refund = negative payment row. Separate from submitPayment so we don't
   *  close the wrong modal on error — RefundModal stays open if the POST
   *  fails, with the error visible. */
  async function submitRefund(body) {
    setModalError(""); setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/tuition-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...body,
          amount: -Math.abs(Number(body.amount)),  // store as negative
          notes: body.notes || "Refund",
        }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Refund failed (${res.status})`));
      }
      await loadTab("tuition-payments");
      if (data["tuition-invoices"]) await loadTab("tuition-invoices");
      setRefundModal(null);
    } catch (e) { setModalError(e.message); }
    finally { setBusy(false); }
  }

  async function runGenerate() {
    setError(""); setGenResult(null); setBusy(true);
    try {
      if (!genFeeId || !genSemId) throw new Error("Pick a fee structure and semester");
      const res = await fetch(`${API_BASE}/api/tuition-invoices/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          feeStructureId: Number(genFeeId),
          semesterId: Number(genSemId),
          dueDate: genDue || null,
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Generate failed (${res.status})`));
      const result = await res.json();
      setGenResult(`Created ${result.created}, skipped ${result.skipped} existing.`);
      await loadTab("tuition-invoices");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  function studentLabel(st) {
    if (!st) return "—";
    return `${st.lastName}, ${st.firstName}`;
  }

  function invoiceLabel(inv) {
    if (!inv) return "—";
    const st = (lookups.students || []).find(s => s.id === inv.studentId);
    const sem = (lookups.semesters || []).find(s => s.id === inv.semesterId);
    return `${studentLabel(st)} · ${sem?.label || ""} · ${inv.amount}`;
  }

  /** Comparable value for a cell — used by the sortable column headers.
   *  Mirrors renderCell's resolution for lookup columns but returns raw
   *  numbers/strings (no JSX, no "—" placeholders) so comparisons behave
   *  correctly. Grade Level sorts by the lookup's `ordering` field so
   *  Nursery < Kinder < Grade 1 instead of alphabetically. */
  function sortValue(row, key) {
    if (key === "studentNumber") {
      const st = (lookups.students || []).find(x => x.id === row.studentId);
      return st?.studentNumber ?? null;
    }
    const v = row[key];
    if (v === null || v === undefined || v === "") return null;
    const field = schema.fields.find(f => f.key === key);
    if (field?.type === "lookup") {
      const list = lookups[field.lookup] || [];
      const ref = list.find(r => r.id === v);
      if (!ref) return null;
      if (ref.ordering != null) return ref.ordering;
      return ref[LOOKUPS[field.lookup].label] ?? "";
    }
    if (field?.type === "lookupStudent") {
      const list = lookups.students || [];
      return studentLabel(list.find(r => r.id === v));
    }
    if (field?.type === "lookupInvoice") return v;
    if (field?.type === "number") {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    if (key === "paidAt" || key === "createdAt" || key === "dueDate") {
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : null;
    }
    return v;
  }

  function renderCell(row, key) {
    // Synthetic column: resolve the studentNumber from the student lookup so
    // the Student column can show just the name (less visual noise) and the
    // student number gets its own column.
    if (key === "studentNumber") {
      const st = (lookups.students || []).find(x => x.id === row.studentId);
      return st?.studentNumber || "—";
    }
    const v = row[key];
    if (v === null || v === undefined || v === "") return "—";
    const field = schema.fields.find(f => f.key === key);
    if (field?.type === "lookup") {
      const list = lookups[field.lookup] || [];
      const ref = list.find(r => r.id === v);
      return ref ? ref[LOOKUPS[field.lookup].label] : `#${v}`;
    }
    if (field?.type === "lookupStudent") {
      const list = lookups.students || [];
      const ref  = list.find(r => r.id === v);
      if (!ref) return "—";
      return (
        <button type="button" className={s.nameLink}
                onClick={() => openStudentProfile(ref)}
                title="View profile">
          {studentLabel(ref)}
        </button>
      );
    }
    if (field?.type === "lookupInvoice") {
      const list = lookups.invoices || [];
      return invoiceLabel(list.find(r => r.id === v));
    }
    if (key === "status") {
      return <span className={`${s.statusBadge} ${s["status_" + v]}`}>{v}</span>;
    }
    if (key === "paidAt" || key === "createdAt") return new Date(v).toLocaleString();
    return String(v);
  }

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <div className={s.eyebrow}>Admin · Tuition</div>
          <h1 className={s.title}>Fees, Invoices &amp; Payments</h1>
          <p className={s.sub}>Define fee structures per grade level, generate invoices for a semester, and record payments.</p>
        </div>
      </div>

      <div className={s.tabBar}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`${s.tab} ${activeTab === t.key ? s.tabActive : ""}`}
            onClick={() => {
              setActiveTab(t.key); cancel(); setGenOpen(false);
              setPayModal(null); setHistoryModal(null); setRefundModal(null);
              setBreakdownModal(null); setRecomputeAllModal(null);
              setManageDiscounts(null);
              setEditing(null); setForm(null);
              setSortBy(SCHEMA[t.key].columns[0][0]); setSortDir("asc");
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={s.card}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>{TABS.find(t => t.key === activeTab).label}</h2>
          <div className={s.headActions}>
            {/* Invoices are auto-generated from fee-structure + enrollment hooks
                (not directly editable), but admins can Recompute one invoice or
                Regenerate-All to apply discount changes in bulk. */}
            {!editing && activeTab === "tuition-invoices" && (
              <button className={s.btnPrimary} onClick={() => setRecomputeAllModal({})}>
                ↻ Regenerate All
              </button>
            )}
            {!editing && activeTab === "student-discounts" && canEditDiscounts && (
              <button className={s.btnPrimary}
                      onClick={() => setManageDiscounts({ studentId: "", schoolYearId: "", isNew: true })}>
                + New
              </button>
            )}
            {!editing && activeTab !== "tuition-invoices" && activeTab !== "tuition-payments" && activeTab !== "student-discounts" && (
              <button className={s.btnPrimary} onClick={startCreate}>+ New</button>
            )}
          </div>
        </div>

        {error && <div className={s.error}>{error}</div>}

        {activeTab === "tuition-invoices" && genOpen && (
          <div className={s.genBox}>
            <div className={s.genGrid}>
              <div className={s.field}>
                <label className={s.label}>Fee structure *</label>
                <select className={s.input} value={genFeeId} onChange={e => setGenFeeId(e.target.value)}>
                  <option value="">— select —</option>
                  {(lookups.feeStructures || []).map(fs => {
                    const gl = (lookups.gradeLevels || []).find(g => g.id === fs.gradeLevelId);
                    const sy = (lookups.schoolYears || []).find(y => y.id === fs.schoolYearId);
                    return (
                      <option key={fs.id} value={fs.id}>
                        {gl?.name || `Grade #${fs.gradeLevelId}`} · {sy?.label || ""} · {fs.totalAmount}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className={s.field}>
                <label className={s.label}>Semester *</label>
                <select className={s.input} value={genSemId} onChange={e => setGenSemId(e.target.value)}>
                  <option value="">— select —</option>
                  {(lookups.semesters || []).map(sm => (
                    <option key={sm.id} value={sm.id}>{sm.label}</option>
                  ))}
                </select>
              </div>
              <div className={s.field}>
                <label className={s.label}>Due date</label>
                <DatePicker value={genDue || ""} onChange={v => setGenDue(v)} />
              </div>
            </div>
            <div className={s.formActions}>
              {genResult && <div className={s.success}>{genResult}</div>}
              <button className={s.btnPrimary} onClick={runGenerate} disabled={busy}>
                {busy ? "Generating…" : "Generate invoices"}
              </button>
            </div>
          </div>
        )}

        {/* Fee Structures + Discounts edit/create now happens in a modal
            overlay (rendered at the bottom of the page) so the list stays
            visible behind it, matching the Academics tab pattern. */}
        <>
            {(activeTab === "student-discounts"
              || activeTab === "tuition-invoices"
              || activeTab === "tuition-payments") && (
              <FilterBar
                activeTab={activeTab}
                schoolYears={lookups.schoolYears || []}
                semesters={lookups.semesters || []}
                syFilter={syFilter} setSyFilter={setSyFilter}
                semFilter={semFilter} setSemFilter={setSemFilter}
                search={search} setSearch={setSearch}
                statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                typeFilter={typeFilter} setTypeFilter={setTypeFilter}
                payDateRange={payDateRange} setPayDateRange={setPayDateRange}
                payDateFrom={payDateFrom}   setPayDateFrom={setPayDateFrom}
                payDateTo={payDateTo}       setPayDateTo={setPayDateTo}
                payDateField={payDateField} setPayDateField={setPayDateField}
                matchCount={activeTab === "student-discounts" ? discountGroups.length : filteredRows.length}
                totalCount={activeTab === "student-discounts"
                  ? new Set(rows.map(r => `${r.studentId}::${r.schoolYearId}`)).size
                  : rows.length}
              />
            )}

            {activeTab === "tuition-payments" && paySummary && (
              <PaymentsSummaryBar
                summary={paySummary}
                payDateRange={payDateRange}
                payDateField={payDateField}
                payDateFrom={payDateFrom}
                payDateTo={payDateTo}
                s={s}
              />
            )}

            {(activeTab === "tuition-invoices" || activeTab === "tuition-payments") && (
              <div className={s.viewRow}>
                <div className={s.segmentedWrap}>
                  <span className={s.segLabel}>View</span>
                  <div className={s.segmented} role="tablist" aria-label="View">
                    {[
                      ["student", "Students"],
                      ["grade",   "Grade Level"],
                      ["section", "Section"],
                      ["table",   "All Students (List form)"],
                    ].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        role="tab"
                        aria-selected={viewMode === val}
                        className={`${s.segBtn} ${viewMode === val ? s.segBtnActive : ""}`}
                        onClick={() => setViewMode(val)}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                {viewMode !== "table" && (
                  <label className={s.toggle}>
                    <input
                      type="checkbox"
                      checked={showEmpty}
                      onChange={e => setShowEmpty(e.target.checked)}
                    />
                    <span>
                      Show empty {viewMode === "grade" ? "grade levels" : viewMode === "section" ? "sections" : "students"}
                      {" "}(no invoices yet)
                    </span>
                  </label>
                )}
              </div>
            )}

            {(activeTab === "tuition-invoices" || activeTab === "tuition-payments") && viewMode !== "table" ? (
              groupedRows.length === 0 ? (
                <div className={s.empty}>
                  {filteredRows.length === 0
                    ? (rows.length === 0 ? "No records yet." : "No records match the current filters.")
                    : (viewMode === "grade" ? "No grade levels configured yet."
                       : viewMode === "section" ? "No sections for this school year yet."
                       : "No students yet.")}
                </div>
              ) : (
                <div className={s.groupScroll}>
                  {groupedRows.map(g => (
                    <div key={g.key} className={s.group}>
                      <div className={s.groupHead}>
                        {g.label}
                        <span className={s.groupCount}>
                          {g.items.length} invoice{g.items.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      {g.items.length === 0 ? (
                        <div className={s.emptyGroup}>
                          No invoices yet
                        </div>
                      ) : (
                        <div className={s.cardGrid}>
                          {g.items.map(row => (
                            activeTab === "tuition-invoices"
                              ? <InvoiceCard key={row.id}
                                  invoice={row}
                                  student={studentById[row.studentId]}
                                  semester={semesterById[row.semesterId]}
                                  onBreakdown={() => openBreakdown(row)}
                                  onHistory={() => openInvoiceHistory(row)}
                                  onOpenStudent={openStudentProfile}
                                  s={s} />
                              : <PaymentInvoiceCard key={row.id}
                                  invoice={row}
                                  student={studentById[row.studentId]}
                                  semester={semesterById[row.semesterId]}
                                  payments={paymentsByInvoice[row.id] || []}
                                  onPay={() => openPayModal(row)}
                                  onHistory={() => openPaymentHistory(row)}
                                  onRefund={() => openRefundModal(row)}
                                  onOpenStudent={openStudentProfile}
                                  s={s} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : activeTab === "tuition-payments" ? (
              <PaymentInvoiceTable
                rows={filteredRows}
                totalCount={rows.length}
                studentById={studentById}
                sectionById={sectionById}
                gradeById={gradeById}
                paymentsByInvoice={paymentsByInvoice}
                onPay={openPayModal}
                onHistory={openPaymentHistory}
                onRefund={openRefundModal}
                onOpenStudent={openStudentProfile}
                onBreakdown={inv => setPaymentBreakdownModal({ invoice: inv })}
                onCharges={inv => {
                  // Resolve the school year from the invoice; the modal
                  // needs both `student` (to scope the list) and the
                  // SY id (the lookups.schoolYears row gives us a label).
                  const stu = studentById[inv.studentId];
                  const sy  = (lookups.schoolYears || []).find(y => y.id === inv.schoolYearId);
                  if (stu && sy) setChargesModal({ student: stu, schoolYear: sy });
                }}
                s={s}
              />
            ) : activeTab === "student-discounts" ? (
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      {schema.columns.map(([k, label]) => {
                        const active = sortBy === k;
                        return (
                          <th key={k}
                              className={`${s.sortable} ${active ? s.sortActive : ""}`}
                              aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                              onClick={() => {
                                if (sortBy === k) setSortDir(d => d === "asc" ? "desc" : "asc");
                                else { setSortBy(k); setSortDir("asc"); }
                              }}>
                            {label}
                            <span className={s.sortArrow}>
                              {active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
                            </span>
                          </th>
                        );
                      })}
                      <th className={s.actionsCol}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discountGroups.length === 0 ? (
                      <tr><td colSpan={schema.columns.length + 1} className={s.emptyRow}>
                        {rows.length === 0 ? "No records yet." : "No records match the current filters."}
                      </td></tr>
                    ) : discountGroups.map(g => {
                      const st = studentById[g.studentId];
                      const sy = (lookups.schoolYears || []).find(y => y.id === g.schoolYearId);
                      const empty = g.items.length === 0;
                      return (
                        <tr key={g.key}
                            className={`${canEditDiscounts ? s.clickableRow : ""} ${empty ? s.discountRowEmpty : ""}`.trim() || undefined}
                            onClick={canEditDiscounts
                              ? () => setManageDiscounts({ studentId: g.studentId, schoolYearId: g.schoolYearId, isNew: false })
                              : undefined}>
                          <td>
                            {st ? (
                              <button type="button" className={s.nameLink}
                                      onClick={e => { e.stopPropagation(); openStudentProfile(st); }}
                                      title="View profile">
                                {`${st.lastName}, ${st.firstName}`}
                              </button>
                            ) : "—"}
                          </td>
                          <td>{st?.studentNumber || "—"}</td>
                          <td>{sy?.label || "—"}</td>
                          <td>
                            <div className={s.discountChips}>
                              {empty ? (
                                <span className={s.discountChipNone}>No discount yet</span>
                              ) : g.items.map(it => {
                                const lbl = (DISCOUNT_TYPES.find(([v]) => v === it.type) || [])[1] || it.type;
                                // Show the custom name for OTHERS rows so two
                                // Others chips on the same student stay
                                // distinguishable at a glance.
                                const display = it.type === "OTHERS" && it.customName
                                  ? `${lbl} · ${it.customName}`
                                  : lbl;
                                return (
                                  <span key={it.id} className={s.discountChip}>
                                    {display} · {Number(it.percentage) || 0}%
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td>
                            {empty
                              ? <span className={s.discountTotalMuted}>0%</span>
                              : <b>{g.totalPct}%</b>}
                          </td>
                          <td className={s.actionsCol} onClick={e => e.stopPropagation()}>
                            {canEditDiscounts ? (
                              <button className={s.btnGhost}
                                      onClick={() => setManageDiscounts({ studentId: g.studentId, schoolYearId: g.schoolYearId, isNew: false })}>
                                Manage
                              </button>
                            ) : (
                              <span style={{ fontSize: "0.85rem", color: "#8a8070", fontStyle: "italic" }}>
                                View only
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      {schema.columns.map(([k, label]) => {
                        const active = sortBy === k;
                        return (
                          <th key={k}
                              className={`${s.sortable} ${active ? s.sortActive : ""}`}
                              aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                              onClick={() => {
                                if (sortBy === k) setSortDir(d => d === "asc" ? "desc" : "asc");
                                else { setSortBy(k); setSortDir("asc"); }
                              }}>
                            {label}
                            <span className={s.sortArrow}>
                              {active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
                            </span>
                          </th>
                        );
                      })}
                      <th className={s.actionsCol}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length === 0 ? (
                      <tr><td colSpan={schema.columns.length + 1} className={s.emptyRow}>
                        {rows.length === 0 ? "No records yet." : "No records match the current filters."}
                      </td></tr>
                    ) : sortedRows.map(row => (
                      <tr key={row.id}>
                        {schema.columns.map(([k]) => <td key={k}>{renderCell(row, k)}</td>)}
                        <td className={s.actionsCol}>
                          {activeTab !== "tuition-invoices" ? (
                            <>
                              <button className={s.btnGhost} onClick={() => startEdit(row)}>Edit</button>
                              <button className={s.btnDanger} onClick={() => remove(row)}>Delete</button>
                            </>
                          ) : (
                            // Invoices can't be edited directly. [i] opens
                            // the Breakdown modal (computation + recompute);
                            // [📋] opens the full activity report (payments +
                            // recompute events interleaved).
                            <>
                              <button type="button" className={s.btnIcon}
                                      title="Breakdown & recompute"
                                      aria-label="Breakdown"
                                      onClick={() => openBreakdown(row)}>i</button>
                              <button type="button" className={s.btnIconReport}
                                      title="Invoice history"
                                      aria-label="Invoice history"
                                      onClick={() => openInvoiceHistory(row)}>📋</button>
                              <button type="button" className={s.btnRecompute}
                                      onClick={() => openBreakdown(row)}>Recompute</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>

      {/* Create / Edit form modal — used by Fee Structures & Discounts.
          Click-outside does NOT dismiss (would lose form data); admins close
          via Cancel or the × button. Same pattern as the Academics tab.
          Portaled to <body> so the fixed-position overlay anchors to the
          viewport, not the .shell ancestor (which has a transform). */}
      {editing && createPortal(
        <div className={s.formOverlay}>
          <form onSubmit={save} className={s.formModal}>
            <div className={s.formModalHead}>
              <h3 className={s.formModalTitle}>
                {editing === "new" ? "New " : "Edit "}
                {TABS.find(t => t.key === activeTab)?.label.replace(/s$/, "") || ""}
              </h3>
              <button type="button" className={s.closeBtn}
                      onClick={cancel} disabled={busy} aria-label="Close">✕</button>
            </div>

            {error && <div className={s.error}>{error}</div>}

            <div className={s.formModalBody}>
              <div className={s.form}>
                {schema.fields.map(f => (
                  <FormField
                    key={f.key}
                    field={f}
                    value={form[f.key]}
                    onChange={v => setForm(prev => {
                      const next = { ...prev, [f.key]: v };
                      // Keep totalAmount in sync with the breakdown so admins
                      // can't ship a mismatched total. Read-only in the UI
                      // either way.
                      if (activeTab === "fee-structures"
                          && (f.key === "tuitionFee" || f.key === "miscFee" || f.key === "booksFee")) {
                        const n = x => (x === "" || x == null ? 0 : Number(x) || 0);
                        next.totalAmount = (n(next.tuitionFee) + n(next.miscFee) + n(next.booksFee)).toFixed(2);
                      }
                      return next;
                    })}
                    lookupData={f.lookup ? lookups[f.lookup] : null}
                    lookups={lookups}
                  />
                ))}
              </div>
            </div>

            <div className={s.formModalFoot}>
              <button type="button" className={s.btnGhost} onClick={cancel} disabled={busy}>Cancel</button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Saving..." : (editing === "new" ? "Create" : "Save changes")}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {payModal && createPortal(
        <PayModal
          invoice={payModal.invoice}
          student={studentById[payModal.invoice.studentId]}
          semester={semesterById[payModal.invoice.semesterId]}
          remaining={(Number(payModal.invoice.amount) || 0) - paidForInvoice(payModal.invoice.id)}
          onClose={() => setPayModal(null)}
          onSubmit={submitPayment}
          busy={busy}
          error={modalError}
        />,
        document.body
      )}

      {historyModal && createPortal(
        <HistoryModal
          invoice={
            // Prefer the freshest invoice from loaded tab data so the header
            // card (status, paid, remaining) reflects recent mutations.
            (data["tuition-payments"] || []).find(r => r.id === historyModal.invoice.id)
              || (data["tuition-invoices"] || []).find(r => r.id === historyModal.invoice.id)
              || historyModal.invoice
          }
          student={studentById[historyModal.invoice.studentId]}
          semester={semesterById[historyModal.invoice.semesterId]}
          mode={historyModal.mode}
          onClose={() => { setHistoryModal(null); setModalError(""); }}
          onPay={() => { setHistoryModal(null); openPayModal(historyModal.invoice); }}
          onChanged={async () => {
            // Re-sync whichever tab's data is loaded so row-level figures
            // reflect the modal's mutation.
            if (data["tuition-payments"]) await loadTab("tuition-payments");
            if (data["tuition-invoices"]) await loadTab("tuition-invoices");
          }}
        />,
        document.body
      )}

      {refundModal && createPortal(
        <RefundModal
          invoice={refundModal.invoice}
          student={studentById[refundModal.invoice.studentId]}
          semester={semesterById[refundModal.invoice.semesterId]}
          overpaid={(() => {
            const paid = (paymentsByInvoice[refundModal.invoice.id] || [])
              .reduce((s2, p) => s2 + (Number(p.amount) || 0), 0);
            return paid - (Number(refundModal.invoice.amount) || 0);
          })()}
          onClose={() => { setRefundModal(null); setModalError(""); }}
          onSubmit={submitRefund}
          busy={busy}
          error={modalError}
        />,
        document.body
      )}

      {breakdownModal && createPortal(
        <InvoiceBreakdownModal
          invoice={
            // Prefer the freshest row from data["tuition-invoices"] so the
            // modal reflects post-recompute changes without re-open.
            (data["tuition-invoices"] || []).find(r => r.id === breakdownModal.invoice.id) || breakdownModal.invoice
          }
          student={studentById[breakdownModal.invoice.studentId]}
          semester={semesterById[breakdownModal.invoice.semesterId]}
          onClose={() => { setBreakdownModal(null); setModalError(""); }}
          onRecompute={runRecomputeOne}
          busy={busy}
          error={modalError}
        />,
        document.body
      )}

      {recomputeAllModal && createPortal(
        <RecomputeAllModal
          syFilter={syFilter}
          semFilter={semFilter}
          schoolYears={lookups.schoolYears || []}
          semesters={lookups.semesters || []}
          totalRows={(data["tuition-invoices"] || []).length}
          filteredRows={filteredRows.length}
          onClose={() => { setRecomputeAllModal(null); setModalError(""); }}
          onSubmit={runRecomputeAll}
          busy={busy}
          error={modalError}
        />,
        document.body
      )}

      {paymentBreakdownModal && createPortal(
        <PaymentBreakdownModal
          // Prefer the freshest row from data["tuition-payments"] so the
          // breakdown reflects post-edit / post-delete changes without
          // a re-open. Falls back to the original click target.
          invoice={
            (data["tuition-payments"] || []).find(r => r.id === paymentBreakdownModal.invoice.id)
              || paymentBreakdownModal.invoice
          }
          student={studentById[paymentBreakdownModal.invoice.studentId]}
          semester={semesterById[paymentBreakdownModal.invoice.semesterId]}
          // Hand the payments down so the modal can resolve which payment
          // to split when the cashier reallocates an overpayment.
          payments={paymentsByInvoice[paymentBreakdownModal.invoice.id] || []}
          onClose={() => setPaymentBreakdownModal(null)}
          onPay={inv => { setPaymentBreakdownModal(null); openPayModal(inv); }}
          onRefund={inv => { setPaymentBreakdownModal(null); openRefundModal(inv); }}
          // After a successful split, refresh the Payments tab so the
          // Activity Log + invoice card both reflect the new split rows.
          onChanged={() => { loadTab("tuition-payments"); }}
        />,
        document.body
      )}

      {chargesModal && createPortal(
        <AdditionalChargesModal
          student={chargesModal.student}
          schoolYear={chargesModal.schoolYear}
          onClose={() => setChargesModal(null)}
          // No tab-data refresh needed yet — additional charges live in
          // their own ledger and don't affect TuitionInvoice rows. The
          // SOA + Breakdown modals re-fetch on every open, so they'll
          // see the new charges naturally.
          onChanged={() => {}}
        />,
        document.body
      )}

      {manageDiscounts && createPortal(
        <StudentDiscountsModal
          isNew={manageDiscounts.isNew}
          initialStudentId={manageDiscounts.studentId}
          initialSchoolYearId={manageDiscounts.schoolYearId}
          students={lookups.students || []}
          schoolYears={lookups.schoolYears || []}
          rows={rows}
          discountTypes={DISCOUNT_TYPES}
          onClose={() => setManageDiscounts(null)}
          onChanged={() => loadTab("student-discounts")}
          apiBase={API_BASE}
          s={s}
        />,
        document.body
      )}

      {/* Read-only profile + admin-gated Documents. Self-portals to <body>. */}
      <StudentDetailModal
        open={!!studentDetailTarget}
        student={studentDetailTarget}
        sections={lookups.sections || []}
        gradeLevels={lookups.gradeLevels || []}
        schoolYears={lookups.schoolYears || []}
        semesters={lookups.semesters || []}
        onClose={() => setStudentDetailTarget(null)}
      />
    </div>
  );
}

/** Per-tab filter bar. Renders the subset of controls that applies to the
 *  active tab, matching the Class Offerings layout — SY + Semester dropdowns,
 *  a search box, and status/type filters where relevant. */
function FilterBar({
  activeTab,
  schoolYears, semesters,
  syFilter, setSyFilter,
  semFilter, setSemFilter,
  search, setSearch,
  statusFilter, setStatusFilter,
  typeFilter, setTypeFilter,
  payDateRange, setPayDateRange,
  payDateFrom,  setPayDateFrom,
  payDateTo,    setPayDateTo,
  payDateField, setPayDateField,
  matchCount, totalCount,
}) {
  const showSemester  = activeTab === "tuition-invoices" || activeTab === "tuition-payments";
  const showStatus    = activeTab === "tuition-invoices" || activeTab === "tuition-payments";
  const showType      = activeTab === "student-discounts";
  const showDateRange = activeTab === "tuition-payments";

  const searchPlaceholder =
    activeTab === "student-discounts" ? "Student name or #…"
    : activeTab === "tuition-invoices" ? "Student name or #…"
    : activeTab === "tuition-payments" ? "Student name, # or OR #…"
    : "Student name or #…";

  return (
    <div className={s.filterBar}>
      <label className={s.filterField}>
        <span>School Year</span>
        <select value={syFilter} onChange={e => setSyFilter(e.target.value)}>
          <option value="">All years</option>
          {schoolYears.map(y => (
            <option key={y.id} value={y.id}>
              {y.label}{y.active ? " · Active" : ""}
            </option>
          ))}
        </select>
      </label>

      {showSemester && (
        <label className={s.filterField}>
          <span>Semester</span>
          <select value={semFilter} onChange={e => setSemFilter(e.target.value)} disabled={!syFilter}>
            <option value="">All semesters</option>
            {semesters
              .filter(x => !syFilter || String(x.schoolYearId) === String(syFilter))
              .map(x => (
                <option key={x.id} value={x.id}>
                  {x.label}{x.active ? " · Active" : ""}
                </option>
              ))}
          </select>
        </label>
      )}

      {showStatus && (
        <label className={s.filterField}>
          <span>Status</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PARTIAL">Partial</option>
            <option value="PAID">Paid</option>
          </select>
        </label>
      )}

      {showType && (
        <label className={s.filterField}>
          <span>Type</span>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            <option value="__any">Has any discount</option>
            <option value="__none">No discount</option>
            {DISCOUNT_TYPES.map(([v, lbl]) => (
              <option key={v} value={v}>{lbl}</option>
            ))}
          </select>
        </label>
      )}

      {showDateRange && (
        <>
          <label className={s.filterField}>
            <span>Payment window</span>
            <select value={payDateRange} onChange={e => setPayDateRange(e.target.value)}>
              {PAY_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          {payDateRange === "custom" && (
            <>
              <label className={s.filterField}>
                <span>From</span>
                <input type="date"
                       value={payDateFrom}
                       max={payDateTo || undefined}
                       onChange={e => setPayDateFrom(e.target.value)} />
              </label>
              <label className={s.filterField}>
                <span>To</span>
                <input type="date"
                       value={payDateTo}
                       min={payDateFrom || undefined}
                       max={isoToday(0)}
                       onChange={e => setPayDateTo(e.target.value)} />
              </label>
            </>
          )}
          <label className={s.filterField}>
            <span>Filter date by</span>
            <select value={payDateField} onChange={e => setPayDateField(e.target.value)}>
              <option value="received">Received date</option>
              <option value="entered">Date entered in system</option>
            </select>
          </label>
        </>
      )}

      <label className={`${s.filterField} ${s.searchField}`}>
        <span>
          Search
          {search && (
            <span className={s.matchHint}>
              {" "}· {matchCount} of {totalCount}
            </span>
          )}
        </span>
        <div className={s.searchWrap}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
               className={s.searchIcon} aria-hidden>
            <circle cx="9" cy="9" r="6" />
            <path d="M15 15l-3.5-3.5" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
          />
          {search && (
            <button type="button" className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
          )}
        </div>
      </label>
    </div>
  );
}

/** Animates a number counting up to `value` whenever it changes — a small
 *  polish touch for the summary bar. Skips the animation (jumps straight
 *  to the final value) for viewers with prefers-reduced-motion set, and
 *  always cancels its rAF loop on unmount/re-trigger so it can never leak
 *  a stray frame callback or keep animating a stat that's no longer on
 *  screen. Pure read of already-fetched data — no side effects beyond
 *  its own local state. */
function useCountUp(value, duration = 700) {
  const [display, setDisplay] = useState(value);
  // Tracks wherever the number is RIGHT NOW, mid-animation or settled —
  // not just its last completed target — so if the filters change again
  // before an animation finishes, the next run continues smoothly from
  // what's actually on screen instead of jumping back to the old value.
  const currentRef = useRef(Number(value) || 0);

  useEffect(() => {
    const target = Number(value) || 0;
    const reduceMotion = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    // Skip animating a no-op change (avoids a pointless rAF loop when a
    // filter tweak doesn't actually move this particular figure).
    if (reduceMotion || Math.abs(target - currentRef.current) < 0.005) {
      currentRef.current = target;
      setDisplay(target);
      return;
    }
    const from = currentRef.current;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      const v = from + (target - from) * eased;
      currentRef.current = v;
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else currentRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [value, duration]);

  return display;
}

/** Human label for the "Collected" hero stat — describes exactly which
 *  window/field the figure is scoped to, so it's unambiguous when
 *  cross-checked against a report pulled for "the same range". */
function payWindowLabel(range, field, from, to) {
  const fieldLabel = field === "entered" ? "entered" : "received";
  if (range === "all") return "All time";
  if (range === "custom") {
    if (from && to) return `${from} → ${to} · by date ${fieldLabel}`;
    return `Custom range · by date ${fieldLabel}`;
  }
  const found = PAY_RANGES.find(r => r.value === range);
  return `${found ? found.label : "Selected window"} · by date ${fieldLabel}`;
}

/** Reconciliation summary bar for the Payments tab — sits above the
 *  student list (any view mode) and totals whatever the current filters
 *  are showing, so an admin can compare this figure against the
 *  dashboard's "Collected This Month" tile / a pulled report before
 *  treating the numbers as reconciled. Purely a client-side aggregation
 *  of data the page already fetched for this admin/cashier session — no
 *  extra network calls, no new writable state, nothing that can drift
 *  from the table it's summarizing. */
function PaymentsSummaryBar({ summary, payDateRange, payDateField, payDateFrom, payDateTo, s }) {
  const collected      = useCountUp(summary.collected);
  const totalAmount    = useCountUp(summary.totalAmount);
  const totalPaid      = useCountUp(summary.totalPaid);
  const totalRemaining = useCountUp(summary.totalRemaining);

  const netOverpaid = summary.totalRemaining < -0.005;
  const windowLabel = payWindowLabel(payDateRange, payDateField, payDateFrom, payDateTo);

  return (
    <div className={s.paySummary}>
      <div className={s.paySummaryHero}>
        <span className={s.paySummaryHeroLabel}>Actual Collected</span>
        <span className={s.paySummaryHeroValue}>₱{fmtMoney(collected)}</span>
        <span className={s.paySummaryHeroSub}>
          {windowLabel} · {summary.paymentCount} payment{summary.paymentCount === 1 ? "" : "s"} · {summary.studentCount} student{summary.studentCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className={s.paySummaryTile}>
        <span className={s.paySummaryTileLabel}>Total Amount</span>
        <span className={s.paySummaryTileValue}>₱{fmtMoney(totalAmount)}</span>
        <span className={s.paySummaryTileHint}>listed students · as of today</span>
      </div>
      <div className={s.paySummaryTile}>
        <span className={s.paySummaryTileLabel}>Total Paid</span>
        <span className={`${s.paySummaryTileValue} ${s.paySummaryPaid}`}>₱{fmtMoney(totalPaid)}</span>
        <span className={s.paySummaryTileHint}>lifetime, this list</span>
      </div>
      <div className={s.paySummaryTile}>
        <span className={s.paySummaryTileLabel}>{netOverpaid ? "Net Overpaid" : "Total Remaining"}</span>
        <span className={`${s.paySummaryTileValue} ${netOverpaid ? s.paySummaryOverpaid : s.paySummaryDue}`}>
          ₱{fmtMoney(netOverpaid ? -totalRemaining : totalRemaining)}
        </span>
        <span className={s.paySummaryTileHint}>Total Amount − Total Paid</span>
      </div>
    </div>
  );
}

/* Card used in Invoices' grouped views (Students / Grade Level / Section).
 * Invoices are not directly editable — the [i] button opens the Breakdown
 * modal where admins can inspect the computation, see the adjustment
 * history, and run Recompute with a reason. */
function InvoiceCard({ invoice, student, semester, onBreakdown, onHistory, onOpenStudent, s }) {
  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;
  const status = invoice.status || "UNPAID";
  return (
    <div className={s.recordCard}>
      <div className={s.recordCardTop}>
        {student && onOpenStudent ? (
          <button type="button" className={`${s.recordName} ${s.nameLink}`}
                  onClick={() => onOpenStudent(student)}
                  title="View profile">
            {name}
          </button>
        ) : (
          <div className={s.recordName}>{name}</div>
        )}
        <span className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}>{status}</span>
      </div>
      <div className={s.recordMeta}>
        {student?.studentNumber && <span>{student.studentNumber}</span>}
        {semester?.label && <span>· {semester.label}</span>}
      </div>
      <div className={s.recordFigures}>
        <span className={s.recordAmount}>{Number(invoice.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        {invoice.dueDate && <span className={s.recordDue}>Due {invoice.dueDate}</span>}
      </div>
      <div className={s.recordActions}>
        <button type="button" className={s.btnIcon}
                title="Breakdown & recompute" aria-label="Breakdown"
                onClick={onBreakdown}>i</button>
        <button type="button" className={s.btnIconReport}
                title="Activity report" aria-label="Activity report"
                onClick={onHistory}>📋</button>
        <button type="button" className={s.btnRecompute} onClick={onBreakdown}>
          Recompute
        </button>
      </div>
    </div>
  );
}

/** Formats a peso figure with 2-decimal precision and thousand separators.
 *  Used by the Payments-tab modals and cards. */
/** Read the current user from session/local storage. Used by the modals
 *  (outside the main component scope) to gate admin-only actions like the
 *  payment Delete button. */
function getCurrentUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

/** Fetch the accepted bank list from site-content. Stored as a JSON array
 *  under the tuition.banks key (managed from Settings → Tuition). Returns []
 *  on any failure so the modals can still open. */
async function loadBankList() {
  try {
    const res = await fetch(`${API_BASE}/api/site-content`);
    if (!res.ok) return [];
    const map = await res.json();
    const raw = map["tuition.banks"];
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(x => typeof x === "string" && x.trim()) : [];
    } catch {
      return String(raw).split("\n").map(s => s.trim()).filter(Boolean);
    }
  } catch { return []; }
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* Payment-tab card: one per invoice, with Pay + History actions.
 * Analogous to the inventory-sales row pattern — the unit of work is the
 * invoice (the "item"), and the card aggregates every payment against it. */
function PaymentInvoiceCard({ invoice, student, semester, payments, onPay, onHistory, onRefund, onOpenStudent, s }) {
  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;
  const paid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const total = Number(invoice.amount) || 0;
  const remaining = total - paid;
  const overpaid = remaining < -0.005;  // paid > total by a meaningful amount
  const status = invoice.status || "UNPAID";
  return (
    <div className={`${s.recordCard} ${overpaid ? s.recordCardOverpaid : ""}`}>
      <div className={s.recordCardTop}>
        {student && onOpenStudent ? (
          <button type="button" className={`${s.recordName} ${s.nameLink}`}
                  onClick={() => onOpenStudent(student)}
                  title="View profile">
            {name}
          </button>
        ) : (
          <div className={s.recordName}>{name}</div>
        )}
        <span className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}>{status}</span>
      </div>
      <div className={s.recordMeta}>
        {student?.studentNumber && <span>{student.studentNumber}</span>}
        {semester?.label && <span>· {semester.label}</span>}
        {invoice.dueDate && <span>· Due {invoice.dueDate}</span>}
      </div>
      {overpaid && (
        <div className={s.overpaidBanner}>
          Overpaid by ₱{fmtMoney(-remaining)} — refund owed
        </div>
      )}
      <div className={s.payStats}>
        <div className={s.payStat}>
          <span className={s.payStatLabel}>Total</span>
          <span className={s.payStatValue}>₱{fmtMoney(total)}</span>
        </div>
        <div className={s.payStat}>
          <span className={s.payStatLabel}>Paid</span>
          <span className={`${s.payStatValue} ${s.payStatPaid}`}>₱{fmtMoney(paid)}</span>
        </div>
        <div className={s.payStat}>
          <span className={s.payStatLabel}>{overpaid ? "Overpaid" : "Remaining"}</span>
          <span className={`${s.payStatValue} ${overpaid ? s.payStatOverpaid : (remaining > 0.005 ? s.payStatDue : s.payStatClear)}`}>
            ₱{fmtMoney(overpaid ? -remaining : remaining)}
          </span>
        </div>
      </div>
      <div className={s.recordActions}>
        <button type="button" className={s.btnIcon} title="Invoice history"
                aria-label="Invoice history" onClick={onHistory}>i</button>
        {overpaid ? (
          <button type="button" className={s.btnRefund} onClick={onRefund}>Issue Refund</button>
        ) : (
          <button type="button" className={s.btnPay} onClick={onPay}>Pay</button>
        )}
      </div>
    </div>
  );
}

/* Payment-tab table view (All Students / List form). Mirrors the card view's
 * figures but in a dense tabular layout.
 *
 * Columns: Student · Grade Level · Total · Paid · Remaining · Status · Actions
 *   - Grade Level replaces the old "Semester" column (the active semester
 *     shows on the filter bar, so the per-row repetition was noise).
 *   - The "Due Date" column was dropped — invoices in this app don't track
 *     a per-row due date the cashier acts on, so the column was always "—".
 *
 * Actions: [📊 Breakdown] · [i Invoice history] · [Pay / Refund]
 *   - Breakdown opens a per-month ledger so the cashier can answer
 *     "how much has this student paid for September?" without reading
 *     the full activity log. */
function PaymentInvoiceTable({
  rows, totalCount, studentById, sectionById, gradeById,
  paymentsByInvoice, onPay, onHistory, onRefund, onOpenStudent, onBreakdown,
  onCharges, s,
}) {
  /** Resolve a student row to "Grade 4 · St. Mary" via section lookup.
   *  Falls back to "—" if the section/grade isn't loaded yet. */
  function gradeLabel(st) {
    if (!st?.currentSectionId) return "—";
    const sec = sectionById ? sectionById[st.currentSectionId] : null;
    if (!sec) return "—";
    const grade = sec.gradeLevelId && gradeById ? gradeById[sec.gradeLevelId] : null;
    if (grade?.name && sec.name) return `${grade.name} · ${sec.name}`;
    return grade?.name || sec.name || "—";
  }

  const COLUMN_COUNT = 7; // Student · Grade · Total · Paid · Remaining · Status · Actions

  if (rows.length === 0) {
    return (
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Student</th><th>Grade Level</th><th>Total</th><th>Paid</th>
              <th>Remaining</th><th>Status</th>
              <th className={s.actionsCol}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={COLUMN_COUNT} className={s.emptyRow}>
              {totalCount === 0 ? "No invoices yet." : "No invoices match the current filters."}
            </td></tr>
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead>
          <tr>
            <th>Student</th><th>Grade Level</th><th>Total</th><th>Paid</th>
            <th>Remaining</th><th>Status</th>
            <th className={s.actionsCol}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(inv => {
            const st  = studentById[inv.studentId];
            const payments = paymentsByInvoice[inv.id] || [];
            const paid = payments.reduce((s2, p) => s2 + (Number(p.amount) || 0), 0);
            const total = Number(inv.amount) || 0;
            const remaining = total - paid;
            const overpaid = remaining < -0.005;
            const status = inv.status || "UNPAID";
            return (
              <tr key={inv.id} className={overpaid ? s.rowOverpaid : ""}>
                <td>
                  {st && onOpenStudent ? (
                    <button type="button" className={s.nameLink}
                            onClick={() => onOpenStudent(st)}
                            title="View profile">
                      {`${st.lastName}, ${st.firstName}`}
                    </button>
                  ) : (
                    st ? `${st.lastName}, ${st.firstName}` : `Student #${inv.studentId}`
                  )}
                  {st?.studentNumber && <span className={s.studentNoInline}> · {st.studentNumber}</span>}
                </td>
                <td>{gradeLabel(st)}</td>
                <td className={s.amountCol}>₱{fmtMoney(total)}</td>
                <td className={`${s.amountCol} ${s.payStatPaid}`}>₱{fmtMoney(paid)}</td>
                <td className={`${s.amountCol} ${overpaid ? s.payStatOverpaid : (remaining > 0.005 ? s.payStatDue : s.payStatClear)}`}>
                  {overpaid
                    ? <>₱{fmtMoney(-remaining)} <span className={s.overpaidTag}>overpaid</span></>
                    : <>₱{fmtMoney(remaining)}</>}
                </td>
                <td><span className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}>{status}</span></td>
                <td className={s.actionsCol}>
                  {onBreakdown && (
                    <button type="button" className={s.btnIcon}
                            title="Per-month payment breakdown"
                            aria-label="Per-month payment breakdown"
                            onClick={() => onBreakdown(inv)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2.2"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="4"  y1="20" x2="4"  y2="10" />
                        <line x1="10" y1="20" x2="10" y2="6"  />
                        <line x1="16" y1="20" x2="16" y2="13" />
                        <line x1="3"  y1="20" x2="21" y2="20" />
                      </svg>
                    </button>
                  )}
                  {onCharges && (
                    <button type="button" className={s.btnIcon}
                            title="Additional charges (summer class, etc.)"
                            aria-label="Additional charges"
                            onClick={() => onCharges(inv)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2.2"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="12" y1="5"  x2="12" y2="19" />
                        <line x1="5"  y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  )}
                  <button type="button" className={s.btnIcon} title="Invoice history"
                          aria-label="Invoice history" onClick={() => onHistory(inv)}>i</button>
                  {overpaid ? (
                    <button type="button" className={s.btnRefund} onClick={() => onRefund(inv)}>Refund</button>
                  ) : (
                    <button type="button" className={s.btnPay} onClick={() => onPay(inv)}>Pay</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Modal: record a payment against a specific invoice. Amount defaults to the
 * remaining balance; negative amounts are accepted as reversals (same
 * convention as the inventory-sales layaway flow). */
function PayModal({ invoice, student, semester, remaining, onClose, onSubmit, busy, error }) {
  const [amount, setAmount]           = useState("");
  const [method, setMethod]           = useState("CASH");
  const [orNumber, setOrNumber]                 = useState("");
  const [referenceNo, setReferenceNo]           = useState("");
  const [bankName, setBankName]                 = useState("");
  // Actual calendar date the school received the payment. Starts empty
  // (required field below) so the cashier has to deliberately pick a date
  // instead of accidentally submitting under an auto-filled "today" they
  // never looked at. Distinct from the auto-stamped paidAt, which is the
  // timestamp when the record was created in the system.
  const [paymentReceivedOn, setPaymentReceivedOn] = useState("");
  const [notes, setNotes]             = useState("");
  // Cash payments don't carry a bank-side reference — only the school's
  // OR # is required. Anything else (BANK / GCASH / CARD / OTHER) is a
  // digital channel and the reference is the bank/wallet txn id.
  const referenceRequired = method !== "CASH";
  // "0" = enrollment, "1".."N" = month row index. There's no "Auto" option
  // any more — every payment must be recorded against one specific month,
  // and it lands on exactly that month, full stop (no cascading into
  // others). Initially null so the SoA-load effect can pick the right
  // default (the next unpaid target) without fighting a pre-set state.
  const [appliesTo, setAppliesTo]     = useState(null);
  // Track whether the cashier has edited the amount field manually. If
  // they have, we stop auto-overwriting it when the dropdown changes —
  // they're in control of the exact peso amount from that point on.
  const [amountTouched, setAmountTouched] = useState(false);
  const [soa, setSoa]                 = useState(null);
  const [soaLoading, setSoaLoading]   = useState(true);
  // Bank options pulled from Settings → Tuition. Empty list means nothing
  // was configured yet, and the Bank Name select will render disabled with
  // a hint pointing at Settings.
  const [bankList, setBankList]       = useState([]);

  useEffect(() => {
    let cancelled = false;
    setSoaLoading(true);
    (async () => {
      try {
        const q = `studentId=${invoice.studentId}&schoolYearId=${invoice.schoolYearId}`;
        const res = await fetch(`${API_BASE}/api/tuition/soa?${q}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSoa(data);
      } finally {
        if (!cancelled) setSoaLoading(false);
      }
    })();
    loadBankList().then(list => { if (!cancelled) setBankList(list); });
    return () => { cancelled = true; };
  }, [invoice.studentId, invoice.schoolYearId]);

  /** Builds an ordered list of payment targets with their Due/Paid/Remaining.
   *  Enrollment first, then each month row. Used to pre-fill the dropdown
   *  labels AND to figure out what "next unpaid" is on modal open. */
  const targets = (() => {
    if (!soa) return [];
    const list = [];
    const eDue  = Number(soa.enrollmentDue)     || 0;
    const ePaid = Number(soa.enrollmentPaidSum) || 0;
    list.push({
      appliesTo: 0, label: "Enrollment fee",
      due: eDue, paid: ePaid, remaining: Math.max(eDue - ePaid, 0),
    });
    (soa.months_rows || []).forEach(r => {
      const due  = Number(r.monthlyDue) || 0;
      const paid = Number(r.amount)     || 0;
      list.push({
        appliesTo: r.index, label: r.month,
        due, paid, remaining: Math.max(due - paid, 0),
      });
    });
    return list;
  })();

  /** The next thing the cashier should be paying for: enrollment if unpaid,
   *  otherwise the first month with a remaining balance. When everything's
   *  settled, falls back to the invoice's remaining (likely zero). */
  const nextTarget = targets.find(t => t.remaining > 0.005) || null;

  /** Due / Paid / Remaining for whichever target the dropdown currently points at. */
  const targetSummary = (() => {
    if (appliesTo === null || appliesTo === undefined) return nextTarget;
    return targets.find(t => String(t.appliesTo) === String(appliesTo)) || null;
  })();

  // Once the SoA loads, default the dropdown to the next unpaid target
  // (cashiers can still change it) and pre-fill the amount with its
  // remaining balance.
  useEffect(() => {
    if (soaLoading) return;
    if (appliesTo !== null) return; // already initialised
    const initial = nextTarget ? nextTarget.appliesTo : (targets[0] ? targets[0].appliesTo : 0);
    setAppliesTo(String(initial));
    if (!amountTouched) {
      if (nextTarget) setAmount(nextTarget.remaining.toFixed(2));
      else setAmount(remaining > 0 ? remaining.toFixed(2) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soaLoading, nextTarget?.appliesTo]);

  // When the cashier changes the dropdown and hasn't hand-edited the
  // amount yet, keep the amount in sync with the picked target.
  function pickAppliesTo(val) {
    setAppliesTo(val);
    if (amountTouched) return;
    const t = targets.find(tt => String(tt.appliesTo) === String(val));
    if (t) setAmount(t.remaining.toFixed(2));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed === 0) return;
    // OR # is the school's audit trail and is required for every payment.
    if (!orNumber.trim()) return;
    // Reference # required only for non-cash methods.
    if (referenceRequired && !referenceNo.trim()) return;
    // Date received is required — every payment needs an actual receipt date.
    if (!paymentReceivedOn) return;
    // 0 = enrollment, 1..N = month row index — always a specific target.
    let applies = null;
    if (appliesTo !== null && appliesTo !== undefined) {
      const n = Number(appliesTo);
      if (Number.isFinite(n)) applies = n;
    }
    onSubmit({
      invoiceId:   invoice.id,
      amount:      parsed,
      method,
      orNumber:    orNumber.trim(),
      // For CASH, drop the digital reference entirely so the saved row
      // doesn't carry a stale value if the cashier toggled methods.
      referenceNo: referenceRequired ? (referenceNo.trim() || null) : null,
      // Bank name is bank-transfer-only; clear it otherwise so a saved
      // payment's channel matches what the UI showed at submit time.
      bankName:    method === "BANK" ? (bankName.trim() || null) : null,
      paymentReceivedOn: paymentReceivedOn || null,
      notes:       notes.trim() || null,
      appliesToMonth: applies,
      // When the cashier targets the enrollment row, stamp the particular
      // too — keeps legacy SoA-layout consumers that still branch on it.
      particular: applies === 0 ? "enrollment fee" : null,
    });
  }

  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;

  // Close on Escape — the backdrop no longer closes on outside click, so
  // this and the explicit × / Cancel buttons are the only ways out.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalPinned}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Record Payment</h3>
          <button className={s.modalClose} type="button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className={s.modalForm}>
          <div className={s.modalScrollArea}>
            <div className={s.modalSub}>
              <div className={s.modalSubName}>{name}</div>
              <div className={s.modalSubMeta}>
                {student?.studentNumber && <span>#{student.studentNumber}</span>}
                {semester?.label && <span>· {semester.label}</span>}
              </div>
              <div className={s.modalSubRow}>
                <span>Invoice total: <b>₱{fmtMoney(invoice.amount)}</b></span>
                <span>Remaining: <b className={remaining > 0.005 ? s.payStatDue : s.payStatClear}>₱{fmtMoney(remaining)}</b></span>
              </div>
            </div>
            {error && <div className={s.error}>{error}</div>}
            <div className={s.field}>
              <label className={s.label}>Applies to</label>
            <select className={s.input}
                    value={appliesTo ?? ""}
                    onChange={e => pickAppliesTo(e.target.value)}
                    disabled={soaLoading}>
              {targets.map(t => (
                <option key={t.appliesTo} value={t.appliesTo}>
                  {t.label} {t.remaining <= 0.005
                    ? "· settled"
                    : `— ₱${fmtMoney(t.remaining)} remaining`}
                </option>
              ))}
            </select>
            {targetSummary && (
              <div className={s.fieldHint}>
                {targetSummary.label} · Due ₱{fmtMoney(targetSummary.due)}
                {" · Paid ₱"}{fmtMoney(targetSummary.paid)}
                {" · "}
                <b style={{ color: targetSummary.remaining > 0.005 ? "#c0392b" : "#2e6a3d" }}>
                  Remaining ₱{fmtMoney(targetSummary.remaining)}
                </b>
              </div>
            )}
          </div>
          <div className={s.field}>
            <label className={s.label}>Amount (₱) <span className={s.req}>*</span></label>
            <input
              className={s.input}
              type="number"
              step="0.01"
              autoFocus
              value={amount}
              onChange={e => { setAmount(e.target.value); setAmountTouched(true); }}
              required
            />
            <div className={s.fieldHint}>
              Pre-filled with the picked row's remaining — change it to pay more
              (advance) or less (partial / promissory). Use a negative amount to
              reverse a mistaken payment.
            </div>
          </div>
          <div className={s.field}>
            <label className={s.label}>Method</label>
            <select className={s.input} value={method} onChange={e => setMethod(e.target.value)}>
              <option value="CASH">Cash</option>
              <option value="BANK">Bank transfer</option>
              <option value="GCASH">GCash</option>
              <option value="CARD">Card</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          {/* Bank name only makes sense for a bank transfer. Dropdown-only
           *  (canonical list lives in Settings → Tuition) so history rows
           *  don't drift with typos or case differences. */}
          {method === "BANK" && (
            <div className={s.field}>
              <label className={s.label}>Bank name</label>
              <select
                className={s.input}
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                disabled={bankList.length === 0}>
                <option value="">
                  {bankList.length === 0 ? "— no banks configured —" : "— select a bank —"}
                </option>
                {bankList.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {bankList.length === 0 && (
                <div className={s.fieldHint}>
                  Ask an admin to add banks in Settings → Tuition.
                </div>
              )}
            </div>
          )}
          {/* OR # — required for every payment regardless of method.
              School's own Official Receipt tracker. Digits only (receipt
              booklets are numeric); kept as text (not type="number") so
              leading zeros like "000123" survive. Uniqueness itself is
              enforced server-side — see TuitionPaymentController.create's
              findByOrNumberStrict check. */}
          <div className={s.field}>
            <label className={s.label}>
              OR # <span className={s.req}>*</span>
            </label>
            <input
              className={s.input}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={orNumber}
              onChange={e => setOrNumber(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, ""))}
              placeholder="Official Receipt number (school tracker)"
              maxLength={64}
              required
            />
            <div className={s.fieldHint}>
              The school's Official Receipt number — digits only, required and
              unique for every payment.
            </div>
          </div>

          {/* Reference # — channel / bank-side transaction id. Required
              for non-cash methods; rendered-but-optional for CASH so a
              cashier can still capture a redundant digital ref if needed. */}
          <div className={s.field}>
            <label className={s.label}>
              Reference #{" "}
              {referenceRequired
                ? <span className={s.req}>*</span>
                : <span className={s.fieldOpt}>(optional · cash)</span>}
            </label>
            <input
              className={s.input}
              type="text"
              value={referenceNo}
              onChange={e => setReferenceNo(e.target.value)}
              placeholder={
                method === "CASH"  ? "Not required for cash"
              : method === "BANK"  ? "Deposit slip #"
              : method === "GCASH" ? "GCash reference #"
              : method === "CARD"  ? "Approval / auth #"
              :                      "Channel reference number"
              }
              maxLength={64}
              required={referenceRequired}
            />
          </div>

          {/* Date received — the actual day the money was collected. Left
              blank on open (not auto-filled with today) so the cashier has
              to consciously set it rather than submit under a date they
              never actually checked. The system also stores the entry
              timestamp separately (paidAt), so reports can distinguish
              "received Oct 15, entered Nov 10". */}
          <div className={s.field}>
            <label className={s.label}>
              Date received <span className={s.req}>*</span>{" "}
              <span className={s.fieldOpt}>(actual receipt date)</span>
            </label>
            <input
              className={s.input}
              type="date"
              value={paymentReceivedOn}
              onChange={e => setPaymentReceivedOn(e.target.value)}
              required
            />
            <div className={s.fieldHint}>
              Enter the actual date the school received this payment.
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>Notes <span className={s.fieldOpt}>(optional)</span></label>
            <textarea
              className={`${s.input} ${s.textarea}`}
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Half payment agreed, rest next month…"
            />
          </div>
          </div>
          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost} onClick={onClose}>Cancel</button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !orNumber.trim() || !paymentReceivedOn
                      || (referenceRequired && !referenceNo.trim())}>
              {busy ? "Saving…" : "✓ Confirm Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Modal: full invoice-level activity report — mirrors the inventory Item
 * History layout. Header card (Total / Paid / Remaining / Status / Due /
 * Created) + a single chronological timeline that interleaves payments
 * (gold dots) and recompute adjustments (purple dots) so an accountant can
 * audit the invoice end-to-end without jumping tabs.
 *
 * Self-fetches both streams on mount and exposes `onChanged` so the parent
 * can refresh its tab data after a delete. */
function HistoryModal({ invoice, student, semester, mode = "payments", onClose, onPay, onChanged }) {
  const isAdjustments = mode === "adjustments";
  const isPayments    = mode === "payments";
  // Delete is admin-only — cashiers and staff can see the history but can't
  // reverse a saved payment. Matches the backend's AdminGuard on
  // DELETE /api/tuition-payments/{id}; hiding the button keeps the UI
  // honest when a non-admin opens the modal.
  const currentUser = getCurrentUser();
  const currentRole = currentUser?.role;
  const currentUsername = currentUser?.username;
  const canDeletePayments = currentRole === "Admin" || currentRole === "SuperAdmin";
  // Cashier / CashierStaff used to be able to delete payments directly.
  // The backend now restricts DELETE to Admin/SuperAdmin and routes the
  // cashier through an approval queue — see PaymentDeletionRequestController.
  // The flag controls whether the row shows the "Request delete" button
  // instead of the admin-only "Delete" button.
  const canRequestDeletion = currentRole === "Cashier" || currentRole === "CashierStaff";
  const [payments, setPayments]       = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  // Map paymentId → pending PaymentDeletionRequest (own user's). Drives
  // the "Pending approval" pill on rows the cashier already requested.
  const [pendingByPaymentId, setPendingByPaymentId] = useState({});
  // Open "Request delete" modal targets a single payment + reason.
  const [requestTarget, setRequestTarget] = useState(null);
  const [requestReason, setRequestReason] = useState("");
  const [requestBusy,   setRequestBusy]   = useState(false);
  const [requestErr,    setRequestErr]    = useState("");
  // Per-payment coverage map computed by SoA — keyed by payment id, value is
  // a list of slot hits {slotIdx, label, amount, overpay}. Lets the Activity
  // Log annotate "→ Covers Enrollment + August + September" etc.
  const [coverage, setCoverage]       = useState({});
  // Lookup for staff name + role on payment / adjustment rows. Payments
  // stamp receivedByStaffId on create, adjustments stamp changedByStaffId —
  // resolve either to a display label like "Maria Santos · Cashier".
  const [employeeById, setEmployeeById] = useState({});
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState("");
  const [deleting, setDeleting]       = useState(false);

  // Additional charges for this student in this school year — fees outside
  // the regular tuition cascade (summer class, special curriculum, etc.).
  // Surfaced here so the cashier sees the COMPLETE picture in one modal,
  // not just the tuition slice. Loaded alongside the rest of the page.
  const [additionalCharges, setAdditionalCharges] = useState([]);

  // Per-charge payment-history modal target — opens from the (i) icon
  // next to each additional-charge label in the read-only block below.
  // Read-only audit view of every receipt against the charge.
  const [chargeHistoryTarget, setChargeHistoryTarget] = useState(null);

  // Per-payment receipt-modal target — opens from the "Receipt" button
  // on each tuition payment row in the Activity Log. Renders a
  // polished mini OR with the coverage breakdown so a parent who paid
  // ₱7,777 across four months sees exactly which months it landed on.
  const [paymentReceiptTarget, setPaymentReceiptTarget] = useState(null);

  // SPA navigation hook — used by "View full SOA" so the route change
  // happens in the SAME tab. Opening in a new tab loses sessionStorage
  // auth (sessionStorage is per-tab) and bounces the user to /login,
  // which is the bug we're fixing here.
  const navigate = useNavigate();

  // Per-payment edit modal. Role-aware:
  //   • Admin / SuperAdmin → direct PUT (mode = "direct")
  //   • Cashier / CashierStaff → submits a PaymentEditRequest with reason
  //     (mode = "request"); Owner / SuperAdmin must approve.
  // Both flows use the same EditPaymentModal component; only the on-save
  // semantics differ.
  const [editingPayment, setEditingPayment] = useState(null);
  const [editBusy,       setEditBusy]       = useState(false);
  const [editError,      setEditError]      = useState("");

  // Pending edit requests, keyed by paymentId — drives the "⏳ Edit pending"
  // badge per row and the inline approve / reject affordance for approvers.
  const [pendingEditByPaymentId, setPendingEditByPaymentId] = useState({});
  // Approve / reject in-flight on a request id (used to disable the buttons).
  const [reviewingId, setReviewingId] = useState(null);
  // RejectReasonDialog target — { id, paymentId, ... } | null. Populated
  // when the approver clicks Reject on a pending edit-request row.
  const [rejectingEdit, setRejectingEdit] = useState(null);

  // Approval gate matches the backend's requireOwnerOrSuperAdmin — Admin
  // can edit directly but cannot approve cashier-initiated requests.
  const canApproveEdits = currentRole === "Owner" || currentRole === "SuperAdmin";
  // Cashier roles can request an edit; non-admin viewers see neither edit
  // nor request affordances. Admin / SuperAdmin / Owner skip the request
  // flow because they (or someone above them) can edit directly.
  const canRequestEdit = currentRole === "Cashier" || currentRole === "CashierStaff";

  async function loadAll() {
    setLoading(true); setLoadError("");
    try {
      // We always fetch all four — the header card figures need payments for
      // the Paid/Remaining totals even when the activity list only shows
      // adjustments, the SoA fetch hands us per-payment allocation, and the
      // employees list resolves processor names / roles in the log.
      const soaUrl = `${API_BASE}/api/tuition/soa?studentId=${invoice.studentId}&schoolYearId=${invoice.schoolYearId}`;
      const acUrl  = `${API_BASE}/api/additional-charges?studentId=${invoice.studentId}`
                   + `&schoolYearId=${invoice.schoolYearId}`;
      const [pRes, aRes, sRes, eRes, acRes] = await Promise.all([
        fetch(`${API_BASE}/api/tuition-payments?invoiceId=${invoice.id}`),
        fetch(`${API_BASE}/api/tuition-invoices/${invoice.id}/history`),
        fetch(soaUrl),
        fetch(`${API_BASE}/api/employees?all=true`),
        fetch(acUrl),
      ]);
      if (!pRes.ok) throw new Error(`Load payments failed (${pRes.status})`);
      if (!aRes.ok) throw new Error(`Load adjustments failed (${aRes.status})`);
      const [p, a] = await Promise.all([pRes.json(), aRes.json()]);
      setPayments(p);
      setAdjustments(a);
      // SoA failure is non-fatal — the Activity Log degrades gracefully to
      // the pre-coverage layout (amount / method / ref# / notes only).
      if (sRes.ok) {
        const soa = await sRes.json();
        setCoverage(soa?.paymentCoverage || {});
      } else {
        setCoverage({});
      }
      // Employees failure also non-fatal — we fall back to "Staff #123" if
      // the lookup didn't load.
      if (eRes.ok) {
        const emp = await eRes.json();
        const map = {};
        (Array.isArray(emp) ? emp : []).forEach(e => { map[e.id] = e; });
        setEmployeeById(map);
      } else {
        setEmployeeById({});
      }
      // Additional charges — non-fatal if the call fails. Cashier still
      // sees tuition history; charges section just renders empty.
      if (acRes.ok) {
        const charges = await acRes.json();
        setAdditionalCharges(Array.isArray(charges) ? charges : []);
      } else {
        setAdditionalCharges([]);
      }
      // Pending deletion requests THIS user has open for any payment in
      // this invoice. Filtered by username so two cashiers don't see each
      // other's queue. Skipped for admins (their flow is direct-delete).
      if (canRequestDeletion && currentUsername) {
        const drUrl = `${API_BASE}/api/payment-deletion-requests`
                    + `?status=PENDING&requestedBy=${encodeURIComponent(currentUsername)}`;
        try {
          const drRes = await fetch(drUrl);
          if (drRes.ok) {
            const list = await drRes.json();
            const map = {};
            (Array.isArray(list) ? list : []).forEach(r => { map[r.paymentId] = r; });
            setPendingByPaymentId(map);
          } else { setPendingByPaymentId({}); }
        } catch { setPendingByPaymentId({}); }
      } else {
        setPendingByPaymentId({});
      }

      // Pending EDIT requests against any payment in this invoice.
      //  - Approvers (Owner / SuperAdmin) see ALL pending edits so they
      //    can review them inline from the Activity Log.
      //  - Cashier roles see only their own (matches deletion-request
      //    behavior — keeps queues isolated per requester).
      //  - Admin sees nothing here because Admin edits directly.
      try {
        let url = null;
        if (canApproveEdits) {
          url = `${API_BASE}/api/payment-edit-requests?status=PENDING`;
        } else if (canRequestEdit && currentUsername) {
          url = `${API_BASE}/api/payment-edit-requests`
              + `?status=PENDING&requestedBy=${encodeURIComponent(currentUsername)}`;
        }
        if (url) {
          const erRes = await fetch(url);
          if (erRes.ok) {
            const list = await erRes.json();
            // Keep only requests targeting payments in THIS invoice's history,
            // so an approver loading "Galo, Ken" doesn't see edits queued
            // against other students.
            const myPaymentIds = new Set(p.map(x => x.id));
            const map = {};
            (Array.isArray(list) ? list : []).forEach(r => {
              if (myPaymentIds.has(r.paymentId)) map[r.paymentId] = r;
            });
            setPendingEditByPaymentId(map);
          } else { setPendingEditByPaymentId({}); }
        } else {
          setPendingEditByPaymentId({});
        }
      } catch { setPendingEditByPaymentId({}); }
    } catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }

  /** Open the "Request delete" modal for one payment row. */
  function openDeletionRequest(payment) {
    setRequestTarget(payment);
    setRequestReason("");
    setRequestErr("");
  }
  function closeDeletionRequest() {
    setRequestTarget(null);
    setRequestReason("");
    setRequestBusy(false);
    setRequestErr("");
  }
  async function submitDeletionRequest() {
    if (!requestTarget) return;
    if (!requestReason.trim()) {
      setRequestErr("Please describe why this payment should be deleted.");
      return;
    }
    setRequestBusy(true); setRequestErr("");
    try {
      const res = await fetch(`${API_BASE}/api/payment-deletion-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: requestTarget.id,
          reason:    requestReason.trim(),
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
      await loadAll();      // refresh so the row flips to "Pending approval"
      closeDeletionRequest();
    } catch (e) { setRequestErr(e.message); }
    finally { setRequestBusy(false); }
  }

  /** Resolve a staff id into a "Name · Role" display label, or null when
   *  the id is missing / unknown. Used for both payments (receivedBy…) and
   *  adjustments (changedBy…). */
  function actorLabel(staffId) {
    if (!staffId) return null;
    const e = employeeById[staffId];
    if (!e) return `Staff #${staffId}`;
    const bits = [e.name || `Staff #${staffId}`];
    if (e.role) bits.push(e.role);
    return bits.join(" · ");
  }

  /** Build the "by …" line for a payment row. Picks the most informative
   *  single label so the audit trail is readable but unambiguous:
   *    - Linked profile available  → "Maria Santos · Cashier"
   *      (the human name is identification enough; appending the username
   *       just clutters the row).
   *    - No profile + SuperAdmin   → "Super Admin"
   *      (one system account by convention; the username is usually just
   *       "admin" and adds no information).
   *    - No profile + other role   → "jdoe · Admin"
   *      (username preserved so two Admins / Cashiers stay distinguishable
   *       in the audit trail).
   *    - Neither stamp recorded    → null (line hidden — legacy rows). */
  function paymentActorLine(p) {
    const profile = actorLabel(p.receivedByStaffId);
    if (profile) return profile;
    const role = p.recordedByRole || null;
    const user = p.recordedByUsername || null;
    if (!role && !user) return null;
    if (role === "SuperAdmin") return "Super Admin";
    if (user && role) return `${user} · ${role}`;
    return user || role;
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [invoice.id]);

  async function handleDelete(payment) {
    if (!window.confirm("Delete this payment? This will also reverse its effect on the invoice status.")) return;
    setDeleting(true); setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/api/tuition-payments/${payment.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await loadAll();
      if (onChanged) onChanged();
    } catch (e) { setLoadError(e.message); }
    finally { setDeleting(false); }
  }

  /** Save the edits made in EditPaymentModal. Role-aware:
   *
   *   • Admin / SuperAdmin → direct PUT, applied immediately.
   *   • Cashier / CashierStaff → POST a PaymentEditRequest with the
   *     proposed changes + reason. The live payment row stays unchanged
   *     until Owner / SuperAdmin approves.
   *
   * `patch` shape is the same in both cases; only the URL/body differ.
   * Role/username headers are injected by the global fetch wrapper in
   * index.js, so we only need Content-Type for the JSON body. */
  async function handleEditSave(patch) {
    if (!editingPayment) return;
    setEditBusy(true); setEditError("");
    try {
      if (canRequestEdit) {
        // Cashier flow → goes into the approval queue.
        const body = {
          paymentId:             editingPayment.id,
          reason:                patch.reason,
          newAmount:             patch.amount,
          newMethod:             patch.method,
          newOrNumber:           patch.orNumber,
          newReferenceNo:        patch.referenceNo,
          newBankName:           patch.bankName,
          newPaymentReceivedOn:  patch.paymentReceivedOn,
          newNotes:              patch.notes,
          newAppliesToMonth:     patch.appliesToMonth,
        };
        const res = await fetch(`${API_BASE}/api/payment-edit-requests`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          throw new Error(await extractErrorMessage(res, `Request failed (${res.status})`));
        }
      } else {
        // Admin flow → direct PUT, applied immediately.
        const body = {
          amount:             patch.amount,
          method:             patch.method,
          orNumber:           patch.orNumber,
          referenceNo:        patch.referenceNo,
          bankName:           patch.bankName,
          paymentReceivedOn:  patch.paymentReceivedOn,
          notes:              patch.notes,
          appliesToMonth:     patch.appliesToMonth,
        };
        const res = await fetch(`${API_BASE}/api/tuition-payments/${editingPayment.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
        }
      }
      setEditingPayment(null);
      await loadAll();
      if (onChanged) onChanged();
    } catch (e) {
      setEditError(e.message || (canRequestEdit ? "Request failed." : "Save failed."));
    } finally {
      setEditBusy(false);
    }
  }

  /** Approve a pending edit request inline from the Activity Log. Owner /
   *  SuperAdmin only; the backend re-checks the gate. */
  async function approveEditRequest(reqRow) {
    if (!reqRow?.id || reviewingId) return;
    setReviewingId(reqRow.id); setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/api/payment-edit-requests/${reqRow.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({}),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Approve failed (${res.status})`));
      }
      await loadAll();
      if (onChanged) onChanged();
    } catch (e) {
      setLoadError(e.message || "Approve failed.");
    } finally {
      setReviewingId(null);
    }
  }

  /** Open the styled RejectReasonDialog for a pending edit request. The
   *  actual POST happens in confirmRejectEdit once the approver fills in
   *  a reason. Owner / SuperAdmin only (gate enforced by the row UI and
   *  re-checked server-side). */
  function rejectEditRequest(reqRow) {
    if (!reqRow?.id || reviewingId) return;
    setLoadError("");
    setRejectingEdit(reqRow);
  }

  async function confirmRejectEdit(reason) {
    if (!rejectingEdit || !reason?.trim()) return;
    const reqRow = rejectingEdit;
    setReviewingId(reqRow.id); setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/api/payment-edit-requests/${reqRow.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ reason: reason.trim() }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Reject failed (${res.status})`));
      }
      setRejectingEdit(null);
      await loadAll();
      if (onChanged) onChanged();
    } catch (e) {
      setLoadError(e.message || "Reject failed.");
    } finally {
      setReviewingId(null);
    }
  }

  /** Cashier withdraws their own pending edit request. Backend enforces
   *  that only the original requester (or Owner / SuperAdmin) can. */
  async function withdrawEditRequest(reqRow) {
    if (!reqRow?.id || reviewingId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Withdraw your edit request? The pending change won't be reviewed.")) return;
    setReviewingId(reqRow.id); setLoadError("");
    try {
      const res = await fetch(`${API_BASE}/api/payment-edit-requests/${reqRow.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Withdraw failed (${res.status})`));
      }
      await loadAll();
      if (onChanged) onChanged();
    } catch (e) {
      setLoadError(e.message || "Withdraw failed.");
    } finally {
      setReviewingId(null);
    }
  }

  const paid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const total = Number(invoice.amount) || 0;
  const remaining = total - paid;
  const overpaid = remaining < -0.005;
  const status = invoice.status || "UNPAID";
  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;

  // Each mode tracks a different stream — Payment History is the cash-flow
  // record, Invoice History is the amount-adjustment audit trail.
  const entries = [];
  if (isPayments) {
    for (const p of payments) {
      entries.push({
        kind: "payment", id: `p-${p.id}`, data: p,
        at: p.paidAt ? new Date(p.paidAt) : new Date(0),
      });
    }
  }
  if (isAdjustments) {
    for (const a of adjustments) {
      entries.push({
        kind: "adjustment", id: `a-${a.id}`, data: a,
        at: a.createdAt ? new Date(a.createdAt) : new Date(0),
      });
    }
  }
  entries.sort((x, y) => x.at - y.at);

  // Close on Escape — the backdrop no longer closes on outside click.
  // Skips closing the whole modal while the nested "Request delete" dialog
  // is open — that dialog's own Escape handler below closes it first.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !requestTarget) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, requestTarget]);

  // Same for the nested "Request payment delete" dialog.
  useEffect(() => {
    if (!requestTarget) return;
    function onKey(e) { if (e.key === "Escape" && !requestBusy) closeDeletionRequest(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestTarget, requestBusy, closeDeletionRequest]);

  return (
    <>
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalWide}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>
            📋 {isAdjustments ? "Invoice History" : "Payment History"}
          </h3>
          <button className={s.modalClose} type="button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{name}{semester?.label ? ` · ${semester.label}` : ""}</div>
          {student?.studentNumber && <div className={s.modalSubMeta}>#{student.studentNumber}</div>}
          {isAdjustments && (
            <div className={s.modalSubRow}>
              <span>Only invoice-amount changes are tracked here — payments are on the Payments tab.</span>
            </div>
          )}
        </div>

        <div className={s.historyGrid}>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>Total Payable</div>
            <div className={s.historyValue}>₱{fmtMoney(total)}</div>
          </div>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>Paid</div>
            <div className={`${s.historyValue} ${s.payStatPaid}`}>₱{fmtMoney(paid)}</div>
          </div>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>{overpaid ? "Overpaid" : "Remaining"}</div>
            <div className={`${s.historyValue} ${overpaid ? s.payStatOverpaid : (remaining > 0.005 ? s.payStatDue : s.payStatClear)}`}>
              ₱{fmtMoney(overpaid ? -remaining : remaining)}
            </div>
          </div>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>Status</div>
            <div><span className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}>{status}</span></div>
          </div>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>Due Date</div>
            <div className={s.historyValue}>{invoice.dueDate || "—"}</div>
          </div>
          <div className={s.historyCell}>
            <div className={s.historyLabel}>Created Date</div>
            <div className={s.historyValue}>
              {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>

        <div className={s.historyLogHead}>Activity Log</div>
        {loadError && <div className={s.error}>{loadError}</div>}
        {loading ? (
          <div className={s.empty}>Loading…</div>
        ) : entries.length === 0 ? (
          <div className={s.empty}>
            {isAdjustments
              ? "No adjustments yet — amount hasn't changed since the invoice was generated."
              : "No payments yet."}
          </div>
        ) : (
          <div className={s.historyList}>
            {entries.map(e => {
              if (e.kind === "payment") {
                const p = e.data;
                const amt = Number(p.amount) || 0;
                const isNegative = amt < 0;
                const covers = coverage[p.id] || [];
                const actor = paymentActorLine(p);
                {/* Two-tier date display:
                    · Top line: the ACTUAL receipt date (Received: …) —
                      prominent. Falls back to paidAt's date portion
                      for legacy rows with a null paymentReceivedOn.
                    · Bottom italic footnote: paidAt — when the record
                      was keyed into the system. Same idea as the
                      receipt modal so the two surfaces match. */}
                const receivedOn = p.paymentReceivedOn
                  || (p.paidAt
                      ? new Date(p.paidAt).toISOString().slice(0, 10)
                      : null);
                const receivedLabel = receivedOn
                  ? new Date(receivedOn + "T12:00:00").toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                    })
                  : "—";
                return (
                  <div key={e.id} className={s.historyItem}>
                    <div className={`${s.historyDot} ${isNegative ? s.historyDotReversal : ""}`} />
                    <div className={s.historyBody}>
                      <div className={s.historyItemTop}>
                        <span className={`${s.historyAmount} ${isNegative ? s.historyAmountReversal : ""}`}>
                          {isNegative ? "−" : ""}₱{fmtMoney(Math.abs(amt))}
                        </span>
                        <span className={s.historyWhen}
                              title="Date the school actually received this payment">
                          <span style={{ color: "#8a6d28", fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.4px", textTransform: "uppercase", marginRight: 6 }}>
                            Received
                          </span>
                          {receivedLabel}
                        </span>
                      </div>
                      <div className={s.historyItemMeta}>
                        {p.method && <span className={s.methodBadge}>{p.method}</span>}
                        {p.bankName && <span>{p.bankName}</span>}
                        {p.orNumber && <span><b>OR</b> {p.orNumber}</span>}
                        {p.referenceNo && <span>Ref {p.referenceNo}</span>}
                      </div>
                      {actor && (
                        <div className={s.historyItemMeta} style={{ color: "#6b5a3a" }}>
                          <span>by {actor}</span>
                        </div>
                      )}
                      {/* Muted system-entry footnote — when this row was
                          keyed into the system. Italic + smaller +
                          de-saturated so the eye lands on Received above. */}
                      <div style={{
                        marginTop: 4, fontSize: "0.72rem", fontStyle: "italic",
                        color: "#9c8b6a", letterSpacing: "0.1px",
                      }}>
                        Entered: {p.paidAt ? new Date(p.paidAt).toLocaleString() : "—"}
                      </div>
                      {covers.length > 0 && (
                        <div className={s.historyNote} style={{ background: "transparent", borderLeft: "3px solid #c9a227", paddingLeft: 8, color: "#5a4e3a" }}>
                          <span style={{ fontWeight: 700, marginRight: 6 }}>Covers:</span>
                          {covers.map((c, i) => (
                            <span key={i}>
                              {i > 0 && " + "}
                              {c.label}
                              {" "}(₱{fmtMoney(Math.abs(Number(c.amount) || 0))}){c.overpay ? " overpay" : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {p.notes && <div className={s.historyNote}>{p.notes}</div>}
                    </div>
                    {/* Edit affordances — coupled to whether this payment
                        already has a pending edit request. While one is
                        pending we lock direct edits for everyone (single
                        source of truth) and surface the right action for
                        the current viewer. */}
                    {(() => {
                      const pendingEdit = pendingEditByPaymentId[p.id];
                      if (pendingEdit) {
                        const isMine = pendingEdit.requestedByUsername === currentUsername;
                        const reviewing = reviewingId === pendingEdit.id;
                        return (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                            <span title={`Edit requested by ${pendingEdit.requestedByUsername || "unknown"}`}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    padding: "4px 10px", fontSize: "0.75rem",
                                    background: "#fff8e1", color: "#8a6a1c",
                                    border: "1px solid #efe1a8", borderRadius: 6,
                                    fontWeight: 700,
                                  }}>
                              ⏳ Edit pending
                            </span>
                            {canApproveEdits && (
                              <span style={{ display: "inline-flex", gap: 6 }}>
                                <button type="button"
                                        onClick={() => approveEditRequest(pendingEdit)}
                                        disabled={reviewing}
                                        style={{
                                          padding: "4px 10px", fontSize: "0.78rem",
                                          background: "#1e6f3e", color: "#fff",
                                          border: "1px solid #1e6f3e", borderRadius: 6,
                                          cursor: reviewing ? "not-allowed" : "pointer",
                                          fontWeight: 700, opacity: reviewing ? 0.6 : 1,
                                        }}>
                                  {reviewing ? "Approving…" : "Approve"}
                                </button>
                                <button type="button"
                                        onClick={() => rejectEditRequest(pendingEdit)}
                                        disabled={reviewing}
                                        style={{
                                          padding: "4px 10px", fontSize: "0.78rem",
                                          background: "#fff", color: "#c0392b",
                                          border: "1px solid #e6b8b4", borderRadius: 6,
                                          cursor: reviewing ? "not-allowed" : "pointer",
                                          fontWeight: 600, opacity: reviewing ? 0.6 : 1,
                                        }}>
                                  Reject
                                </button>
                              </span>
                            )}
                            {isMine && !canApproveEdits && (
                              <button type="button"
                                      onClick={() => withdrawEditRequest(pendingEdit)}
                                      disabled={reviewing}
                                      style={{
                                        padding: "4px 10px", fontSize: "0.78rem",
                                        background: "#fff", color: "#6b3a30",
                                        border: "1px solid #d6c8a8", borderRadius: 6,
                                        cursor: reviewing ? "not-allowed" : "pointer",
                                        fontWeight: 600, opacity: reviewing ? 0.6 : 1,
                                      }}>
                                {reviewing ? "Withdrawing…" : "Withdraw"}
                              </button>
                            )}
                          </div>
                        );
                      }
                      // No pending edit — show normal Edit/Delete (admin) or
                      // Request edit / Request delete (cashier). All actions
                      // wrap in one container so they occupy a single grid
                      // cell (col 3 of .historyItem's `24px 1fr auto` grid).
                      // Without this wrapper a fragment's children spill
                      // into the next row's cells.
                      return (
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 6,
                          flexShrink: 0,
                        }}>
                          {/* Receipt — primary courtesy action. Shown
                              for every payment row regardless of role
                              so a cashier can re-print, or any future
                              parent-facing surface can offer the same
                              affordance. Negative-amount rows are
                              reversal entries (refunds), not real
                              receipts; hide the button there to avoid
                              issuing an "OR" for money going back out. */}
                          {!isNegative && (
                            <button type="button"
                                    onClick={() => setPaymentReceiptTarget({
                                      payment: p,
                                      coverage: covers,
                                    })}
                                    title="Open this payment's official receipt"
                                    aria-label={`Open receipt for ₱${fmtMoney(amt)} ref ${p.referenceNo || ""}`}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 5,
                                      padding: "4px 10px", fontSize: "0.78rem",
                                      background: "linear-gradient(180deg,#fffaef 0%,#fbf2dc 100%)",
                                      color: "#6b3a30",
                                      border: "1px solid #d6c89e",
                                      borderRadius: 6,
                                      cursor: "pointer",
                                      fontWeight: 700,
                                      letterSpacing: 0.2,
                                      whiteSpace: "nowrap",
                                      boxShadow: "0 1px 2px rgba(107,58,48,0.08)",
                                      transition: "background 140ms ease, "
                                                + "border-color 140ms ease, "
                                                + "color 140ms ease, "
                                                + "transform 140ms cubic-bezier(.2,.8,.2,1.05), "
                                                + "box-shadow 140ms ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = "linear-gradient(180deg,#fff5d6 0%,#f5e6c8 100%)";
                                      e.currentTarget.style.borderColor = "#c9a227";
                                      e.currentTarget.style.color = "#4d2820";
                                      e.currentTarget.style.transform = "translateY(-1px)";
                                      e.currentTarget.style.boxShadow = "0 3px 8px rgba(201,162,39,0.25)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "linear-gradient(180deg,#fffaef 0%,#fbf2dc 100%)";
                                      e.currentTarget.style.borderColor = "#d6c89e";
                                      e.currentTarget.style.color = "#6b3a30";
                                      e.currentTarget.style.transform = "";
                                      e.currentTarget.style.boxShadow = "0 1px 2px rgba(107,58,48,0.08)";
                                    }}>
                              <span aria-hidden>🧾</span>
                              <span>Receipt</span>
                            </button>
                          )}
                          {canDeletePayments && (
                            <button type="button" className={s.btnGhost}
                                    disabled={deleting || editBusy}
                                    onClick={() => { setEditError(""); setEditingPayment(p); }}
                                    title="Edit amount, allocation, method, reference, or notes">
                              Edit
                            </button>
                          )}
                          {canRequestEdit && (
                            <button type="button"
                                    disabled={editBusy}
                                    onClick={() => { setEditError(""); setEditingPayment(p); }}
                                    style={{
                                      padding: "4px 10px", fontSize: "0.78rem",
                                      background: "#fff", color: "#6b3a30",
                                      border: "1px solid #d6c8a8", borderRadius: 6,
                                      cursor: editBusy ? "not-allowed" : "pointer",
                                      fontWeight: 600, opacity: editBusy ? 0.6 : 1,
                                      whiteSpace: "nowrap",
                                    }}
                                    title="Propose changes; an Owner / SuperAdmin will review">
                              Request edit
                            </button>
                          )}
                          {canDeletePayments && (
                            <button type="button" className={s.btnDanger}
                                    disabled={deleting} onClick={() => handleDelete(p)}>
                              Delete
                            </button>
                          )}
                          {canRequestDeletion && (
                            pendingByPaymentId[p.id] ? (
                              <span title="An admin is reviewing your delete request"
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 4,
                                      padding: "4px 10px", fontSize: "0.75rem",
                                      background: "#fff8e1", color: "#8a6a1c",
                                      border: "1px solid #efe1a8", borderRadius: 6,
                                      fontWeight: 700,
                                    }}>
                                ⏳ Delete pending
                              </span>
                            ) : (
                              <button type="button"
                                      onClick={() => openDeletionRequest(p)}
                                      style={{
                                        padding: "4px 10px", fontSize: "0.78rem",
                                        background: "#fff", color: "#c0392b",
                                        border: "1px solid #e6b8b4", borderRadius: 6,
                                        cursor: "pointer", fontWeight: 600,
                                      }}>
                                Request delete
                              </button>
                            )
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              }
              // adjustment
              const a = e.data;
              const diff = Number(a.newAmount) - Number(a.oldAmount);
              const actor = actorLabel(a.changedByStaffId);
              return (
                <div key={e.id} className={s.historyItem}>
                  <div className={`${s.historyDot} ${s.historyDotAdjust}`} />
                  <div className={s.historyBody}>
                    <div className={s.historyItemTop}>
                      <span className={s.historyAdjustLabel}>
                        ✎ Recomputed
                        <span className={s.historyDelta}>
                          {" "}₱{fmtMoney(a.oldAmount)} → ₱{fmtMoney(a.newAmount)}
                          {" "}({diff > 0 ? "+" : "−"}₱{fmtMoney(Math.abs(diff))})
                        </span>
                      </span>
                      <span className={s.historyWhen}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleString() : "—"}
                      </span>
                    </div>
                    {actor && (
                      <div className={s.historyItemMeta} style={{ color: "#6b5a3a" }}>
                        <span>by {actor}</span>
                      </div>
                    )}
                    {a.reason && <div className={s.historyNote}>{a.reason}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Additional charges — surfaces non-tuition fees (summer class,
            special curriculum, etc.) so the cashier sees the COMPLETE
            picture in one modal. Hidden entirely when the student has no
            charges so existing data renders identically. Read-only here:
            adds / payments are still recorded via the dedicated +-icon
            in the Payments tab; this is just a visibility surface. */}
        {isPayments && additionalCharges.length > 0 && (() => {
          const totalAmt  = additionalCharges.reduce((s2, c) => s2 + (Number(c.amount)     || 0), 0);
          const totalPaid = additionalCharges.reduce((s2, c) => s2 + (Number(c.paidAmount) || 0), 0);
          const totalRem  = totalAmt - totalPaid;
          const pillFor = status => {
            const map = {
              UNPAID:  { bg: "#fdecea", fg: "#a8281c", border: "#e6b8b4", label: "Unpaid"  },
              PARTIAL: { bg: "#fff8e1", fg: "#8a6a1c", border: "#efe1a8", label: "Partial" },
              PAID:    { bg: "#e3f2e6", fg: "#1f6a2d", border: "#bcdcc4", label: "Paid"    },
            };
            return map[status] || map.UNPAID;
          };
          return (
            <>
              <div className={s.historyLogHead} style={{ marginTop: 18 }}>
                Additional Charges
                <span style={{
                  marginLeft: 10, fontSize: "0.74rem",
                  fontWeight: 500, color: "#8a8070", letterSpacing: 0,
                  textTransform: "none",
                }}>
                  Outside the regular tuition · {additionalCharges.length}
                  {" "}charge{additionalCharges.length === 1 ? "" : "s"} on file
                </span>
              </div>
              <div style={{
                display: "grid", gap: 8, marginTop: 8,
              }}>
                {additionalCharges.map(c => {
                  const remaining = Math.max(
                    Number(c.amount || 0) - Number(c.paidAmount || 0), 0);
                  const p = pillFor(c.status);
                  return (
                    <div key={c.id} style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 12,
                      padding: "10px 12px",
                      background: "#fff",
                      border: "1px solid #f0ebe4",
                      borderLeft: `3px solid ${
                        c.status === "PAID"    ? "#1f6a2d"
                      : c.status === "PARTIAL" ? "#c9a227"
                      :                          "#c0392b"
                      }`,
                      borderRadius: 10,
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center",
                                      gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, color: "#1a1a1a",
                                          wordBreak: "break-word",
                                          display: "inline-flex", alignItems: "center" }}>
                            {c.label}
                            <ChargeInfoButton
                              onClick={() => setChargeHistoryTarget(c)}
                              label={`View payments for ${c.label}`}
                            />
                          </span>
                          <span style={{
                            fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.4,
                            textTransform: "uppercase",
                            padding: "2px 8px", borderRadius: 999,
                            background: p.bg, color: p.fg,
                            border: `1px solid ${p.border}`,
                          }}>{p.label}</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap",
                                      gap: "2px 12px", marginTop: 4,
                                      fontSize: "0.78rem", color: "#6b5a3a" }}>
                          <span>
                            <b style={{ color: "#1a1a1a", fontVariantNumeric: "tabular-nums" }}>
                              ₱{fmtMoney(c.amount)}
                            </b>
                            <span style={{ color: "#8a8070" }}> amount</span>
                          </span>
                          {Number(c.paidAmount) > 0.005 && (
                            <span>
                              <b style={{ color: "#1f6a2d", fontVariantNumeric: "tabular-nums" }}>
                                ₱{fmtMoney(c.paidAmount)}
                              </b>
                              <span style={{ color: "#8a8070" }}> paid</span>
                            </span>
                          )}
                          {remaining > 0.005 && (
                            <span>
                              <b style={{ color: "#a8281c", fontVariantNumeric: "tabular-nums" }}>
                                ₱{fmtMoney(remaining)}
                              </b>
                              <span style={{ color: "#8a8070" }}> remaining</span>
                            </span>
                          )}
                          {c.method && (
                            <span style={{
                              fontSize: "0.64rem", fontWeight: 800,
                              letterSpacing: 0.5, textTransform: "uppercase",
                              padding: "1px 7px", borderRadius: 4,
                              background: "#e8dfc9", color: "#5a4e3a",
                              border: "1px solid #d6cdb8",
                            }}>
                              {c.method}
                            </span>
                          )}
                          {c.referenceNo && (
                            <span style={{ fontFamily: "ui-monospace, monospace",
                                            color: "#4a3d2a" }}>
                              Ref {c.referenceNo}
                            </span>
                          )}
                          {c.paidAt && (
                            <span style={{ color: "#8a8070" }}>
                              · Paid {new Date(c.paidAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto",
                  gap: 10, alignItems: "center",
                  padding: "10px 12px",
                  background: "#faf6ee",
                  border: "1px solid #efe7d6",
                  borderRadius: 10,
                  fontSize: "0.82rem",
                }}>
                  <span style={{ fontWeight: 800, color: "#6b5a3a",
                                  textTransform: "uppercase", letterSpacing: 0.5,
                                  fontSize: "0.7rem" }}>
                    Subtotal
                  </span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: "#8a8070", marginRight: 4 }}>Amount</span>
                    <b style={{ color: "#1a1a1a" }}>₱{fmtMoney(totalAmt)}</b>
                  </span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: "#8a8070", marginRight: 4 }}>Paid</span>
                    <b style={{ color: "#1f6a2d" }}>₱{fmtMoney(totalPaid)}</b>
                  </span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    <span style={{ color: "#8a8070", marginRight: 4 }}>Remaining</span>
                    <b style={{ color: totalRem > 0.005 ? "#a8281c" : "#1f6a2d" }}>
                      ₱{fmtMoney(Math.max(totalRem, 0))}
                    </b>
                  </span>
                </div>
              </div>
            </>
          );
        })()}

        <div className={s.modalActions}>
          <button type="button" className={s.btnGhost} onClick={onClose}>Close</button>
          {/* View full SOA — opens the complete Statement of Account
              for this student + school year. Navigates in the SAME
              tab via react-router so the SPA session (sessionStorage
              auth) is preserved; opening in a new tab would land on
              /login because sessionStorage is per-tab and the new
              tab starts unauthenticated. The SOA page honours both
              ?studentId= and ?schoolYearId= query params so it lands
              pre-filtered. */}
          {isPayments && (
            <button type="button"
                    onClick={() => {
                      // Close the modal first so the body scroll lock
                      // it set is released before the route swap; the
                      // SOA page would otherwise inherit a frozen body.
                      onClose?.();
                      navigate(`/tuition/soa?studentId=${invoice.studentId}`
                             + `&schoolYearId=${invoice.schoolYearId}`);
                    }}
                    title="Open the full Statement of Account"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 14px",
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "#1a4a6b",
                      background: "#fff",
                      border: "1.5px solid #1a4a6b",
                      borderRadius: 8,
                      cursor: "pointer",
                      letterSpacing: 0.2,
                      transition: "background 140ms ease, color 140ms ease, transform 140ms ease, box-shadow 140ms ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#1a4a6b";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.boxShadow = "0 3px 8px rgba(26,74,107,0.20)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                      e.currentTarget.style.color = "#1a4a6b";
                      e.currentTarget.style.boxShadow = "";
                      e.currentTarget.style.transform = "";
                    }}>
              <span aria-hidden>📄</span>
              <span>View full SOA</span>
              <span aria-hidden style={{ fontSize: "0.85rem", opacity: 0.7 }}>→</span>
            </button>
          )}
          {/* Record Payment only makes sense on the Payment History modal —
              the Invoice History modal is a read-only audit trail. */}
          {onPay && isPayments && (
            <button type="button" className={s.btnPrimary} onClick={onPay}>+ Record Payment</button>
          )}
        </div>

        {requestTarget && (
          <div className={s.modalOverlay}
               style={{ zIndex: 70 }}>
            <div className={s.modal}
                 style={{ maxWidth: 460 }}
                 onClick={e => e.stopPropagation()}>
              <div className={s.modalHead}>
                <h3 className={s.modalTitle}>Request payment delete</h3>
                <button className={s.modalClose} type="button"
                        onClick={closeDeletionRequest} disabled={requestBusy}
                        aria-label="Close">×</button>
              </div>
              <div style={{ padding: "4px 2px", display: "grid", gap: 12 }}>
                <div style={{ background: "#faf6ee", borderRadius: 8,
                              padding: "10px 12px", border: "1px solid #efe7d6" }}>
                  <div style={{ fontWeight: 800, color: "#1f6a2d" }}>
                    ₱{fmtMoney(requestTarget.amount)}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "#6b5a3a", marginTop: 2 }}>
                    {requestTarget.method || "—"}
                    {requestTarget.referenceNo ? ` · Ref ${requestTarget.referenceNo}` : ""}
                    {requestTarget.paidAt ? ` · ${new Date(requestTarget.paidAt).toLocaleString()}` : ""}
                  </div>
                </div>
                <label style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#3d342a" }}>
                    Reason for delete *
                  </span>
                  <textarea
                    rows={4}
                    value={requestReason}
                    onChange={e => setRequestReason(e.target.value)}
                    placeholder='e.g. "Wrong amount, parent paid 4,500 not 5,000"'
                    autoFocus
                    style={{
                      padding: 8, fontSize: "0.85rem",
                      border: "1px solid #d6cdb8", borderRadius: 6,
                      fontFamily: "inherit", resize: "vertical",
                    }}
                  />
                  <span style={{ fontSize: "0.72rem", color: "#8a8070" }}>
                    An admin will see this and decide whether to approve or reject.
                    The payment stays in place until then.
                  </span>
                </label>
                {requestErr && (
                  <div style={{ fontSize: "0.78rem", color: "#c0392b" }}>
                    {requestErr}
                  </div>
                )}
              </div>
              <div className={s.modalActions}>
                <button type="button" className={s.btnGhost}
                        onClick={closeDeletionRequest} disabled={requestBusy}>
                  Cancel
                </button>
                <button type="button" className={s.btnPrimary}
                        onClick={submitDeletionRequest} disabled={requestBusy}>
                  {requestBusy ? "Sending…" : "Send request"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    {editingPayment && (
      <EditPaymentModal
        payment={editingPayment}
        invoice={invoice}
        student={student}
        semester={semester}
        mode={canRequestEdit ? "request" : "direct"}
        onClose={() => { if (!editBusy) { setEditingPayment(null); setEditError(""); } }}
        onSave={handleEditSave}
        busy={editBusy}
        error={editError}
      />
    )}
    {/* Styled reject dialog — replaces the old window.prompt for edit
        request rejections so the approver gets a textarea, focus
        management, and a Cmd/Ctrl+Enter shortcut. Reason is required. */}
    <RejectReasonDialog
      open={!!rejectingEdit}
      title="Reject edit request"
      description={rejectingEdit
        ? `Requested by ${rejectingEdit.requestedByUsername || "unknown"} · ₱${fmtMoney(rejectingEdit.payment?.amount ?? rejectingEdit.snapshot?.amount)}`
        : ""}
      placeholder='e.g. "Amount differs from the receipt — please verify and re-submit."'
      busy={reviewingId === rejectingEdit?.id}
      onConfirm={confirmRejectEdit}
      onCancel={() => { if (reviewingId !== rejectingEdit?.id) setRejectingEdit(null); }}
    />

    {chargeHistoryTarget && (
      <ChargePaymentsModal
        charge={chargeHistoryTarget}
        student={student}
        onClose={() => setChargeHistoryTarget(null)}
      />
    )}

    {/* Per-payment receipt overlay — fired from a row in Activity Log.
        Wraps the tuition payment with a synthetic subject so the
        receipt renders the same shape it does for additional-charge
        receipts. Coverage map is sourced from the SoA's per-payment
        allocation map already loaded by this modal. */}
    {paymentReceiptTarget && (
      <PaymentReceiptModal
        payment={paymentReceiptTarget.payment}
        subject={{
          label: "Tuition fee",
          sub: semester?.label || "Tuition payment",
        }}
        student={student}
        coverage={paymentReceiptTarget.coverage}
        onClose={() => setPaymentReceiptTarget(null)}
      />
    )}
    </>
  );
}

/* Modal: record a refund against an overpaid invoice. Stored as a negative
 * payment row so it shares the invoice history timeline. Amount is
 * pre-filled with the current overpaid delta so the default "Confirm" zeroes
 * the balance — admins can override if the refund is partial. */
function RefundModal({ invoice, student, semester, overpaid, onClose, onSubmit, busy, error }) {
  const defaultAmount = overpaid > 0 ? overpaid.toFixed(2) : "";
  const [amount, setAmount]           = useState(defaultAmount);
  const [method, setMethod]           = useState("CASH");
  const [referenceNo, setReferenceNo] = useState("");
  const [bankName, setBankName]       = useState("");
  const [notes, setNotes]             = useState("Refund (overpayment)");
  const [bankList, setBankList]       = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadBankList().then(list => { if (!cancelled) setBankList(list); });
    return () => { cancelled = true; };
  }, []);

  function handleSubmit(e) {
    e.preventDefault();
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    // Reference # is required for every refund — every transaction needs
    // a paper trail, regardless of method. Backend also rejects blanks.
    if (!referenceNo.trim()) return;
    onSubmit({
      invoiceId:   invoice.id,
      amount:      parsed,
      method,
      referenceNo: referenceNo.trim(),
      bankName:    method === "BANK" ? (bankName.trim() || null) : null,
      notes:       notes.trim() || "Refund",
    });
  }

  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Issue Refund</h3>
          <button className={s.modalClose} type="button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={`${s.modalSub} ${s.modalSubRefund}`}>
          <div className={s.modalSubName}>{name}</div>
          <div className={s.modalSubMeta}>
            {student?.studentNumber && <span>#{student.studentNumber}</span>}
            {semester?.label && <span>· {semester.label}</span>}
          </div>
          <div className={s.modalSubRow}>
            <span>Invoice total: <b>₱{fmtMoney(invoice.amount)}</b></span>
            <span>Overpaid by: <b className={s.payStatOverpaid}>₱{fmtMoney(overpaid)}</b></span>
          </div>
        </div>
        <form onSubmit={handleSubmit} className={s.modalForm}>
          {error && <div className={s.error}>{error}</div>}
          <div className={s.field}>
            <label className={s.label}>Refund Amount (₱) <span className={s.req}>*</span></label>
            <input
              className={s.input}
              type="number"
              step="0.01"
              min="0.01"
              autoFocus
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
            />
            <div className={s.fieldHint}>
              Default is the full overpayment (₱{fmtMoney(overpaid)}). Stored as a negative
              payment so it appears in the invoice history alongside receipts.
            </div>
          </div>
          <div className={s.field}>
            <label className={s.label}>Method</label>
            <select className={s.input} value={method} onChange={e => setMethod(e.target.value)}>
              <option value="CASH">Cash</option>
              <option value="BANK">Bank transfer</option>
              <option value="GCASH">GCash</option>
              <option value="CARD">Card</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          {method === "BANK" && (
            <div className={s.field}>
              <label className={s.label}>Bank name</label>
              <select
                className={s.input}
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                disabled={bankList.length === 0}>
                <option value="">
                  {bankList.length === 0 ? "— no banks configured —" : "— select a bank —"}
                </option>
                {bankList.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {bankList.length === 0 && (
                <div className={s.fieldHint}>
                  Ask an admin to add banks in Settings → Tuition.
                </div>
              )}
            </div>
          )}
          <div className={s.field}>
            <label className={s.label}>
              Reference # <span className={s.req}>*</span>
            </label>
            <input
              className={s.input}
              type="text"
              value={referenceNo}
              onChange={e => setReferenceNo(e.target.value)}
              placeholder={
                method === "CASH"  ? "OR # / receipt #"
              : method === "BANK"  ? "deposit slip #"
              : method === "GCASH" ? "GCash reference #"
              : method === "CARD"  ? "approval / auth #"
              :                      "reference number"
              }
              maxLength={64}
              required
            />
          </div>
          <div className={s.field}>
            <label className={s.label}>Reason / Notes</label>
            <textarea
              className={`${s.input} ${s.textarea}`}
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Refund after discount adjustment"
            />
          </div>
          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost} onClick={onClose}>Cancel</button>
            <button type="submit" className={s.btnRefund}
                    disabled={busy || !referenceNo.trim()}>
              {busy ? "Processing…" : "− Confirm Refund"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Modal: edit a recorded payment's allocation, amount, method, reference
 * number, bank name, and/or notes. Role-aware:
 *
 *  - Admin / SuperAdmin → direct PUT, no approval needed.
 *  - Cashier / CashierStaff → submits a PaymentEditRequest with a reason;
 *    Owner or SuperAdmin must approve before the live payment row changes.
 *
 * The form is the same in both modes — only the submit semantics differ.
 * `mode` = "direct" (admin) or "request" (cashier).
 *
 * Self-fetches the same SOA target list the PayModal uses so the cashier
 * sees "September (₱222.26 remaining)" not an opaque "2", and the bank
 * list from Settings → Tuition so BANK transfers stay constrained.
 */
function EditPaymentModal({ payment, invoice, student, semester, mode, onClose, onSave, busy, error }) {
  const isRequest = mode === "request";

  // No more Auto/FIFO — a legacy row with no recorded month falls back to
  // enrollment (slot 0), matching what the backend now does for it too.
  const initialApplies =
    payment.appliesToMonth == null ? "0" : String(payment.appliesToMonth);
  const [appliesTo,        setAppliesTo]        = useState(initialApplies);
  const [amount,           setAmount]           = useState(
    payment.amount == null ? "" : String(payment.amount));
  const [method,           setMethod]           = useState(payment.method || "CASH");
  const [orNumber,         setOrNumber]         = useState(payment.orNumber || "");
  const [referenceNo,      setReferenceNo]      = useState(payment.referenceNo || "");
  const [bankName,         setBankName]         = useState(payment.bankName    || "");
  // Actual receipt date for this row. Falls back to paidAt's date portion
  // for legacy rows so the picker opens on a sensible day instead of an
  // empty / "1970" state. An empty value submits as null.
  const [paymentReceivedOn, setPaymentReceivedOn] = useState(() => {
    if (payment.paymentReceivedOn) return payment.paymentReceivedOn;
    if (payment.paidAt) {
      const d = new Date(payment.paidAt);
      if (!isNaN(d)) {
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      }
    }
    return "";
  });
  const [notes,            setNotes]            = useState(payment.notes       || "");
  // Cashier's reason — required when mode === "request".
  const [reason,           setReason]           = useState("");

  // Cash payments don't carry a bank-side reference — only the school's
  // OR # is required. Anything else (BANK / GCASH / CARD / OTHER) is a
  // digital channel and the reference is the bank/wallet txn id.
  const referenceRequired = method !== "CASH";

  const [soa, setSoa]               = useState(null);
  const [soaLoading, setSoaLoading] = useState(true);
  const [bankList,   setBankList]   = useState([]);

  // SOA + bank list. Same shape PayModal pulls so the dropdown options
  // and validations stay in lockstep.
  useEffect(() => {
    let cancelled = false;
    setSoaLoading(true);
    (async () => {
      try {
        const q = `studentId=${invoice.studentId}&schoolYearId=${invoice.schoolYearId}`;
        const res = await fetch(`${API_BASE}/api/tuition/soa?${q}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSoa(data);
      } finally {
        if (!cancelled) setSoaLoading(false);
      }
    })();
    loadBankList().then(list => { if (!cancelled) setBankList(list); });
    return () => { cancelled = true; };
  }, [invoice.studentId, invoice.schoolYearId]);

  const targets = (() => {
    if (!soa) return [];
    const list = [];
    list.push({
      appliesTo: 0, label: "Enrollment fee",
      remaining: Math.max((Number(soa.enrollmentDue) || 0) - (Number(soa.enrollmentPaidSum) || 0), 0),
    });
    (soa.months_rows || []).forEach(r => {
      list.push({
        appliesTo: r.index, label: r.month,
        remaining: Math.max((Number(r.monthlyDue) || 0) - (Number(r.amount) || 0), 0),
      });
    });
    return list;
  })();

  // Detect any change vs the original — disables Save / Send when nothing
  // would actually be written. String compare on amounts so "5000" and "5000.00"
  // don't both flag as dirty just because of formatting.
  const dirty =
       String(initialApplies) !== String(appliesTo)
    || Number(payment.amount || 0).toFixed(2) !== (Number(amount || 0)).toFixed(2)
    || (payment.method            || "")    !== method
    || (payment.orNumber          || "")    !== orNumber
    || (payment.referenceNo       || "")    !== referenceNo
    || (payment.bankName          || "")    !== bankName
    || (payment.paymentReceivedOn || "")    !== paymentReceivedOn
    || (payment.notes             || "")    !== notes;

  function submit(e) {
    e.preventDefault();
    if (busy) return;
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;
    if (isRequest && !reason.trim()) return;
    // OR # is required on every row (school's audit trail). Reference #
    // is required only for non-cash methods (bank slip / GCash ref / etc.).
    if (!orNumber.trim()) return;
    if (referenceRequired && !referenceNo.trim()) return;

    const next = Number(appliesTo);
    if (!Number.isFinite(next)) return;

    onSave({
      amount:             parsedAmount,
      method,
      orNumber:           orNumber.trim(),
      // For cash, drop the reference # entirely — keeps the live row's
      // distinction between OR # and channel reference clean instead of
      // leaving a stale digital ref behind when the method was flipped.
      referenceNo:        referenceRequired ? referenceNo.trim() : null,
      // Bank name is bank-only — clear it when method flipped away.
      bankName:           method === "BANK" ? (bankName.trim() || null) : null,
      paymentReceivedOn:  paymentReceivedOn || null,
      notes:              notes.trim() || null,
      appliesToMonth:     next,
      // Only the cashier's request flow uses this; admin direct PUT ignores it.
      reason:             reason.trim() || null,
    });
  }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !busy) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return (
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>
            {isRequest ? "Request payment edit" : "Edit payment"}
          </h3>
          <button className={s.modalClose} type="button" onClick={onClose}
                  aria-label="Close" disabled={busy}>×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>
            ₱{fmtMoney(payment.amount)} · {payment.method || "—"}
          </div>
          <div className={s.modalSubMeta}>
            {payment.paidAt && <span>{new Date(payment.paidAt).toLocaleString()}</span>}
            {semester?.label && <span>· {semester.label}</span>}
            {student && <span>· {student.lastName}, {student.firstName}</span>}
          </div>
          {isRequest && (
            <div className={s.fieldHint} style={{ marginTop: 6 }}>
              Your changes won't apply until an Owner or SuperAdmin approves them.
            </div>
          )}
        </div>
        <form onSubmit={submit} className={s.modalForm}>
          {error && <div className={s.error}>{error}</div>}

          <div className={s.field}>
            <label className={s.label}>Applies to</label>
            <select className={s.input}
                    value={appliesTo}
                    onChange={e => setAppliesTo(e.target.value)}
                    disabled={soaLoading || busy}>
              {targets.map(t => (
                <option key={t.appliesTo} value={t.appliesTo}>
                  {t.label}{t.remaining > 0.005 ? ` — ₱${fmtMoney(t.remaining)} remaining` : " · settled"}
                </option>
              ))}
            </select>
          </div>

          <div className={s.field}>
            <label className={s.label}>Amount (₱) <span className={s.req}>*</span></label>
            <input
              className={s.input}
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={busy}
              required
            />
            <div className={s.fieldHint}>
              Editing the amount will recompute the invoice's Paid / Remaining
              and may change its UNPAID / PARTIAL / PAID status.
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>Method</label>
            <select className={s.input}
                    value={method}
                    onChange={e => setMethod(e.target.value)}
                    disabled={busy}>
              <option value="CASH">Cash</option>
              <option value="BANK">Bank transfer</option>
              <option value="GCASH">GCash</option>
              <option value="CARD">Card</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {method === "BANK" && (
            <div className={s.field}>
              <label className={s.label}>Bank name</label>
              <select
                className={s.input}
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                disabled={busy || bankList.length === 0}>
                <option value=""></option>
                {bankList.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {bankList.length === 0 && (
                <div className={s.fieldHint}>
                  Ask an admin to add banks in Settings → Tuition.
                </div>
              )}
            </div>
          )}

          {/* OR # — required for every payment regardless of method.
              School's own audit trail; the Reference # below is the
              bank-side / wallet-side counterpart and is only required
              for non-cash methods. */}
          <div className={s.field}>
            <label className={s.label}>
              OR # <span className={s.req}>*</span>
            </label>
            <input
              className={s.input}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={orNumber}
              onChange={e => setOrNumber(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, ""))}
              placeholder="Official Receipt number (school tracker)"
              maxLength={64}
              disabled={busy}
              required
            />
            <div className={s.fieldHint}>
              The school's Official Receipt number — digits only, no leading zeros, required for every payment.
            </div>
          </div>

          {/* Reference # — only required for non-cash methods. The field
              still renders for CASH so a cashier who has a redundant
              digital reference can capture it, but submission isn't
              blocked when it's blank. */}
          <div className={s.field}>
            <label className={s.label}>
              Reference #{" "}
              {referenceRequired
                ? <span className={s.req}>*</span>
                : <span className={s.fieldOpt}>(optional · cash)</span>}
            </label>
            <input
              className={s.input}
              type="text"
              value={referenceNo}
              onChange={e => setReferenceNo(e.target.value)}
              placeholder={
                method === "CASH"  ? "Not required for cash"
              : method === "BANK"  ? "Deposit slip #"
              : method === "GCASH" ? "GCash reference #"
              : method === "CARD"  ? "Approval / auth #"
              :                      "Channel reference number"
              }
              maxLength={64}
              disabled={busy}
              required={referenceRequired}
            />
          </div>

          {/* Date received — the actual day the school collected this
              payment. Separate from the auto-stamped paidAt timestamp
              (which records WHEN this record was entered into the
              system) so a cashier can backdate an entry without losing
              the audit trail of when it was actually keyed in. */}
          <div className={s.field}>
            <label className={s.label}>
              Date received <span className={s.fieldOpt}>(actual receipt date)</span>
            </label>
            <input
              className={s.input}
              type="date"
              value={paymentReceivedOn}
              onChange={e => setPaymentReceivedOn(e.target.value)}
              disabled={busy}
            />
            <div className={s.fieldHint}>
              The calendar day the school actually received the money — leave as-is
              for same-day entries, change it when entering a payment received earlier.
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>Notes <span className={s.fieldOpt}>(optional)</span></label>
            <textarea
              className={`${s.input} ${s.textarea}`}
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={busy}
            />
          </div>

          {isRequest && (
            <div className={s.field}>
              <label className={s.label}>
                Reason for change <span className={s.req}>*</span>
              </label>
              <textarea
                className={`${s.input} ${s.textarea}`}
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder='e.g. "Parent paid 4,500 not 5,000 — wrong amount entered."'
                disabled={busy}
                required
              />
              <div className={s.fieldHint}>
                The approver will see this. Be specific so they can decide quickly.
              </div>
            </div>
          )}

          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost}
                    onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !dirty || !orNumber.trim() || (isRequest && !reason.trim())}>
              {busy
                ? (isRequest ? "Sending…" : "Saving…")
                : (isRequest ? "Send request" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Modal: per-month payment breakdown for one invoice. Shows what the
 * student owes for each slot (Enrollment + each month) alongside what's
 * been paid against it, with a status filter (All / Unpaid / Partial /
 * Paid / Overpaid) so the cashier can answer "how much for September?"
 * without scanning the activity log.
 *
 * Self-fetches /api/tuition/soa to get the same per-row figures the SOA
 * page renders, but in a tighter modal layout suitable for quick
 * lookups from the Payments tab. */
function PaymentBreakdownModal({
  invoice, student, semester, payments,
  onClose, onPay, onRefund, onChanged,
}) {
  const [soa, setSoa]               = useState(null);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState("");

  // Ref to the printable content (the inner modal box, not the
  // overlay). html2canvas snapshots THIS subtree so the captured
  // output excludes the dark backdrop and matches what the user
  // sees on screen.
  const printableRef = useRef(null);
  const [printBusy, setPrintBusy] = useState(false);
  const [printError, setPrintError] = useState("");

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Print path — same canvas → PDF → hidden-iframe pattern as the SOA.
   *  Going through a snapshot means the printed artifact is pixel-
   *  identical to the on-screen modal — no fight with browser auto-
   *  scale, no responsive-breakpoint reflow, no background-colour
   *  stripping (print stylesheets default to white-background-with-
   *  black-text for ink-saving; an image PDF preserves the maroon
   *  status pills and the gold-tinted Total Due / Paid / Remaining
   *  cards exactly as drawn). */
  async function handlePrint() {
    if (!printableRef.current || printBusy) return;
    setPrintBusy(true);
    setPrintError("");
    try {
      // Freeze any in-flight CSS animation/transition on the modal so
      // the snapshot captures the resting visual state — without this
      // a chip's fade-in keyframe could leave half-opacity values in
      // the canvas. Mirrors PaymentReceiptModal's data-capture trick.
      printableRef.current.setAttribute("data-capture", "true");
      void printableRef.current.offsetHeight; // force layout flush

      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        // Skip controls on the captured page — the close ×, the
        // action buttons row (Close / Print / Record Payment), and
        // anything else flagged with data-noprint. Keeps the printed
        // sheet a clean record without buttons.
        ignoreElements: (el) =>
          el?.dataset?.noprint === "true",
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      // Single-page fast path + multi-page pagination — mirrors the
      // SOA's handlePrint so both surfaces paginate identically.
      if (imgH <= pageH - margin * 2) {
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
      } else {
        let heightLeft = imgH;
        let position = margin;
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= (pageH - margin * 2);
        while (heightLeft > 0) {
          position = margin - (imgH - heightLeft);
          pdf.addPage();
          pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
          heightLeft -= (pageH - margin * 2);
        }
      }

      pdf.autoPrint();
      const existing = document.getElementById("breakdown-print-frame");
      if (existing) existing.remove();
      const blobUrl = pdf.output("bloburl");
      const frame = document.createElement("iframe");
      frame.id = "breakdown-print-frame";
      frame.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;border:0;opacity:0;";
      const trigger = () => {
        try { frame.contentWindow?.focus(); frame.contentWindow?.print(); }
        catch (e) { /* PDF /OpenAction fallback */ }
      };
      frame.onload = trigger;
      frame.src = blobUrl;
      document.body.appendChild(frame);
      setTimeout(trigger, 1200);
      setTimeout(() => {
        try { URL.revokeObjectURL(blobUrl); } catch { /* already revoked */ }
        frame.remove();
      }, 60000);
    } catch (e) {
      setPrintError(`Print failed: ${e.message}`);
    } finally {
      // Always release the animation freeze, even on error, so the
      // on-screen modal doesn't stay frozen if capture throws.
      printableRef.current?.removeAttribute("data-capture");
      setPrintBusy(false);
    }
  }

  // Safety net: tear down any leftover print iframe on unmount.
  useEffect(() => () => {
    const f = document.getElementById("breakdown-print-frame");
    if (f) f.remove();
  }, []);
  // "all" | "unpaid" | "partial" | "paid" | "overpaid". Default "all" so
  // the cashier sees every row first; they can narrow with one click.
  const [filter, setFilter]         = useState("all");
  // Reallocate dialog target — { row, sourcePayment } | null. Opens when
  // the cashier clicks "Reallocate" on an OVERPAID row; the dialog asks
  // for the target month + amount (default = full overpayment).
  const [reallocateTarget, setReallocateTarget] = useState(null);
  const [reallocateBusy,   setReallocateBusy]   = useState(false);
  const [reallocateError,  setReallocateError]  = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError("");
    (async () => {
      try {
        const q = `studentId=${invoice.studentId}&schoolYearId=${invoice.schoolYearId}`;
        const res = await fetch(`${API_BASE}/api/tuition/soa?${q}`);
        if (!res.ok) throw new Error(`Load failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setSoa(data);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Could not load breakdown.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoice.studentId, invoice.schoolYearId]);

  /** Convert one SOA row → the canonical {label, due, paid, remaining,
   *  status} shape this modal renders. Status is a derived value (not the
   *  invoice-level status) so each row reads independently. slotIndex
   *  matches the backend's appliesToMonth vocabulary (0 = enrollment,
   *  1..N = months) so a row can be mapped back to the payments it
   *  represents for split / reallocate. */
  function deriveRow(slotIndex, label, due, paid) {
    const dueN  = Number(due)  || 0;
    const paidN = Number(paid) || 0;
    const rem   = dueN - paidN;
    let status;
    if (paidN <= 0.005 && dueN > 0.005)         status = "unpaid";
    else if (rem <= -0.005)                     status = "overpaid";
    else if (rem <= 0.005)                      status = "paid";
    else                                        status = "partial";
    return { slotIndex, label, due: dueN, paid: paidN, remaining: rem, status };
  }

  const rows = (() => {
    if (!soa) return [];
    const list = [];
    list.push(deriveRow(
      0,
      "Enrollment fee",
      Number(soa.enrollmentDue)     || 0,
      Number(soa.enrollmentPaidSum) || 0,
    ));
    (soa.months_rows || []).forEach(r => {
      list.push(deriveRow(
        Number(r.index) || 0,
        r.month || `Month ${r.index}`,
        Number(r.monthlyDue) || 0,
        Number(r.amount)     || 0,
      ));
    });
    return list;
  })();

  /** Find the payment we should split when the cashier reallocates an
   *  overpayment on a slot. Strategy:
   *    1. Look at the SOA's paymentCoverage map — keyed by paymentId,
   *       value is a list of { slotIdx, amount, overpay } hits.
   *    2. Filter to entries where slotIdx === slot AND overpay === true
   *       (i.e. payments that contributed surplus to this exact slot).
   *    3. Of those candidates, pick the most recent (latest paidAt).
   *
   *  Falls back to picking any payment whose appliesToMonth equals the
   *  source slot if the coverage map is missing — covers older SOA
   *  responses that don't include payment coverage. Returns null when
   *  no candidate is identifiable. */
  function findSplitablePayment(slotIndex, neededAmount) {
    if (!Array.isArray(payments) || payments.length === 0) return null;
    const paidAt = id => {
      const p = payments.find(x => x.id === id);
      return p?.paidAt ? new Date(p.paidAt).getTime() : 0;
    };

    const coverage = soa?.paymentCoverage || {};
    // Pass 1: prefer payments whose coverage flagged overpay on this slot.
    const overpayCandidates = [];
    for (const [pid, hits] of Object.entries(coverage)) {
      if (!Array.isArray(hits)) continue;
      if (hits.some(h => Number(h.slotIdx) === slotIndex && h.overpay)) {
        const numId = Number(pid);
        const p = payments.find(x => x.id === numId);
        if (p && Number(p.amount) > neededAmount + 0.005) overpayCandidates.push(p);
      }
    }
    if (overpayCandidates.length > 0) {
      overpayCandidates.sort((a, b) => paidAt(b.id) - paidAt(a.id));
      return overpayCandidates[0];
    }

    // Pass 2: fall back to payments whose stored appliesToMonth points at
    // this slot — older SOA responses or payments that haven't been
    // through coverage yet.
    const directCandidates = payments.filter(p =>
      Number(p.appliesToMonth) === slotIndex
      && Number(p.amount) > neededAmount + 0.005
    );
    if (directCandidates.length > 0) {
      directCandidates.sort((a, b) => paidAt(b.id) - paidAt(a.id));
      return directCandidates[0];
    }
    return null;
  }

  /** Open the reallocate dialog for a row. Resolves the source payment
   *  up-front so the dialog can show "From: April · ₱2,888.87 (CASH)"
   *  context. If we can't find a splittable payment, surface a clear
   *  error instead of opening a dialog with a null source. */
  function openReallocate(row) {
    const overAmount = Math.abs(row.remaining);  // remaining is negative on overpay
    const src = findSplitablePayment(row.slotIndex, overAmount);
    if (!src) {
      setLoadError(
        "Couldn't find a single payment to split — the overpayment came from "
        + "multiple receipts. Open Payment History and edit the relevant "
        + "payment's Applies-to manually."
      );
      return;
    }
    setLoadError("");
    setReallocateError("");
    setReallocateTarget({ row, sourcePayment: src, overAmount });
  }

  /** Submit handler for ReallocateDialog. Routes to POST /split which
   *  splits the source payment into two rows: original keeps
   *  (originalAmount - amount), new row carries amount + targetAppliesToMonth.
   *  Both rows share the receipt's metadata. */
  async function submitReallocate(payload) {
    if (!reallocateTarget || reallocateBusy) return;
    setReallocateBusy(true); setReallocateError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/tuition-payments/${reallocateTarget.sourcePayment.id}/split`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Reallocate failed (${res.status})`));
      }
      setReallocateTarget(null);
      // Re-fetch the SOA so the breakdown reflects the new split rows.
      const q = `studentId=${invoice.studentId}&schoolYearId=${invoice.schoolYearId}`;
      const soaRes = await fetch(`${API_BASE}/api/tuition/soa?${q}`);
      if (soaRes.ok) setSoa(await soaRes.json());
      // Tell the parent so its cached payments list refreshes.
      onChanged?.();
    } catch (err) {
      setReallocateError(err.message || "Reallocate failed.");
    } finally {
      setReallocateBusy(false);
    }
  }

  const filtered = filter === "all" ? rows : rows.filter(r => r.status === filter);

  // Aggregate totals across the visible filtered rows so the footer shows
  // the cashier exactly what the chosen subset adds up to.
  const totals = filtered.reduce((acc, r) => ({
    due:       acc.due       + r.due,
    paid:      acc.paid      + r.paid,
    remaining: acc.remaining + r.remaining,
  }), { due: 0, paid: 0, remaining: 0 });

  const counts = {
    all:      rows.length,
    unpaid:   rows.filter(r => r.status === "unpaid").length,
    partial:  rows.filter(r => r.status === "partial").length,
    paid:     rows.filter(r => r.status === "paid").length,
    overpaid: rows.filter(r => r.status === "overpaid").length,
  };

  const studentName = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;

  function statusPill(status) {
    const map = {
      unpaid:   { bg: "#fdecea", fg: "#a8281c", border: "#e6b8b4", label: "Unpaid"  },
      partial:  { bg: "#fff8e1", fg: "#8a6a1c", border: "#efe1a8", label: "Partial" },
      paid:     { bg: "#e3f2e6", fg: "#1f6a2d", border: "#bcdcc4", label: "Paid"    },
      overpaid: { bg: "#e7f0fb", fg: "#1d4fa8", border: "#c2d4ee", label: "Overpaid"},
    };
    const m = map[status] || map.unpaid;
    return (
      <span style={{
        fontSize: "0.7rem", fontWeight: 800, letterSpacing: 0.4,
        textTransform: "uppercase",
        padding: "2px 8px", borderRadius: 999,
        background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
        whiteSpace: "nowrap",
      }}>{m.label}</span>
    );
  }

  function chip(key, label, count) {
    const active = filter === key;
    return (
      <button
        type="button"
        onClick={() => setFilter(key)}
        style={{
          padding: "6px 12px",
          fontSize: "0.78rem",
          fontWeight: 700,
          background: active ? "#1a1a1a" : "#fff",
          color:      active ? "#fff"    : "#3d342a",
          border: `1px solid ${active ? "#1a1a1a" : "#e8e4dc"}`,
          borderRadius: 999,
          cursor: "pointer",
          transition: "background 0.14s, border-color 0.14s, color 0.14s",
          whiteSpace: "nowrap",
          font: "inherit",
        }}>
        {label}
        <span style={{
          marginLeft: 6, padding: "1px 6px", borderRadius: 999,
          background: active ? "rgba(255,255,255,0.18)" : "#f4ecd8",
          color: active ? "#fff" : "#6b3a30",
          fontSize: "0.68rem", fontWeight: 800,
        }}>
          {count}
        </span>
      </button>
    );
  }

  return (
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalWide}`}
           ref={printableRef}
           onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Payment breakdown</h3>
          {/* data-noprint = filtered out by the html2canvas capture so
              the close × doesn't show on the printed page. */}
          <button className={s.modalClose} type="button" onClick={onClose}
                  data-noprint="true"
                  aria-label="Close">×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{studentName}</div>
          <div className={s.modalSubMeta}>
            {student?.studentNumber && <span>#{student.studentNumber}</span>}
            {semester?.label        && <span>· {semester.label}</span>}
          </div>
        </div>

        {loadError && <div className={s.error}>{loadError}</div>}

        {/* Filter chips — count beside each so the cashier knows how many
            rows hide behind a tab before clicking it. */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 8,
          padding: "4px 0 12px",
        }}>
          {chip("all",      "All",      counts.all)}
          {chip("unpaid",   "Unpaid",   counts.unpaid)}
          {chip("partial",  "Partial",  counts.partial)}
          {chip("paid",     "Paid",     counts.paid)}
          {counts.overpaid > 0 && chip("overpaid", "Overpaid", counts.overpaid)}
        </div>

        {/* Ledger table — scrollable inside the modal so a 12-month list
            stays compact and the header / filters / footer stay anchored. */}
        <div style={{
          border: "1px solid #f0ebe4", borderRadius: 12,
          overflow: "hidden", background: "#fff",
          maxHeight: "min(48vh, 420px)", overflowY: "auto",
        }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "#8a8070",
                          fontSize: "0.86rem" }}>
              Loading breakdown…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#8a8070",
                          fontSize: "0.86rem", fontStyle: "italic" }}>
              {rows.length === 0
                ? "No fee structure attached to this invoice yet."
                : "No rows match this filter."}
            </div>
          ) : (
            <table style={{
              width: "100%", borderCollapse: "separate",
              borderSpacing: 0, fontSize: "0.85rem",
            }}>
              <thead>
                <tr>
                  {["Period", "Due", "Paid", "Remaining", "Status", ""].map((h, i, arr) => (
                    <th key={h || `col-${i}`} style={{
                      position: "sticky", top: 0, zIndex: 1,
                      background: "#faf6ee", color: "#6b3a30",
                      textAlign: i === 0 ? "left"
                               : (i === 4 || i === arr.length - 1) ? "center"
                               : "right",
                      padding: "9px 12px",
                      fontSize: "0.7rem",
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 800,
                      borderBottom: "1.5px solid #f0ebe4",
                      boxShadow: "0 2px 0 #f0ebe4",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={`${r.slotIndex}-${r.label}-${i}`}
                      style={{ background: i % 2 ? "#fcfaf3" : "#fff" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1a1a1a" }}>
                      {r.label}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right",
                                 fontVariantNumeric: "tabular-nums" }}>
                      ₱{fmtMoney(r.due)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right",
                                 fontVariantNumeric: "tabular-nums",
                                 color: r.paid > 0.005 ? "#1f6a2d" : "#a09080",
                                 fontWeight: r.paid > 0.005 ? 700 : 500 }}>
                      ₱{fmtMoney(r.paid)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right",
                                 fontVariantNumeric: "tabular-nums",
                                 fontWeight: 700,
                                 color: r.status === "paid"     ? "#1f6a2d"
                                      : r.status === "overpaid" ? "#1d4fa8"
                                      :                            "#a8281c" }}>
                      {r.status === "overpaid"
                        ? <>₱{fmtMoney(-r.remaining)} <span style={{ fontSize: "0.7rem",
                              fontWeight: 700, color: "#1d4fa8" }}>over</span></>
                        : <>₱{fmtMoney(r.remaining)}</>}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {statusPill(r.status)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {r.status === "overpaid" && (
                        <span style={{ display: "inline-flex", gap: 4, justifyContent: "center" }}>
                          <button type="button"
                                  onClick={() => openReallocate(r)}
                                  disabled={reallocateBusy}
                                  title="Move the surplus to a different month — splits the receipt into two rows"
                                  style={{
                                    padding: "4px 10px", fontSize: "0.74rem",
                                    background: "#1d4fa8", color: "#fff",
                                    border: "1px solid #1d4fa8", borderRadius: 6,
                                    cursor: reallocateBusy ? "not-allowed" : "pointer",
                                    fontWeight: 700, opacity: reallocateBusy ? 0.6 : 1,
                                  }}>
                            ⇄ Reallocate
                          </button>
                          {onRefund && (
                            <button type="button"
                                    onClick={() => onRefund(invoice)}
                                    title="Refund the surplus to the parent"
                                    style={{
                                      padding: "4px 10px", fontSize: "0.74rem",
                                      background: "#fff", color: "#a8281c",
                                      border: "1px solid #e6b8b4", borderRadius: 6,
                                      cursor: "pointer", fontWeight: 600,
                                    }}>
                              − Refund
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Filter-aware totals — re-renders as the cashier flips chips. */}
        {!loading && filtered.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10, marginTop: 12,
          }}>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: "#fffaeb", border: "1px solid #efe2a7",
            }}>
              <div style={{ fontSize: "0.7rem", color: "#8a6d28",
                            fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: 0.5 }}>Total due</div>
              <div style={{ fontWeight: 800, fontSize: "1rem",
                            color: "#1a1a1a", marginTop: 2,
                            fontVariantNumeric: "tabular-nums" }}>
                ₱{fmtMoney(totals.due)}
              </div>
            </div>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: "#e3f2e6", border: "1px solid #bcdcc4",
            }}>
              <div style={{ fontSize: "0.7rem", color: "#1f6a2d",
                            fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: 0.5 }}>Total paid</div>
              <div style={{ fontWeight: 800, fontSize: "1rem",
                            color: "#1f6a2d", marginTop: 2,
                            fontVariantNumeric: "tabular-nums" }}>
                ₱{fmtMoney(totals.paid)}
              </div>
            </div>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: totals.remaining < -0.005 ? "#e7f0fb" : "#fdecea",
              border:    totals.remaining < -0.005 ? "1px solid #c2d4ee" : "1px solid #e6b8b4",
            }}>
              <div style={{
                fontSize: "0.7rem",
                color: totals.remaining < -0.005 ? "#1d4fa8" : "#a8281c",
                fontWeight: 800, textTransform: "uppercase",
                letterSpacing: 0.5,
              }}>
                {totals.remaining < -0.005 ? "Overpaid" : "Remaining"}
              </div>
              <div style={{
                fontWeight: 800, fontSize: "1rem",
                color: totals.remaining < -0.005 ? "#1d4fa8" : "#a8281c",
                marginTop: 2, fontVariantNumeric: "tabular-nums",
              }}>
                ₱{fmtMoney(Math.abs(totals.remaining))}
              </div>
            </div>
          </div>
        )}

        {printError && (
          <div className={s.error} style={{ marginTop: 10 }} data-noprint="true">
            {printError}
          </div>
        )}
        <div className={s.modalActions} style={{ marginTop: 14 }}
             data-noprint="true">
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
          {/* Print — captures the modal via html2canvas, builds a PDF,
              and triggers print from a hidden iframe. Output is pixel-
              identical to the on-screen modal (status pills keep their
              backgrounds, totals cards keep their amber/green/red
              tints — none of which native @media print preserves
              without print-color-adjust acrobatics). */}
          <button type="button"
                  className={s.btnSecondary || s.btnGhost}
                  onClick={handlePrint}
                  disabled={loading || rows.length === 0 || printBusy}
                  title="Print this payment breakdown">
            {printBusy ? "Preparing…" : "🖨 Print"}
          </button>
          {onPay && (
            <button type="button" className={s.btnPrimary}
                    onClick={() => onPay(invoice)}>
              + Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Focused reallocate dialog — overlays the breakdown modal when
          the cashier clicks "Reallocate" on an overpaid row. Splits
          the source payment into two ledger rows: original keeps
          (originalAmount - amount), new row carries amount + new
          appliesToMonth. Same OR # / method / paidAt on both. */}
      <ReallocateDialog
        open={!!reallocateTarget}
        target={reallocateTarget}
        rows={rows}
        busy={reallocateBusy}
        error={reallocateError}
        onCancel={() => { if (!reallocateBusy) { setReallocateTarget(null); setReallocateError(""); } }}
        onConfirm={submitReallocate}
      />
    </div>
  );
}

/* Focused "Reallocate overpayment" dialog — splits one TuitionPayment
 * into two rows so the cashier can move the surplus from one slot to
 * another. Both rows keep the same physical-receipt metadata
 * (method / referenceNo / paidAt / recordedBy) so the audit trail still
 * ties them back to the same transaction.
 *
 * UX:
 *   - Source row + overpayment shown front-and-centre.
 *   - Target month dropdown lists slots with remaining > 0 (so the
 *     cashier can't accidentally re-overpay another month).
 *   - Amount defaults to the full overpayment; can be lowered for a
 *     partial move.
 *
 * Same animation language as RejectReasonDialog / PayChargeDialog —
 * fade + scale-in, focus trap, Esc cancel, body scroll lock,
 * prefers-reduced-motion fallback.
 *
 * Submit shape:
 *   { amount, targetAppliesToMonth }
 *
 * The component is dumb — validation + the actual POST live in the
 * parent's submitReallocate handler. */
function ReallocateDialog({ open, target, rows, busy, error, onCancel, onConfirm }) {
  const overAmount = target?.overAmount || 0;
  const sourceLabel = target?.row?.label || "—";
  const sourcePayment = target?.sourcePayment;
  const sourceSlotIndex = target?.row?.slotIndex;

  const [amount, setAmount]                 = useState("");
  const [targetSlot, setTargetSlot]         = useState("");
  const [localErr, setLocalErr]             = useState("");

  // Reset every time the dialog opens — never carry stale text between
  // two reallocations the cashier hits in the same session.
  useEffect(() => {
    if (!open) return;
    setAmount(overAmount > 0.005 ? overAmount.toFixed(2) : "");
    setTargetSlot("");
    setLocalErr("");
  }, [open, overAmount]);

  // Esc cancels; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!busy) onCancel?.();
      }
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onCancel]);

  if (!open || !target) return null;

  // Targets the cashier can pick — every slot except the source, with a
  // human label + remaining hint. UNPAID and PARTIAL slots float to the
  // top so the cashier sees the obvious targets first; settled slots
  // stay reachable in case they want to move a payment for another
  // reason. No Auto/FIFO any more — there's no cascade left to defer to,
  // so the cashier must pick one specific slot.
  const targetOptions = (rows || [])
    .filter(r => r.slotIndex !== sourceSlotIndex)
    .sort((a, b) => {
      // unpaid → partial → others
      const rank = x => x.status === "unpaid" ? 0
                      : x.status === "partial" ? 1
                      : 2;
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      return a.slotIndex - b.slotIndex;
    });

  function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setLocalErr("");
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setLocalErr("Amount must be greater than 0.");
      return;
    }
    if (parsed - 0.005 > overAmount) {
      setLocalErr(`Cannot exceed the overpaid amount (₱${fmtMoney(overAmount)}).`);
      return;
    }
    if (sourcePayment && parsed >= Number(sourcePayment.amount) - 0.005) {
      setLocalErr(
        `The source payment is ₱${fmtMoney(sourcePayment.amount)}. `
        + `Splitting must leave at least ₱0.01 in the original — try a smaller amount, `
        + `or edit the payment's Applies-to instead of splitting.`
      );
      return;
    }
    if (targetSlot === "" || targetSlot === null) {
      setLocalErr("Pick a target month.");
      return;
    }
    const targetN = Number(targetSlot);
    if (!Number.isFinite(targetN)) {
      setLocalErr("Target month must be a real slot.");
      return;
    }
    onConfirm?.({
      amount:               parsed.toFixed(2),
      targetAppliesToMonth: targetN,
    });
  }

  return createPortal(
    <div className={s.modalOverlay}
         style={{ zIndex: 1110 }}>
      <div className={s.modal}
           onClick={e => e.stopPropagation()}
           style={{ maxWidth: 480 }}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Move overpayment</h3>
          <button className={s.modalClose} type="button"
                  onClick={onCancel} aria-label="Close" disabled={busy}>×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{sourceLabel}</div>
          <div className={s.modalSubMeta}>
            <span style={{ color: "#1d4fa8", fontWeight: 700 }}>
              Currently overpaid by ₱{fmtMoney(overAmount)}
            </span>
            {sourcePayment && (
              <>
                <span>·</span>
                <span>
                  Splitting receipt
                  {sourcePayment.method ? ` · ${sourcePayment.method}` : ""}
                  {sourcePayment.referenceNo ? ` · Ref ${sourcePayment.referenceNo}` : ""}
                </span>
              </>
            )}
          </div>
        </div>
        <form onSubmit={submit} className={s.modalForm}>
          {(error || localErr) && (
            <div className={s.error}>{localErr || error}</div>
          )}

          <div className={s.field}>
            <label className={s.label}>
              Move to <span className={s.req}>*</span>
            </label>
            <select className={s.input}
                    value={targetSlot}
                    onChange={e => setTargetSlot(e.target.value)}
                    disabled={busy}
                    required>
              <option value="">— pick a month —</option>
              {targetOptions.map(opt => (
                <option key={opt.slotIndex} value={opt.slotIndex}>
                  {opt.label}
                  {opt.status === "unpaid"  ? ` — ₱${fmtMoney(opt.due)} unpaid`
                  : opt.status === "partial" ? ` — ₱${fmtMoney(opt.remaining)} remaining`
                  : opt.status === "paid"    ? ` · already settled`
                  : opt.status === "overpaid" ? ` · already overpaid`
                  :                              ""}
                </option>
              ))}
            </select>
            <div className={s.fieldHint}>
              UNPAID and PARTIAL months are listed first.
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>
              Amount to move (₱) <span className={s.req}>*</span>
            </label>
            <input
              className={s.input}
              type="number"
              step="0.01"
              min="0.01"
              max={overAmount.toFixed(2)}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={busy}
              required
              autoFocus
            />
            <div className={s.fieldHint}>
              Defaults to the full overpayment. Lower it to leave some
              surplus on {sourceLabel} (e.g. for a partial reallocate).
            </div>
          </div>

          <div style={{
            background: "#fffaeb", border: "1px solid #efe2a7",
            borderRadius: 10, padding: "10px 12px",
            fontSize: "0.78rem", color: "#5a4e3a", lineHeight: 1.5,
          }}>
            <b style={{ color: "#1a1a1a" }}>What this does:</b> splits the
            receipt into two ledger rows. One stays on <b>{sourceLabel}</b>;
            one moves to your picked target. Same OR # / method / paid date
            on both — it's the same physical transaction, recorded as two
            allocation lines. Reversible by Edit Payment in the Activity Log.
          </div>

          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost}
                    onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !amount || !targetSlot}>
              {busy ? "Moving…" : "⇄ Reallocate"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/* Modal: manage one student's "additional charges" for a school year —
 * summer class fees, special-curriculum surcharges, ID replacement, etc.
 * These sit beside the regular tuition cascade rather than inside it, so
 * the existing UNPAID/PARTIAL/PAID logic, Recompute, and per-month
 * cascade stay untouched. The SOA renders them at the bottom (before
 * signature) and rolls them into the grand total.
 *
 * Auth (enforced by the backend, mirrored here for affordances):
 *   - Anyone with tuition access can list / add / record-payment
 *   - Only Admin / SuperAdmin can edit the underlying label / amount or delete
 */
function AdditionalChargesModal({ student, schoolYear, onClose, onChanged }) {
  const [charges, setCharges]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState("");
  const [busy, setBusy]             = useState(false);

  // New-charge form
  const [newLabel,  setNewLabel]    = useState("");
  const [newAmount, setNewAmount]   = useState("");
  const [newError,  setNewError]    = useState("");

  // Per-row "record payment" inline state — keyed by charge id. Used by
  // the admin-only "Edit paid" override path (for corrections); the
  // cashier's day-to-day flow goes through the focused Pay dialog below.
  const [paymentDraft, setPaymentDraft] = useState({});  // { [id]: { paidAmount, referenceNo, error } }

  // Focused "Pay" dialog target — opens when the cashier clicks Pay on
  // an UNPAID/PARTIAL row. Captures amount-collected-this-time, method,
  // reference, and notes; submit calls POST /pay which adds (not sets)
  // to the charge's paidAmount on the backend.
  const [payTarget,  setPayTarget]  = useState(null);   // charge | null
  const [payBusy,    setPayBusy]    = useState(false);
  const [payError,   setPayError]   = useState("");

  // Per-charge payment-history modal target — opens from the (i) icon
  // next to each charge label. Read-only: shows every receipt recorded
  // against the charge with amount / method / ref / time / cashier.
  const [historyTarget, setHistoryTarget] = useState(null); // charge | null

  // Strict-admin gate for destructive actions; matches the backend's
  // requireStrictAdmin on /edit and DELETE.
  const currentUser = getCurrentUser();
  const canHardEdit = currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin";

  async function load() {
    if (!student?.id || !schoolYear?.id) {
      setCharges([]); setLoading(false);
      return;
    }
    setLoading(true); setLoadError("");
    try {
      const url = `${API_BASE}/api/additional-charges`
                + `?studentId=${student.id}&schoolYearId=${schoolYear.id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const rows = await res.json();
      setCharges(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setLoadError(e.message || "Could not load additional charges.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
           [student?.id, schoolYear?.id]);

  function startEditPayment(c) {
    setPaymentDraft(d => ({
      ...d,
      [c.id]: {
        paidAmount:  c.paidAmount != null ? String(c.paidAmount) : "",
        referenceNo: c.referenceNo || "",
        error: "",
      },
    }));
  }
  function cancelEditPayment(id) {
    setPaymentDraft(d => {
      const next = { ...d };
      delete next[id];
      return next;
    });
  }
  function patchDraft(id, patch) {
    setPaymentDraft(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  }

  async function addCharge(e) {
    e.preventDefault();
    if (busy) return;
    setNewError("");
    const label  = newLabel.trim();
    const amount = Number(newAmount);
    if (!label) { setNewError("Label is required."); return; }
    if (!Number.isFinite(amount) || amount <= 0) {
      setNewError("Amount must be greater than 0.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/additional-charges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          studentId:    student.id,
          schoolYearId: schoolYear.id,
          label,
          amount:       amount.toFixed(2),
        }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Add failed (${res.status})`));
      }
      setNewLabel(""); setNewAmount("");
      await load();
      onChanged?.();
    } catch (err) {
      setNewError(err.message || "Add failed.");
    } finally {
      setBusy(false);
    }
  }

  async function savePayment(charge) {
    const draft = paymentDraft[charge.id];
    if (!draft || busy) return;
    const paid = Number(draft.paidAmount);
    if (!Number.isFinite(paid) || paid < 0) {
      patchDraft(charge.id, { error: "Paid amount must be 0 or more." });
      return;
    }
    if (paid > Number(charge.amount) + 0.005) {
      patchDraft(charge.id, { error: `Cannot exceed ₱${fmtMoney(charge.amount)}.` });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/additional-charges/${charge.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paidAmount:  paid.toFixed(2),
          referenceNo: draft.referenceNo || null,
        }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      cancelEditPayment(charge.id);
      await load();
      onChanged?.();
    } catch (err) {
      patchDraft(charge.id, { error: err.message || "Save failed." });
    } finally {
      setBusy(false);
    }
  }

  async function deleteCharge(charge) {
    if (!canHardEdit) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${charge.label}"? Any payment recorded against it will also be removed.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/additional-charges/${charge.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Delete failed (${res.status})`));
      }
      await load();
      onChanged?.();
    } catch (err) {
      setLoadError(err.message || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  /** Submit handler for PayChargeDialog. Routes to POST /pay which adds
   *  (not sets) the entered amount to paidAmount, captures method, ref,
   *  notes. Backend re-validates the cumulative cap so a race between
   *  two cashiers can't push paid past the charge's amount. */
  async function submitPayment(payload) {
    if (!payTarget || payBusy) return;
    setPayBusy(true); setPayError("");
    try {
      const res = await fetch(`${API_BASE}/api/additional-charges/${payTarget.id}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Payment failed (${res.status})`));
      }
      setPayTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      setPayError(err.message || "Payment failed.");
    } finally {
      setPayBusy(false);
    }
  }

  // Aggregate totals — what the parent will see at the bottom of the SOA.
  const totals = charges.reduce((acc, c) => ({
    amount: acc.amount + (Number(c.amount)     || 0),
    paid:   acc.paid   + (Number(c.paidAmount) || 0),
  }), { amount: 0, paid: 0 });
  const remaining = totals.amount - totals.paid;

  function statusPill(status) {
    const map = {
      UNPAID:  { bg: "#fdecea", fg: "#a8281c", border: "#e6b8b4", label: "Unpaid"  },
      PARTIAL: { bg: "#fff8e1", fg: "#8a6a1c", border: "#efe1a8", label: "Partial" },
      PAID:    { bg: "#e3f2e6", fg: "#1f6a2d", border: "#bcdcc4", label: "Paid"    },
    };
    const m = map[status] || map.UNPAID;
    return (
      <span style={{
        fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.4,
        textTransform: "uppercase",
        padding: "2px 8px", borderRadius: 999,
        background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
        whiteSpace: "nowrap",
      }}>{m.label}</span>
    );
  }

  const studentName = student
    ? `${student.lastName || ""}, ${student.firstName || ""}`.replace(/^, |, $/g, "")
    : "—";

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !busy) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return (
    <div className={s.modalOverlay}>
      {/* Wider than the default .modalWide (640) so the 6-column table
          (Particular + 3 numeric + Status + Actions) has room for the
          Pay / Edit / ✕ stack without the Actions column getting
          squeezed and cutting off the buttons against the right edge. */}
      <div className={`${s.modal} ${s.modalWide}`}
           style={{ maxWidth: 820 }}
           onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Additional charges</h3>
          <button className={s.modalClose} type="button" onClick={onClose}
                  aria-label="Close" disabled={busy}>×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{studentName}</div>
          <div className={s.modalSubMeta}>
            {student?.studentNumber && <span>#{student.studentNumber}</span>}
            {schoolYear?.label      && <span>· SY {schoolYear.label}</span>}
          </div>
          <div className={s.fieldHint} style={{ marginTop: 6 }}>
            For fees outside the regular tuition — summer class, special curriculum,
            field trips, etc. These appear at the bottom of the SOA and are added
            to the grand total.
          </div>
        </div>

        {loadError && <div className={s.error}>{loadError}</div>}

        {/* Existing charges */}
        {/* Container: vertical scroll (cap modal height) + horizontal
            scroll fallback for very narrow viewports. The table sets
            its own minWidth so columns never collapse below readability;
            on a normal-width modal nothing scrolls horizontally, but on
            mobile the user can swipe right to reach Actions. */}
        <div style={{
          border: "1px solid #f0ebe4", borderRadius: 12,
          background: "#fff",
          maxHeight: "min(40vh, 340px)",
          overflowY: "auto",
          overflowX: "auto",
        }}>
          {loading ? (
            <div style={{ padding: 28, textAlign: "center", color: "#8a8070",
                          fontSize: "0.86rem" }}>
              Loading charges…
            </div>
          ) : charges.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center", color: "#8a8070",
                          fontSize: "0.86rem", fontStyle: "italic" }}>
              No additional charges yet. Use the form below to add one.
            </div>
          ) : (
            <table style={{
              width: "100%", borderCollapse: "separate",
              borderSpacing: 0, fontSize: "0.85rem",
              minWidth: 740,  // ensures columns stay readable; container scrolls if narrower
            }}>
              {/* Explicit column widths — keeps the Actions column wide
                  enough for Pay + Edit + ✕ even when the row data is
                  short. Particular is unsized so it absorbs the slack. */}
              <colgroup>
                <col />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90  }} />
                <col style={{ width: 180 }} />
              </colgroup>
              <thead>
                <tr>
                  {["Particular", "Amount", "Paid", "Remaining", "Status", "Actions"].map((h, i) => (
                    <th key={h} style={{
                      position: "sticky", top: 0, zIndex: 1,
                      background: "#faf6ee", color: "#6b3a30",
                      textAlign: i === 0 ? "left" : (i >= 4 ? "center" : "right"),
                      // Last column gets extra right padding so the
                      // button group has breathing room from the
                      // border instead of kissing the right edge.
                      padding: i === 5 ? "9px 16px 9px 12px" : "9px 12px",
                      fontSize: "0.7rem",
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 800,
                      borderBottom: "1.5px solid #f0ebe4",
                      boxShadow: "0 2px 0 #f0ebe4",
                      whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {charges.map((c, i) => {
                  const draft = paymentDraft[c.id];
                  const editing = !!draft;
                  const remainingForRow = Number(c.amount) - Number(c.paidAmount);
                  return (
                    <tr key={c.id}
                        style={{ background: i % 2 ? "#fcfaf3" : "#fff" }}>
                      <td style={{ padding: "10px 12px", color: "#1a1a1a" }}>
                        <div style={{ fontWeight: 700, wordBreak: "break-word",
                                      display: "inline-flex", alignItems: "center",
                                      flexWrap: "wrap" }}>
                          <span>{c.label}</span>
                          <ChargeInfoButton
                            onClick={() => setHistoryTarget(c)}
                            label={`View payments for ${c.label}`}
                          />
                        </div>
                        {(c.method || c.referenceNo) && (
                          <div style={{ display: "flex", gap: 6, alignItems: "center",
                                        marginTop: 3, flexWrap: "wrap" }}>
                            {c.method && (
                              <span style={{
                                fontSize: "0.62rem", fontWeight: 800,
                                letterSpacing: 0.5, textTransform: "uppercase",
                                padding: "1px 7px", borderRadius: 4,
                                background: "#e8dfc9", color: "#5a4e3a",
                                border: "1px solid #d6cdb8",
                              }}>
                                {c.method}
                              </span>
                            )}
                            {c.referenceNo && (
                              <span style={{ fontSize: "0.74rem", color: "#8a8070",
                                             fontFamily: "ui-monospace, monospace" }}>
                                Ref {c.referenceNo}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right",
                                   fontVariantNumeric: "tabular-nums" }}>
                        ₱{fmtMoney(c.amount)}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right",
                                   fontVariantNumeric: "tabular-nums",
                                   color: Number(c.paidAmount) > 0.005 ? "#1f6a2d" : "#a09080",
                                   fontWeight: Number(c.paidAmount) > 0.005 ? 700 : 500 }}>
                        ₱{fmtMoney(c.paidAmount)}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right",
                                   fontVariantNumeric: "tabular-nums",
                                   fontWeight: 700,
                                   color: remainingForRow <= 0.005 ? "#1f6a2d" : "#a8281c" }}>
                        ₱{fmtMoney(Math.max(remainingForRow, 0))}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "center" }}>
                        {statusPill(c.status)}
                      </td>
                      <td style={{ padding: "10px 16px 10px 12px",
                                   textAlign: "center", whiteSpace: "nowrap" }}>
                        {editing ? (
                          /* Admin-only paid-amount override (corrections /
                             reversal). Cashier's day-to-day flow uses the
                             "Pay" button which routes through POST /pay. */
                          <span style={{ display: "inline-flex", gap: 4, justifyContent: "center" }}>
                            <button type="button" className={s.btnPrimary}
                                    style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                    onClick={() => savePayment(c)} disabled={busy}>
                              Save
                            </button>
                            <button type="button" className={s.btnGhost}
                                    style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                    onClick={() => cancelEditPayment(c.id)} disabled={busy}>
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", gap: 4, justifyContent: "center" }}>
                            {c.status !== "PAID" && (
                              <button type="button" className={s.btnPay}
                                      style={{ padding: "4px 12px", fontSize: "0.78rem" }}
                                      onClick={() => { setPayError(""); setPayTarget(c); }}
                                      disabled={busy || payBusy}
                                      title="Record a payment against this charge">
                                Pay
                              </button>
                            )}
                            {canHardEdit && (
                              <button type="button" className={s.btnGhost}
                                      style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                      onClick={() => startEditPayment(c)} disabled={busy || payBusy}
                                      title="Admin override — set a specific paid total (for corrections)">
                                Edit
                              </button>
                            )}
                            {canHardEdit && (
                              <button type="button" className={s.btnDanger}
                                      style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                      onClick={() => deleteCharge(c)} disabled={busy || payBusy}
                                      title="Delete this charge">
                                ✕
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Inline edit-payment row right under each editing row.
                    Rendered as a separate <tr> with colspan so the form
                    inputs have full width; less visual noise than cramming
                    them into the existing cells. */}
                {charges.map(c => {
                  const draft = paymentDraft[c.id];
                  if (!draft) return null;
                  return (
                    <tr key={`${c.id}-edit`} style={{ background: "#fffaeb" }}>
                      <td colSpan={6} style={{ padding: "10px 12px",
                                                borderTop: "1px dashed #efe2a7" }}>
                        <div style={{ display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 10 }}>
                          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: "0.7rem", fontWeight: 800,
                                           letterSpacing: 0.5, textTransform: "uppercase",
                                           color: "#8a6d28" }}>
                              Paid amount
                            </span>
                            <input type="number" step="0.01" min="0"
                                   className={s.input}
                                   value={draft.paidAmount}
                                   onChange={e => patchDraft(c.id, { paidAmount: e.target.value, error: "" })}
                                   placeholder="0.00"
                                   disabled={busy} />
                          </label>
                          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span style={{ fontSize: "0.7rem", fontWeight: 800,
                                           letterSpacing: 0.5, textTransform: "uppercase",
                                           color: "#8a6d28" }}>
                              Reference # <span style={{ color: "#a09080", fontWeight: 600 }}>(optional)</span>
                            </span>
                            <input type="text"
                                   className={s.input}
                                   value={draft.referenceNo}
                                   onChange={e => patchDraft(c.id, { referenceNo: e.target.value })}
                                   placeholder="OR # / receipt #"
                                   maxLength={64}
                                   disabled={busy} />
                          </label>
                        </div>
                        {draft.error && (
                          <div style={{ fontSize: "0.78rem", color: "#a8281c",
                                        marginTop: 6 }}>
                            {draft.error}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Totals — visible whenever there's at least one charge. */}
        {!loading && charges.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10, marginTop: 12,
          }}>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: "#fffaeb", border: "1px solid #efe2a7",
            }}>
              <div style={{ fontSize: "0.7rem", color: "#8a6d28",
                            fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: 0.5 }}>Total amount</div>
              <div style={{ fontWeight: 800, fontSize: "1rem",
                            color: "#1a1a1a", marginTop: 2,
                            fontVariantNumeric: "tabular-nums" }}>
                ₱{fmtMoney(totals.amount)}
              </div>
            </div>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: "#e3f2e6", border: "1px solid #bcdcc4",
            }}>
              <div style={{ fontSize: "0.7rem", color: "#1f6a2d",
                            fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: 0.5 }}>Total paid</div>
              <div style={{ fontWeight: 800, fontSize: "1rem",
                            color: "#1f6a2d", marginTop: 2,
                            fontVariantNumeric: "tabular-nums" }}>
                ₱{fmtMoney(totals.paid)}
              </div>
            </div>
            <div style={{
              padding: "10px 12px", borderRadius: 10,
              background: remaining <= 0.005 ? "#e3f2e6" : "#fdecea",
              border:    remaining <= 0.005 ? "1px solid #bcdcc4" : "1px solid #e6b8b4",
            }}>
              <div style={{ fontSize: "0.7rem",
                            color: remaining <= 0.005 ? "#1f6a2d" : "#a8281c",
                            fontWeight: 800, textTransform: "uppercase",
                            letterSpacing: 0.5 }}>Remaining</div>
              <div style={{ fontWeight: 800, fontSize: "1rem",
                            color: remaining <= 0.005 ? "#1f6a2d" : "#a8281c",
                            marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                ₱{fmtMoney(Math.max(remaining, 0))}
              </div>
            </div>
          </div>
        )}

        {/* Add new charge */}
        <form onSubmit={addCharge}
              style={{
                marginTop: 14,
                padding: "12px 14px",
                background: "#fffaeb",
                border: "1px dashed #e7d49a",
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}>
          <div style={{ fontSize: "0.74rem", fontWeight: 800,
                        letterSpacing: 0.5, textTransform: "uppercase",
                        color: "#8a6d28" }}>
            + Add a new charge
          </div>
          <div style={{ display: "grid",
                        gridTemplateColumns: "1.6fr 1fr auto",
                        gap: 10, alignItems: "end" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                             color: "#6b5a3a" }}>
                Particular
              </span>
              <input type="text"
                     className={s.input}
                     value={newLabel}
                     onChange={e => setNewLabel(e.target.value)}
                     placeholder="e.g. Summer Class 2026"
                     maxLength={200}
                     disabled={busy} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                             color: "#6b5a3a" }}>
                Amount (₱)
              </span>
              <input type="number" step="0.01" min="0.01"
                     className={s.input}
                     value={newAmount}
                     onChange={e => setNewAmount(e.target.value)}
                     placeholder="0.00"
                     disabled={busy} />
            </label>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !newLabel.trim() || !Number(newAmount)}
                    style={{ height: 38 }}>
              {busy ? "Adding…" : "+ Add"}
            </button>
          </div>
          {newError && (
            <div style={{ fontSize: "0.8rem", color: "#a8281c",
                          background: "#fdeae5", border: "1px solid #f1d6cf",
                          borderRadius: 8, padding: "7px 11px" }}>
              {newError}
            </div>
          )}
        </form>

        <div className={s.modalActions} style={{ marginTop: 14 }}>
          <button type="button" className={s.btnGhost} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>
      </div>

      {/* Focused payment dialog — overlays the charges modal when the
          cashier clicks "Pay" on any unpaid/partial row. Routes through
          POST /pay so the entered amount adds to the charge's running
          paidAmount on the server. */}
      <PayChargeDialog
        open={!!payTarget}
        charge={payTarget}
        student={student}
        busy={payBusy}
        error={payError}
        onCancel={() => { if (!payBusy) { setPayTarget(null); setPayError(""); } }}
        onConfirm={submitPayment}
      />

      {historyTarget && (
        <ChargePaymentsModal
          charge={historyTarget}
          student={student}
          schoolYearLabel={schoolYear?.label}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}

/* Focused "Pay" dialog for one AdditionalCharge — the cashier's
 * day-to-day "I'm collecting X today" flow. Defaults the amount to the
 * remaining balance (so the common "pay in full" case is one click of
 * Confirm); the cashier can override for partial payments. Method is
 * required and constrained to the same vocabulary as tuition payments
 * so the SOA's REF# column stays consistent.
 *
 * Same animation language as RejectReasonDialog — fade + scale-in,
 * focus trap, Esc cancel, body scroll lock, prefers-reduced-motion
 * fallback. Auto-focuses the amount field so the cashier can type or
 * just hit Enter to confirm the default.
 *
 * Submit shape:
 *   { amount, method, referenceNo, bankName, notes }
 *
 * Bank name only carries a value when method === "BANK"; the dropdown
 * is sourced from the curated list under Settings → Tuition so audit
 * rows stay consistent and don't drift with typos.
 *
 * The component itself is dumb — validation + the actual POST live in
 * the parent's submitPayment handler. */
function PayChargeDialog({ open, charge, student, busy, error, onCancel, onConfirm }) {
  const remaining = charge
    ? Math.max(Number(charge.amount || 0) - Number(charge.paidAmount || 0), 0)
    : 0;
  const [amount,            setAmount]            = useState("");
  const [method,            setMethod]            = useState("CASH");
  const [orNumber,          setOrNumber]          = useState("");
  const [referenceNo,       setReferenceNo]       = useState("");
  const [bankName,          setBankName]          = useState("");
  const [paymentReceivedOn, setPaymentReceivedOn] = useState("");
  const [notes,             setNotes]             = useState("");
  const [localErr,          setLocalErr]          = useState("");
  // Bank options pulled from Settings → Tuition — same loader the
  // tuition PayModal uses. Empty list renders the dropdown disabled
  // with a hint pointing at Settings.
  const [bankList,    setBankList]    = useState([]);

  // CASH receipts ride on the OR # alone; anything else (BANK / GCASH /
  // CARD / OTHER) is a digital channel and the bank-side reference id
  // is required. Mirrors the rule the tuition PayModal uses.
  const referenceRequired = method !== "CASH";

  const amountRef = useRef(null);

  // Reset every time the dialog opens — never carry stale text between
  // two charges the cashier hits in the same session.
  useEffect(() => {
    if (!open) return;
    setAmount(remaining > 0.005 ? remaining.toFixed(2) : "");
    setMethod("CASH");
    setOrNumber("");
    setReferenceNo("");
    setBankName("");
    // Default the receipt date to today so same-day entries need zero
    // clicks; backdating is one date-picker tweak away.
    const d = new Date();
    setPaymentReceivedOn(
      `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`
    );
    setNotes("");
    setLocalErr("");
    // Default focus to the amount field so a cashier collecting the
    // full remaining can just hit Enter.
    const t = setTimeout(() => amountRef.current?.select?.(), 0);
    return () => clearTimeout(t);
  }, [open, remaining]);

  // Lazy-load the bank list the first time the dialog opens. Cached
  // afterwards so flipping between charges doesn't re-fetch on every
  // open. Non-fatal: the dropdown just shows "no banks configured".
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadBankList().then(list => { if (!cancelled) setBankList(list); });
    return () => { cancelled = true; };
  }, [open]);

  // Esc → cancel; lock body scroll while open. Same hooks as ConfirmDialog.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!busy) onCancel?.();
      }
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onCancel]);

  if (!open || !charge) return null;

  const ALLOWED = ["CASH", "BANK", "GCASH", "CARD", "OTHER"];

  function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setLocalErr("");
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setLocalErr("Amount must be greater than 0.");
      return;
    }
    if (parsed - 0.005 > remaining) {
      setLocalErr(`Cannot exceed remaining ₱${fmtMoney(remaining)}.`);
      return;
    }
    if (!ALLOWED.includes(method)) {
      setLocalErr("Pick a payment method.");
      return;
    }
    // OR # — always required (school's audit trail).
    if (!orNumber.trim()) {
      setLocalErr("OR # is required.");
      return;
    }
    // Reference # — only required for non-cash methods.
    if (referenceRequired && !referenceNo.trim()) {
      setLocalErr(`Reference # is required for ${method} payments.`);
      return;
    }
    onConfirm?.({
      amount:             parsed.toFixed(2),
      method,
      orNumber:           orNumber.trim(),
      // For CASH, drop the digital reference entirely so the saved row
      // doesn't carry a stale value if the cashier toggled methods.
      referenceNo:        referenceRequired ? (referenceNo.trim() || null) : null,
      // Bank name is bank-transfer-only; the backend also nulls it for
      // any other method, but mirror the convention here so what gets
      // POSTed matches what the cashier saw at submit time.
      bankName:           method === "BANK" ? (bankName.trim() || null) : null,
      // Actual receipt date — backend treats empty / null as "no
      // backdating, default to today's date implicitly".
      paymentReceivedOn:  paymentReceivedOn || null,
      notes:              notes.trim() || null,
    });
  }

  const studentName = student
    ? `${student.lastName || ""}, ${student.firstName || ""}`.replace(/^, |, $/g, "")
    : "—";

  // Use createPortal so the dialog escapes the parent modal's transformed
  // ancestor and centers on the viewport — same pattern every other modal
  // in this file uses.
  return createPortal(
    <div className={s.modalOverlay}
         style={{ zIndex: 1110 }}>
      <div className={s.modal}
           onClick={e => e.stopPropagation()}
           style={{ maxWidth: 460 }}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>Record payment</h3>
          <button className={s.modalClose} type="button"
                  onClick={onCancel} aria-label="Close" disabled={busy}>×</button>
        </div>
        <div className={s.modalSub}>
          <div className={s.modalSubName}>{charge.label}</div>
          <div className={s.modalSubMeta}>
            <span>{studentName}</span>
            <span>· Amount ₱{fmtMoney(charge.amount)}</span>
            <span style={{ color: "#a8281c", fontWeight: 700 }}>
              · Remaining ₱{fmtMoney(remaining)}
            </span>
          </div>
        </div>
        <form onSubmit={submit} className={s.modalForm}>
          {(error || localErr) && (
            <div className={s.error}>{localErr || error}</div>
          )}
          <div className={s.field}>
            <label className={s.label}>
              Amount (₱) <span className={s.req}>*</span>
            </label>
            <input
              ref={amountRef}
              className={s.input}
              type="number"
              step="0.01"
              min="0.01"
              max={remaining.toFixed(2)}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={busy}
              required
              autoFocus
            />
            <div className={s.fieldHint}>
              Defaults to the remaining balance. Lower it for a partial
              payment — the rest stays UNPAID until later.
            </div>
          </div>
          <div className={s.field}>
            <label className={s.label}>Method <span className={s.req}>*</span></label>
            <select className={s.input}
                    value={method}
                    onChange={e => setMethod(e.target.value)}
                    disabled={busy}>
              <option value="CASH">Cash</option>
              <option value="BANK">Bank transfer</option>
              <option value="GCASH">GCash</option>
              <option value="CARD">Card</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          {/* Bank name only makes sense for a bank transfer. Dropdown-only
              (canonical list lives in Settings → Tuition) so history
              rows don't drift with typos. Mirrors the tuition PayModal
              pattern so cashiers see the same field across both flows. */}
          {method === "BANK" && (
            <div className={s.field}>
              <label className={s.label}>Bank name</label>
              <select
                className={s.input}
                value={bankName}
                onChange={e => setBankName(e.target.value)}
                disabled={busy || bankList.length === 0}>
                <option value="">
                  {bankList.length === 0 ? "— no banks configured —" : "— select a bank —"}
                </option>
                {bankList.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              {bankList.length === 0 && (
                <div className={s.fieldHint}>
                  Ask an admin to add banks in Settings → Tuition.
                </div>
              )}
            </div>
          )}
          {/* OR # — required for every payment. School's own audit
              trail; the channel reference below is the bank/wallet
              counterpart and only applies to non-cash methods. */}
          <div className={s.field}>
            <label className={s.label}>
              OR # <span className={s.req}>*</span>
            </label>
            <input
              className={s.input}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={orNumber}
              onChange={e => setOrNumber(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, ""))}
              placeholder="Official Receipt number (school tracker)"
              maxLength={64}
              disabled={busy}
              required
            />
            <div className={s.fieldHint}>
              The school's Official Receipt number — digits only, no leading zeros, required for every payment.
            </div>
          </div>

          {/* Reference # — required only for non-cash methods. The
              field still renders for CASH so a cashier with a redundant
              digital ref can capture it; submission isn't blocked when
              it's blank under CASH. */}
          <div className={s.field}>
            <label className={s.label}>
              Reference #{" "}
              {referenceRequired
                ? <span className={s.req}>*</span>
                : <span className={s.fieldOpt}>(optional · cash)</span>}
            </label>
            <input
              className={s.input}
              type="text"
              value={referenceNo}
              onChange={e => setReferenceNo(e.target.value)}
              placeholder={
                method === "CASH"  ? "Not required for cash"
              : method === "BANK"  ? "Deposit slip #"
              : method === "GCASH" ? "GCash reference #"
              : method === "CARD"  ? "Approval / auth #"
              :                      "Channel reference number"
              }
              maxLength={64}
              disabled={busy}
              required={referenceRequired}
            />
          </div>

          {/* Date received — the actual day the school collected this
              payment. Defaults to today; backdate if entering a
              previously-collected payment now. The system separately
              stamps paidAt so reports can distinguish "received Oct
              15, entered Nov 10". */}
          <div className={s.field}>
            <label className={s.label}>
              Date received <span className={s.fieldOpt}>(actual receipt date)</span>
            </label>
            <input
              className={s.input}
              type="date"
              value={paymentReceivedOn}
              onChange={e => setPaymentReceivedOn(e.target.value)}
              disabled={busy}
            />
            <div className={s.fieldHint}>
              Set to today by default. Change it to the actual date the school
              received this payment if you're entering it later.
            </div>
          </div>

          <div className={s.field}>
            <label className={s.label}>Notes <span className={s.fieldOpt}>(optional)</span></label>
            <textarea
              className={`${s.input} ${s.textarea}`}
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className={s.modalActions}>
            <button type="button" className={s.btnGhost}
                    onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className={s.btnPrimary}
                    disabled={busy || !orNumber.trim()
                              || (referenceRequired && !referenceNo.trim())}>
              {busy ? "Recording…" : "✓ Confirm Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/* Modal: show how an invoice's current amount is computed (fees + every
 * applied discount) together with the audit trail of past Recomputes.
 * Exposes Recompute action with a reason field and live delta preview so
 * admins see WHAT will change before committing. */
function InvoiceBreakdownModal({ invoice, student, semester, onClose, onRecompute, busy, error }) {
  const [breakdown, setBreakdown]   = useState(null);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState("");
  const [confirmOpen, setConfirm]   = useState(false);
  const [reason, setReason]         = useState("");

  async function loadAll() {
    setLoading(true); setLoadError("");
    try {
      const [bdRes, histRes] = await Promise.all([
        fetch(`${API_BASE}/api/tuition-invoices/${invoice.id}/breakdown`),
        fetch(`${API_BASE}/api/tuition-invoices/${invoice.id}/history`),
      ]);
      if (!bdRes.ok)   throw new Error(`Breakdown load failed (${bdRes.status})`);
      if (!histRes.ok) throw new Error(`History load failed (${histRes.status})`);
      const [bd, hist] = await Promise.all([bdRes.json(), histRes.json()]);
      setBreakdown(bd);
      setHistory(hist);
    } catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [invoice.id]);

  async function handleConfirm() {
    try {
      await onRecompute(invoice.id, reason);
      await loadAll();
      setConfirm(false); setReason("");
    } catch (_) { /* parent writes to `error` */ }
  }

  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : `Student #${invoice.studentId}`;

  const currentAmount = Number(invoice.amount) || 0;
  const computedTotal = breakdown ? Number(breakdown.total) || 0 : currentAmount;
  const delta = computedTotal - currentAmount;
  const willChange = Math.abs(delta) > 0.005;
  const paid = breakdown ? Number(breakdown.paid) || 0 : 0;
  const remainingAfter = computedTotal - paid;
  const willOverpay = remainingAfter < -0.005;

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={s.modalOverlay}>
      <div className={`${s.modal} ${s.modalWide}`} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>🧮 Invoice Breakdown</h3>
          <button className={s.modalClose} type="button" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.modalSub}>
          <div className={s.modalSubName}>{name}{semester?.label ? ` · ${semester.label}` : ""}</div>
          {student?.studentNumber && <div className={s.modalSubMeta}>#{student.studentNumber}</div>}
        </div>

        {loadError && <div className={s.error}>{loadError}</div>}
        {loading ? (
          <div className={s.empty}>Loading breakdown…</div>
        ) : breakdown && (
          <>
            <div className={s.historyLogHead}>Current Computation</div>
            <div className={s.breakdownBox}>
              {(() => {
                // Build the scope label ("→ Tuition + Misc + Books") shown
                // next to each discount so the reader can see WHICH components
                // each discount actually deducts from. Mirrors the chip we
                // already show in the discount manager.
                const scopeOf = (d) => {
                  const anyFlagSet = d.appliesTuition != null || d.appliesMisc != null || d.appliesBooks != null;
                  const t = anyFlagSet ? !!d.appliesTuition : true;
                  const m = anyFlagSet ? !!d.appliesMisc    : false;
                  const b = anyFlagSet ? !!d.appliesBooks   : false;
                  return [t && "Tuition", m && "Misc", b && "Books"].filter(Boolean).join(" + ") || "—";
                };
                // Per-component renderer. With no discount on this component
                // it falls back to the plain BreakdownRow layout so the visual
                // weight matches the rest of the modal. With a discount it
                // shows the original price struck through, the percentage, and
                // the discounted final.
                const componentRow = (label, original, pct, discounted) => {
                  const hasDiscount = Number(pct) > 0;
                  if (!hasDiscount) {
                    return <BreakdownRow label={label} value={original} s={s} />;
                  }
                  return (
                    <div className={s.breakdownRow}>
                      <span className={s.breakdownRowLabel}>
                        {label}
                        <span style={{
                          color: "#c9a227", marginLeft: 8,
                          fontSize: "0.78rem", fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          (−{Number(pct).toFixed(2)}%)
                        </span>
                      </span>
                      <span className={s.breakdownRowValue} style={{ display: "inline-flex", gap: 8, alignItems: "baseline" }}>
                        <span style={{
                          color: "#a39880", textDecoration: "line-through",
                          fontWeight: 500, fontSize: "0.82rem",
                        }}>
                          ₱{fmtMoney(original)}
                        </span>
                        <span style={{ color: "#1f6a2d", fontWeight: 800 }}>
                          ₱{fmtMoney(discounted)}
                        </span>
                      </span>
                    </div>
                  );
                };

                const all       = breakdown.discounts || [];
                const nonOthers = all.filter(d => d.type !== "OTHERS");
                const others    = all.filter(d => d.type === "OTHERS");
                const othersTotal = others.reduce(
                  (sum, d) => sum + (Number(d.percentage) || 0), 0);

                return (
                  <>
                    {all.length > 0 ? (
                      <>
                        <div className={s.breakdownSubHead}>Discounts applied</div>
                        {nonOthers.map(d => (
                          <div key={d.id} className={s.breakdownDiscount}>
                            <span style={{ minWidth: 0 }}>
                              <b>{d.type}</b>
                              <span className={s.breakdownDiscountNote} style={{ marginLeft: 8 }}>
                                → {scopeOf(d)}
                              </span>
                              {d.notes ? <span className={s.breakdownDiscountNote}> · {d.notes}</span> : null}
                            </span>
                            <span className={s.breakdownDiscountPct}>−{Number(d.percentage).toFixed(2)}%</span>
                          </div>
                        ))}
                        {others.length > 0 && (
                          <>
                            <div className={s.breakdownDiscount}>
                              <span>
                                <b>OTHERS</b>
                                <span className={s.breakdownDiscountNote} style={{ marginLeft: 6 }}>
                                  ({others.length} one-off{others.length === 1 ? "" : "s"})
                                </span>
                              </span>
                              <span className={s.breakdownDiscountPct}>−{othersTotal.toFixed(2)}%</span>
                            </div>
                            <div style={{
                              display: "grid", gap: 2,
                              margin: "2px 0 6px 14px",
                              paddingLeft: 12,
                              borderLeft: "2px solid #e8dfc9",
                            }}>
                              {others.map(d => (
                                <div key={d.id} style={{
                                  display: "flex", justifyContent: "space-between",
                                  alignItems: "baseline", gap: 10,
                                  padding: "3px 4px",
                                  fontSize: "0.82rem", color: "#5a4f3d",
                                }}>
                                  <span style={{ minWidth: 0, overflow: "hidden",
                                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <span aria-hidden="true" style={{ color: "#b8a674", marginRight: 6 }}>↳</span>
                                    {d.customName
                                      ? d.customName
                                      : <em style={{ color: "#a39880" }}>(untitled)</em>}
                                    <span style={{ color: "#8a8070" }}> → {scopeOf(d)}</span>
                                    {d.notes ? <span style={{ color: "#8a8070" }}> · {d.notes}</span> : null}
                                  </span>
                                  <span style={{ fontVariantNumeric: "tabular-nums",
                                                 color: "#a8862c", fontWeight: 700, flexShrink: 0 }}>
                                    −{Number(d.percentage).toFixed(2)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        <div className={s.breakdownSubHead} style={{ marginTop: 6 }}>After discounts</div>
                      </>
                    ) : (
                      <div className={s.breakdownSubHead}>No discounts on file for this student + school year</div>
                    )}

                    {componentRow("Tuition",       breakdown.tuitionFee, breakdown.tuitionDiscountPercent, breakdown.discountedTuition)}
                    {componentRow("Miscellaneous", breakdown.miscFee,    breakdown.miscDiscountPercent,    breakdown.discountedMisc)}
                    {componentRow("Books",         breakdown.booksFee,   breakdown.booksDiscountPercent,   breakdown.discountedBooks)}

                    <div className={s.breakdownTotalRow}>
                      <span>Computed total</span>
                      <span>₱{fmtMoney(breakdown.total)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div className={s.breakdownDeltaBox}>
              <div className={s.breakdownDeltaRow}>
                <span className={s.breakdownDeltaLabel}>Invoice amount today</span>
                <span className={s.breakdownDeltaValue}>₱{fmtMoney(currentAmount)}</span>
              </div>
              <div className={s.breakdownDeltaRow}>
                <span className={s.breakdownDeltaLabel}>Computed now</span>
                <span className={s.breakdownDeltaValue}>₱{fmtMoney(computedTotal)}</span>
              </div>
              <div className={`${s.breakdownDeltaRow} ${willChange ? s.breakdownDeltaChanged : s.breakdownDeltaSame}`}>
                <span className={s.breakdownDeltaLabel}>Difference</span>
                <span className={s.breakdownDeltaValue}>
                  {willChange
                    ? `${delta > 0 ? "+" : "−"}₱${fmtMoney(Math.abs(delta))}`
                    : "No change"}
                </span>
              </div>
              {paid > 0 && (
                <div className={s.breakdownDeltaRow}>
                  <span className={s.breakdownDeltaLabel}>Paid so far (won&apos;t change)</span>
                  <span className={s.breakdownDeltaValue}>₱{fmtMoney(paid)}</span>
                </div>
              )}
              {willOverpay && (
                <div className={s.breakdownWarn}>
                  ⚠ Student will be overpaid by <b>₱{fmtMoney(-remainingAfter)}</b> after this change.
                  Issue a refund from the Payments tab.
                </div>
              )}
            </div>

            <div className={s.historyLogHead}>Adjustment History</div>
            {history.length === 0 ? (
              <div className={s.empty}>No adjustments yet — this invoice still holds its original amount.</div>
            ) : (
              <div className={s.historyList} style={{ maxHeight: "none", overflowY: "visible" }}>
                {history.map(h => {
                  const diff = Number(h.newAmount) - Number(h.oldAmount);
                  return (
                    <div key={h.id} className={s.historyItem}>
                      <div className={`${s.historyDot} ${diff < 0 ? s.historyDotReversal : ""}`} />
                      <div className={s.historyBody}>
                        <div className={s.historyItemTop}>
                          <span className={s.historyAmount}>
                            ₱{fmtMoney(h.oldAmount)} → ₱{fmtMoney(h.newAmount)}
                            <span className={s.historyDelta}>
                              {" "}({diff > 0 ? "+" : "−"}₱{fmtMoney(Math.abs(diff))})
                            </span>
                          </span>
                          <span className={s.historyWhen}>
                            {h.createdAt ? new Date(h.createdAt).toLocaleString() : "—"}
                          </span>
                        </div>
                        {h.reason && <div className={s.historyNote}>{h.reason}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {confirmOpen ? (
              <div className={s.confirmBox}>
                {error && <div className={s.error}>{error}</div>}
                <div className={s.confirmQuestion}>
                  {willChange
                    ? `Recompute will change amount by ${delta > 0 ? "+" : "−"}₱${fmtMoney(Math.abs(delta))}.`
                    : "Amount won't change, but a history row will still be recorded only if the amount moves."}
                </div>
                <div className={s.field}>
                  <label className={s.label}>Reason <span className={s.fieldOpt}>(optional)</span></label>
                  <textarea
                    className={`${s.input} ${s.textarea}`}
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Applied new sibling discount"
                  />
                </div>
                <div className={s.modalActions}>
                  <button type="button" className={s.btnGhost} disabled={busy}
                          onClick={() => { setConfirm(false); setReason(""); }}>
                    Cancel
                  </button>
                  <button type="button" className={s.btnPrimary} disabled={busy}
                          onClick={handleConfirm}>
                    {busy ? "Recomputing…" : "✓ Confirm Recompute"}
                  </button>
                </div>
              </div>
            ) : (
              <div className={s.modalActions}>
                <button type="button" className={s.btnGhost} onClick={onClose}>Close</button>
                <button type="button" className={s.btnPrimary}
                        onClick={() => setConfirm(true)}>
                  ↻ Recompute
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* Small helper for the single-line rows inside the breakdown computation. */
function BreakdownRow({ label, value, strongValue, s }) {
  return (
    <div className={s.breakdownRow}>
      <span className={s.breakdownRowLabel}>{label}</span>
      <span className={`${s.breakdownRowValue} ${strongValue ? s.breakdownRowStrong : ""}`}>
        ₱{fmtMoney(value)}
      </span>
    </div>
  );
}

/* Modal: bulk Recompute for every invoice matching the active filter (or all
 * invoices if no filter set). Shares a single `reason` across all adjustment
 * rows it writes. */
function RecomputeAllModal({ syFilter, semFilter, schoolYears, semesters, totalRows, filteredRows, onClose, onSubmit, busy, error }) {
  const [reason, setReason] = useState("");
  const [result, setResult] = useState(null);

  const syLabel = syFilter
    ? (schoolYears.find(y => String(y.id) === String(syFilter))?.label || `#${syFilter}`)
    : null;
  const semLabel = semFilter
    ? (semesters.find(sm => String(sm.id) === String(semFilter))?.label || `#${semFilter}`)
    : null;

  async function handleSubmit() {
    try {
      const r = await onSubmit(reason);
      setResult(r);
    } catch (_) { /* error is on parent */ }
  }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !busy) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return (
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>↻ Regenerate All Invoices</h3>
          <button className={s.modalClose} type="button" onClick={onClose}
                  disabled={busy} aria-label="Close">×</button>
        </div>

        {result ? (
          <>
            <div className={s.modalSub}>
              <div className={s.modalSubName}>Done</div>
            </div>
            <div className={s.breakdownBox}>
              <div className={s.breakdownRow}>
                <span className={s.breakdownRowLabel}>Amount changed</span>
                <span className={s.breakdownRowValue}><b>{result.changed}</b></span>
              </div>
              <div className={s.breakdownRow}>
                <span className={s.breakdownRowLabel}>Unchanged (already correct)</span>
                <span className={s.breakdownRowValue}>{result.unchanged}</span>
              </div>
              <div className={s.breakdownRow}>
                <span className={s.breakdownRowLabel}>Skipped (missing fee structure / section)</span>
                <span className={s.breakdownRowValue}>{result.skipped}</span>
              </div>
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.btnPrimary} onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div className={s.modalSub}>
              <div className={s.modalSubName}>
                This will recompute{" "}
                <b>{syFilter || semFilter ? filteredRows : totalRows}</b>
                {" "}invoice{((syFilter || semFilter ? filteredRows : totalRows) === 1) ? "" : "s"}.
              </div>
              <div className={s.modalSubMeta}>
                Scope: {syLabel  ? <b>{syLabel}</b>  : "all school years"}
                {semLabel ? <> · <b>{semLabel}</b></> : null}
              </div>
              <div className={s.modalSubRow}>
                <span>
                  Each invoice's amount is recomputed from its fee structure and discounts.
                  A history row is written only for invoices whose amount actually moves.
                  Payments are never modified.
                </span>
              </div>
            </div>

            {error && <div className={s.error}>{error}</div>}

            <div className={s.field}>
              <label className={s.label}>
                Reason <span className={s.fieldOpt}>(recorded on every adjustment)</span>
              </label>
              <textarea
                className={`${s.input} ${s.textarea}`}
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. End-of-year discount audit"
              />
            </div>

            <div className={s.modalActions}>
              <button type="button" className={s.btnGhost} disabled={busy} onClick={onClose}>Cancel</button>
              <button type="button" className={s.btnPrimary} disabled={busy} onClick={handleSubmit}>
                {busy ? "Recomputing…" : "✓ Confirm Regenerate"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* Card used in Payments' grouped views. Resolves student via the payment's
 * invoiceId so the card shows who paid without a lookup table in the caller. */
function PaymentCard({ payment, invoice, studentById, semesterById, onEdit, onDelete, s }) {
  const student = invoice ? studentById[invoice.studentId] : null;
  const semester = invoice ? semesterById[invoice.semesterId] : null;
  const name = student
    ? `${student.lastName}, ${student.firstName}`
    : invoice ? `Student #${invoice.studentId}` : `Invoice #${payment.invoiceId}`;
  return (
    <div className={s.recordCard}>
      <div className={s.recordCardTop}>
        <div className={s.recordName}>{name}</div>
        <span className={s.methodBadge}>{payment.method || "—"}</span>
      </div>
      <div className={s.recordMeta}>
        {student?.studentNumber && <span>{student.studentNumber}</span>}
        {semester?.label && <span>· {semester.label}</span>}
        {payment.referenceNo && <span>· Ref {payment.referenceNo}</span>}
      </div>
      <div className={s.recordFigures}>
        <span className={s.recordAmount}>{Number(payment.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
        {payment.paidAt && (
          <span className={s.recordDue}>{new Date(payment.paidAt).toLocaleDateString()}</span>
        )}
      </div>
      <div className={s.recordActions}>
        <button type="button" className={s.btnGhost}  onClick={onEdit}>Edit</button>
        <button type="button" className={s.btnDanger} onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

function FormField({ field, value, onChange, lookupData, lookups }) {
  if (field.type === "select") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          {field.options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "lookup") {
    const list = lookupData || [];
    const labelKey = LOOKUPS[field.lookup].label;
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          <option value="">— select —</option>
          {list.map(r => <option key={r.id} value={r.id}>{r[labelKey]}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "lookupStudent") {
    const list = lookupData || [];
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          <option value="">— select —</option>
          {list.map(r => <option key={r.id} value={r.id}>{r.lastName}, {r.firstName} ({r.studentNumber})</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "lookupInvoice") {
    const list = lookupData || [];
    const semList = lookups.semesters || [];
    const stuList = lookups.students || [];
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <select className={s.input} value={value ?? ""} onChange={e => onChange(e.target.value)} required={field.required}>
          <option value="">— select —</option>
          {list.map(inv => {
            const st = stuList.find(x => x.id === inv.studentId);
            const sm = semList.find(x => x.id === inv.semesterId);
            return (
              <option key={inv.id} value={inv.id}>
                {st ? `${st.lastName}, ${st.firstName}` : `Student #${inv.studentId}`} · {sm?.label || ""} · {inv.amount} ({inv.status})
              </option>
            );
          })}
        </select>
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div className={s.field}>
        <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
        <DatePicker
          value={value || ""}
          onChange={v => onChange(v)}
          placeholder={field.placeholder || "MM/DD/YYYY"}
        />
      </div>
    );
  }
  return (
    <div className={s.field}>
      <label className={s.label}>{field.label}{field.required && <span className={s.req}> *</span>}</label>
      <input
        className={`${s.input} ${field.readOnly ? s.inputReadOnly : ""}`}
        type={field.type}
        value={value ?? ""}
        placeholder={field.placeholder || ""}
        onChange={e => onChange(e.target.value)}
        required={field.required && !field.readOnly}
        readOnly={field.readOnly || undefined}
        tabIndex={field.readOnly ? -1 : undefined}
        step={field.type === "number" ? "0.01" : undefined}
      />
    </div>
  );
}

/** Stroke-based SVG icons for the row-action buttons (info / edit / delete).
 *  Defined at module scope so they don't re-create on every render. Sized via
 *  currentColor so they inherit the button text colour (red for danger, etc). */
const IconInfo = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);
const IconEdit = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4l10.5-10.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4Z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);
const IconTrash = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
    <path d="M10 11v7" />
    <path d="M14 11v7" />
  </svg>
);

/** Per-student Discounts manager. Consolidates every discount row for one
 *  (student, schoolYear) pair into one place: list on top, add-form below.
 *  For the "+ New" entry point the student + SY are selectable until the
 *  first discount is saved; after that they become read-only for the rest
 *  of the session — lines always belong to one pair.
 *
 *  Edit flow lives in a nested popup (not inline) so the row list stays
 *  uncluttered and the edit context is unambiguous. */
function StudentDiscountsModal({
  isNew, initialStudentId, initialSchoolYearId,
  students, schoolYears, rows, discountTypes,
  onClose, onChanged, apiBase, s,
}) {
  const [studentId,    setStudentId]    = useState(initialStudentId || "");
  const [schoolYearId, setSchoolYearId] = useState(initialSchoolYearId || "");
  const [localRows,    setLocalRows]    = useState([]);
  // Default scope: Tuition only — matches the pre-flags math, so admins who
  // don't touch the checkboxes see the same behaviour as before.
  const BLANK = {
    type: discountTypes[0][0], percentage: "", notes: "", customName: "",
    linkedStudentId: "",
    appliesTuition: true, appliesMisc: false, appliesBooks: false,
  };
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");
  const [locked, setLocked] = useState(!isNew);
  // Row being edited. null = composing a new discount; row.id otherwise.
  const [editingId, setEditingId] = useState(null);
  // Row whose details popup is open (i icon). null = closed.
  const [detailsRow, setDetailsRow] = useState(null);
  // "Manage Referrals / Siblings" group modal. Holds the type string while open.
  const [groupModal, setGroupModal] = useState(null);
  const BLANK_GROUP = { linkedStudentId: "", percentage: "", notes: "",
                        appliesTuition: true, appliesMisc: false, appliesBooks: false };
  const [groupForm, setGroupForm]   = useState(BLANK_GROUP);
  const [groupBusy, setGroupBusy]   = useState(false);
  const [groupErr,  setGroupErr]    = useState("");

  // Quick lookup for the linked-student name/number on the row chips and
  // details popup — avoids re-scanning `students` per render.
  const studentByIdLocal = useMemo(() => {
    const m = {};
    for (const st of students) m[st.id] = st;
    return m;
  }, [students]);

  const isLinkedType = (t) => t === "SIBLING" || t === "REFERRAL";

  // Whenever the parent rows refresh (after a save/delete), resync our local
  // list for the currently-selected pair so the UI reflects the latest state.
  useEffect(() => {
    if (!studentId || !schoolYearId) { setLocalRows([]); return; }
    setLocalRows(rows.filter(r =>
      String(r.studentId) === String(studentId)
      && String(r.schoolYearId) === String(schoolYearId)
    ));
  }, [rows, studentId, schoolYearId]);

  const totalPct = localRows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
  const student = students.find(st => String(st.id) === String(studentId));
  const sy      = schoolYears.find(y  => String(y.id)  === String(schoolYearId));

  // Set of discount types already present for this (student, SY). Used to
  // disable duplicate picks in the type dropdown. OTHERS is exempt — each
  // "one-off" is a distinct label by design. The currently-editing row's
  // own type is also allowed so the admin can tweak it without clearing.
  const takenTypes = useMemo(() => new Set(
    localRows
      .filter(r => !MULTI_USE_TYPES.has(r.type) && r.id !== editingId)
      .map(r => r.type)
  ), [localRows, editingId]);

  // Set of student IDs that are already the referred student on ANY other
  // student's REFERRAL discount in this same school year. A student can only
  // be referred once — the dropdown and save-guard both use this set.
  const globalReferredIds = useMemo(() => {
    const ids = new Set();
    for (const r of rows) {
      if (r.type === "REFERRAL"
          && r.linkedStudentId != null
          && String(r.schoolYearId) === String(schoolYearId)
          && String(r.studentId) !== String(studentId)) {
        ids.add(String(r.linkedStudentId));
      }
    }
    return ids;
  }, [rows, schoolYearId, studentId]);

  // If the composer's picked type just became taken (e.g. admin saved it in
  // another window, or we switched rows), flip to the first still-available
  // type so the form doesn't start invalid.
  useEffect(() => {
    if (editingId) return;
    if (MULTI_USE_TYPES.has(form.type)) return;
    if (!takenTypes.has(form.type)) return;
    const next = discountTypes.find(([v]) => MULTI_USE_TYPES.has(v) || !takenTypes.has(v));
    if (next) setForm(f => ({ ...f, type: next[0] }));
  }, [takenTypes, editingId, form.type, discountTypes]);

  function openGroupModal(type) {
    setGroupModal(type);
    setGroupForm(BLANK_GROUP);
    setGroupErr("");
  }

  async function saveGroupRow(e) {
    e.preventDefault();
    setGroupErr("");
    if (!groupForm.linkedStudentId) {
      setGroupErr(groupModal === "SIBLING" ? "Pick the sibling." : "Pick the referred student.");
      return;
    }
    if (String(groupForm.linkedStudentId) === String(studentId)) {
      setGroupErr("Must be a different student from the one receiving the discount.");
      return;
    }
    const pct = Number(groupForm.percentage);
    if (!Number.isFinite(pct) || pct <= 0) { setGroupErr("Percentage must be greater than 0."); return; }
    if (!groupForm.appliesTuition && !groupForm.appliesMisc && !groupForm.appliesBooks) {
      setGroupErr("Pick at least one fee (Tuition / Misc / Books).");
      return;
    }
    const dup = localRows.some(r =>
      r.type === groupModal && String(r.linkedStudentId) === String(groupForm.linkedStudentId)
    );
    if (dup) {
      setGroupErr(groupModal === "SIBLING"
        ? "This sibling is already listed."
        : "This referred student is already listed.");
      return;
    }
    if (groupModal === "REFERRAL" && globalReferredIds.has(String(groupForm.linkedStudentId))) {
      setGroupErr("This student has already been referred by another student this school year. A student can only be referred once.");
      return;
    }
    setGroupBusy(true);
    try {
      const body = {
        studentId: Number(studentId), schoolYearId: Number(schoolYearId),
        type: groupModal, percentage: pct,
        notes: groupForm.notes || null,
        linkedStudentId: Number(groupForm.linkedStudentId),
        appliesTuition: !!groupForm.appliesTuition,
        appliesMisc:    !!groupForm.appliesMisc,
        appliesBooks:   !!groupForm.appliesBooks,
      };
      const res = await fetch(`${apiBase}/api/student-discounts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setGroupForm(BLANK_GROUP);
      setLocked(true);
      await onChanged();
    } catch (e2) { setGroupErr(e2.message); }
    finally { setGroupBusy(false); }
  }

  function startEdit(row) {
    setGroupModal(null);
    setEditingId(row.id);
    setErr("");
    // Legacy rows predate the applies_* flags — fall back to Tuition-only
    // (same default the compute math uses for null flags) so the checkboxes
    // reflect the effective behaviour.
    const anyFlagSet = row.appliesTuition != null || row.appliesMisc != null || row.appliesBooks != null;
    setForm({
      type:           row.type,
      percentage:     String(row.percentage ?? ""),
      notes:          row.notes      || "",
      customName:     row.customName || "",
      linkedStudentId: row.linkedStudentId != null ? String(row.linkedStudentId) : "",
      appliesTuition: anyFlagSet ? !!row.appliesTuition : true,
      appliesMisc:    anyFlagSet ? !!row.appliesMisc    : false,
      appliesBooks:   anyFlagSet ? !!row.appliesBooks   : false,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setErr("");
    // Snap type back to the first available (not a taken one). BLANK's default
    // might itself be taken now.
    const first = discountTypes.find(([v]) => v === "OTHERS" || !takenTypes.has(v));
    setForm({ ...BLANK, type: first ? first[0] : BLANK.type });
  }

  async function saveDiscount(e) {
    e.preventDefault();
    setErr("");
    if (!studentId || !schoolYearId) { setErr("Pick a student and school year first."); return; }
    const pct = Number(form.percentage);
    if (!Number.isFinite(pct) || pct <= 0) { setErr("Percentage must be greater than 0."); return; }
    if (!form.appliesTuition && !form.appliesMisc && !form.appliesBooks) {
      setErr("Pick at least one fee this discount applies to (Tuition / Misc / Books).");
      return;
    }
    if (form.type === "OTHERS" && !form.customName.trim()) {
      setErr("Give this Others discount a custom name (e.g. \"Early Bird\").");
      return;
    }
    if (form.type === "OTHERS") {
      const name = form.customName.trim().toLowerCase();
      const dup = localRows.some(r =>
        r.id !== editingId
        && r.type === "OTHERS"
        && (r.customName || "").trim().toLowerCase() === name
      );
      if (dup) {
        setErr(`An Others discount named "${form.customName.trim()}" already exists for this student and school year.`);
        return;
      }
    }
    if (isLinkedType(form.type)) {
      if (!form.linkedStudentId) {
        setErr(form.type === "SIBLING"
          ? "Pick the sibling enrolled in the school."
          : "Pick the enrolled student who made the referral.");
        return;
      }
      if (String(form.linkedStudentId) === String(studentId)) {
        setErr("Linked student must be different from the discounted student.");
        return;
      }
      // Prevent adding the same sibling / referrer twice for this student+SY.
      const dupLinked = localRows.some(r =>
        r.id !== editingId
        && r.type === form.type
        && String(r.linkedStudentId) === String(form.linkedStudentId)
      );
      if (dupLinked) {
        setErr(form.type === "SIBLING"
          ? "This sibling discount is already recorded for this student and school year."
          : "This referral discount is already recorded for this student and school year.");
        return;
      }
    }
    // Belt-and-braces for new additions only — editing an existing row of a
    // type that also has other rows (e.g. second REFERRAL row) is fine.
    if (!editingId && !MULTI_USE_TYPES.has(form.type) && takenTypes.has(form.type)) {
      setErr("That discount type is already added for this student and school year.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        studentId: Number(studentId),
        schoolYearId: Number(schoolYearId),
        type: form.type,
        percentage: pct,
        notes: form.notes || null,
        customName: form.type === "OTHERS" ? form.customName.trim() : null,
        linkedStudentId: isLinkedType(form.type) ? Number(form.linkedStudentId) : null,
        appliesTuition: !!form.appliesTuition,
        appliesMisc:    !!form.appliesMisc,
        appliesBooks:   !!form.appliesBooks,
      };
      const url = editingId
        ? `${apiBase}/api/student-discounts/${editingId}`
        : `${apiBase}/api/student-discounts`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setEditingId(null);
      setForm(BLANK);
      setLocked(true);
      await onChanged();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  async function deleteRow(row) {
    if (!window.confirm(`Remove ${row.type} discount?`)) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${apiBase}/api/student-discounts/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      // Deleting the row we're editing would leave the form pointing at a
      // ghost id — drop back to create mode.
      if (editingId === row.id) {
        setEditingId(null);
        setForm(BLANK);
      }
      await onChanged();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  }

  const title = locked && student
    ? `Discounts — ${student.lastName}, ${student.firstName}${sy ? ` · ${sy.label}` : ""}`
    : (isNew ? "Add discount" : "Discounts");

  // Close on Escape — only the topmost nested dialog (group modal > edit
  // dialog > details popup > this outer modal) responds; each one below
  // has its own matching effect further down.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && !groupModal && !editingId && !detailsRow && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy, groupModal, editingId, detailsRow]);

  // Nested "Manage Referrals / Siblings" dialog.
  useEffect(() => {
    if (!groupModal) return;
    function onKey(e) { if (e.key === "Escape" && !groupBusy) setGroupModal(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [groupModal, groupBusy]);

  // Nested "Edit discount" dialog.
  useEffect(() => {
    if (!editingId) return;
    function onKey(e) { if (e.key === "Escape" && !busy) cancelEdit(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [editingId, busy]);

  // Nested discount-details popup.
  useEffect(() => {
    if (!detailsRow) return;
    function onKey(e) { if (e.key === "Escape") setDetailsRow(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detailsRow]);

  return (
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>{title}</h3>
          <button className={s.modalClose} type="button" onClick={onClose}
                  disabled={busy} aria-label="Close">×</button>
        </div>

        {err && <div className={s.error}>{err}</div>}

        {!locked && (
          <div className={s.form} style={{ marginBottom: 16 }}>
            <div className={s.field}>
              <label className={s.label}>Student *</label>
              <select className={s.input} value={studentId}
                      onChange={e => setStudentId(e.target.value)}>
                <option value="">— select —</option>
                {students.map(st => (
                  <option key={st.id} value={st.id}>
                    {st.lastName}, {st.firstName} {st.studentNumber ? `(${st.studentNumber})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className={s.field}>
              <label className={s.label}>School year *</label>
              <select className={s.input} value={schoolYearId}
                      onChange={e => setSchoolYearId(e.target.value)}>
                <option value="">— select —</option>
                {schoolYears.map(y => (
                  <option key={y.id} value={y.id}>{y.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {studentId && schoolYearId && (
          <div className={s.breakdownBox}>
            {localRows.length === 0 ? (
              <div className={s.emptyRow} style={{ padding: 12 }}>
                No discounts yet — add the first one below.
              </div>
            ) : (() => {
              const iconBtn = {
                width: 28, height: 28, padding: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                lineHeight: 1,
              };
              const referralRows = localRows.filter(r => r.type === "REFERRAL");
              const siblingRows  = localRows.filter(r => r.type === "SIBLING");
              const singleRows   = localRows.filter(r => r.type !== "REFERRAL" && r.type !== "SIBLING");

              const renderGroupBlock = (type, label, groupRows) => {
                if (groupRows.length === 0) return null;
                const groupPct = groupRows.reduce((s, r) => s + (Number(r.percentage) || 0), 0);
                return (
                  <div key={type}>
                    <div className={s.breakdownRow} style={{ alignItems: "center", gap: 10 }}>
                      <span className={s.breakdownRowLabel}
                            style={{ flex: 1, minWidth: 0, display: "block" }}>
                        <b>{label}</b>
                        <span style={{ color: "#8a8070", fontSize: "0.75rem", marginLeft: 6 }}>
                          {groupRows.length} {type === "REFERRAL" ? "referral" : "sibling"}{groupRows.length !== 1 ? "s" : ""}
                        </span>
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                        <span className={s.breakdownRowValue} style={{ minWidth: 44, textAlign: "right" }}>
                          {groupPct}%
                        </span>
                        <button type="button" className={s.btnGhost}
                                title={`Manage ${label}s`} aria-label={`Manage ${label}s`}
                                style={iconBtn}
                                onClick={() => openGroupModal(type)} disabled={busy}>
                          <IconEdit />
                        </button>
                      </span>
                    </div>
                    {groupRows.map(r => {
                      const linkedSt = studentByIdLocal[r.linkedStudentId];
                      const anyFlagSet = r.appliesTuition != null || r.appliesMisc != null || r.appliesBooks != null;
                      const appT = anyFlagSet ? !!r.appliesTuition : true;
                      const appM = anyFlagSet ? !!r.appliesMisc    : false;
                      const appB = anyFlagSet ? !!r.appliesBooks   : false;
                      const scope = [appT && "Tuition", appM && "Misc", appB && "Books"].filter(Boolean).join(" + ");
                      const stName = linkedSt
                        ? `${linkedSt.lastName}, ${linkedSt.firstName}`
                        : (r.linkedStudentId ? `Student #${r.linkedStudentId}` : "—");
                      return (
                        <div key={r.id} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "3px 6px 3px 16px",
                          borderLeft: "2px solid #f0ebe4", marginLeft: 8, marginBottom: 2,
                        }}>
                          <span style={{ flex: 1, fontSize: "0.8rem", color: "#5a4e3a", minWidth: 0,
                                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            → {stName}
                            <span style={{ color: "#8a8070" }}> · {scope}</span>
                            {r.notes && <span style={{ color: "#a39880" }}> · {r.notes}</span>}
                          </span>
                          <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#4a6d55",
                                         minWidth: 34, textAlign: "right", flexShrink: 0 }}>
                            {Number(r.percentage) || 0}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              };

              return (
                <>
                  {renderGroupBlock("REFERRAL", "Referral", referralRows)}
                  {renderGroupBlock("SIBLING",  "Sibling",  siblingRows)}
                  {singleRows.map(r => {
                    const lbl = (discountTypes.find(([v]) => v === r.type) || [])[1] || r.type;
                    const anyFlagSet = r.appliesTuition != null || r.appliesMisc != null || r.appliesBooks != null;
                    const appT = anyFlagSet ? !!r.appliesTuition : true;
                    const appM = anyFlagSet ? !!r.appliesMisc    : false;
                    const appB = anyFlagSet ? !!r.appliesBooks   : false;
                    const scope = [appT && "Tuition", appM && "Misc", appB && "Books"].filter(Boolean).join(" + ");
                    const nameParts = r.type === "OTHERS" && r.customName ? `${lbl} · ${r.customName}` : lbl;
                    return (
                      <div key={r.id} className={s.breakdownRow} style={{ alignItems: "center", gap: 10 }}>
                        <span className={s.breakdownRowLabel} style={{ flex: 1, minWidth: 0, display: "block" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <b style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {nameParts}
                            </b>
                          </div>
                          <div style={{ color: "#8a8070", fontSize: "0.78rem", marginTop: 2,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            → {scope || "—"}
                            {r.notes ? <span style={{ color: "#a39880" }}> · {r.notes}</span> : null}
                          </div>
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <span className={s.breakdownRowValue} style={{ minWidth: 44, textAlign: "right" }}>
                            {Number(r.percentage) || 0}%
                          </span>
                          <button type="button" className={s.btnGhost}
                                  title="Details" aria-label="Details"
                                  style={iconBtn} onClick={() => setDetailsRow(r)} disabled={busy}>
                            <IconInfo />
                          </button>
                          <button type="button" className={s.btnGhost}
                                  title="Edit" aria-label="Edit"
                                  style={iconBtn} onClick={() => startEdit(r)} disabled={busy}>
                            <IconEdit />
                          </button>
                          <button type="button" className={s.btnDanger}
                                  title="Remove" aria-label="Remove"
                                  style={iconBtn} onClick={() => deleteRow(r)} disabled={busy}>
                            <IconTrash />
                          </button>
                        </span>
                      </div>
                    );
                  })}
                  <div className={s.breakdownRow}>
                    <span className={s.breakdownRowLabel}><b>Total</b></span>
                    <span className={s.breakdownRowValue}><b>{totalPct}%</b></span>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Manage Referrals / Siblings group modal */}
        {groupModal && createPortal(
          <div className={s.modalOverlay} style={{ zIndex: 1100 }}>
            <div className={s.modal} style={{ maxWidth: 520 }}
                 onClick={e => e.stopPropagation()}>
              <div className={s.modalHead}>
                <h3 className={s.modalTitle}>
                  {groupModal === "REFERRAL" ? "Manage Referrals" : "Manage Siblings"}
                </h3>
                <button className={s.modalClose} type="button"
                        onClick={() => setGroupModal(null)} disabled={groupBusy}>×</button>
              </div>

              {/* Existing linked rows */}
              {(() => {
                const groupRows = localRows.filter(r => r.type === groupModal);
                const smallBtn = {
                  width: 26, height: 26, padding: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                };
                return groupRows.length === 0 ? (
                  <div style={{ color: "#8a8070", fontSize: "0.84rem", padding: "4px 2px" }}>
                    None added yet — use the form below.
                  </div>
                ) : (
                  <div className={s.breakdownBox} style={{ marginBottom: 8 }}>
                    {groupRows.map(r => {
                      const linkedSt = studentByIdLocal[r.linkedStudentId];
                      const stName = linkedSt
                        ? `${linkedSt.lastName}, ${linkedSt.firstName}${linkedSt.studentNumber ? ` (${linkedSt.studentNumber})` : ""}`
                        : (r.linkedStudentId ? `Student #${r.linkedStudentId}` : "—");
                      return (
                        <div key={r.id} className={s.breakdownRow}
                             style={{ alignItems: "center", gap: 8 }}>
                          <span style={{ flex: 1, fontSize: "0.85rem", minWidth: 0,
                                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {stName}
                          </span>
                          <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "#4a6d55",
                                         minWidth: 36, textAlign: "right", flexShrink: 0 }}>
                            {Number(r.percentage) || 0}%
                          </span>
                          <button type="button" className={s.btnGhost} title="Edit" aria-label="Edit"
                                  style={smallBtn}
                                  onClick={() => { setGroupModal(null); startEdit(r); }}
                                  disabled={groupBusy || busy}>
                            <IconEdit />
                          </button>
                          <button type="button" className={s.btnDanger} title="Remove" aria-label="Remove"
                                  style={smallBtn}
                                  onClick={() => deleteRow(r)}
                                  disabled={groupBusy || busy}>
                            <IconTrash />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {groupErr && <div className={s.error}>{groupErr}</div>}

              {/* Add sub-form */}
              <form onSubmit={saveGroupRow}>
                <div className={s.field} style={{ marginBottom: 10 }}>
                  <label className={s.label}>
                    {groupModal === "REFERRAL" ? "Referred student (enrolled) *" : "Sibling (enrolled) *"}
                  </label>
                  <select className={s.input} value={groupForm.linkedStudentId}
                          onChange={e => setGroupForm(f => ({ ...f, linkedStudentId: e.target.value }))}>
                    <option value="">— select enrolled student —</option>
                    {students
                      .filter(st =>
                        String(st.id) !== String(studentId) &&
                        !localRows.some(r => r.type === groupModal && String(r.linkedStudentId) === String(st.id)) &&
                        (groupModal !== "REFERRAL" || !globalReferredIds.has(String(st.id)))
                      )
                      .map(st => (
                        <option key={st.id} value={st.id}>
                          {st.lastName}, {st.firstName}{st.studentNumber ? ` (${st.studentNumber})` : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>
                  <div className={s.field}>
                    <label className={s.label}>Percentage (0-100) *</label>
                    <input className={s.input} type="number" step="0.01" min="0" max="100"
                           value={groupForm.percentage}
                           onChange={e => setGroupForm(f => ({ ...f, percentage: e.target.value }))}
                           placeholder="9" />
                  </div>
                  <div className={s.field}>
                    <label className={s.label}>Applies to *</label>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingTop: 6 }}>
                      {[["appliesTuition","Tuition"],["appliesMisc","Misc"],["appliesBooks","Books"]].map(([k, l]) => (
                        <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4,
                                                cursor: "pointer", fontSize: "0.84rem" }}>
                          <input type="checkbox" checked={!!groupForm[k]}
                                 onChange={e => setGroupForm(f => ({ ...f, [k]: e.target.checked }))} />
                          {l}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={s.field} style={{ marginTop: 8 }}>
                  <label className={s.label}>Notes (optional)</label>
                  <input className={s.input} type="text"
                         value={groupForm.notes}
                         onChange={e => setGroupForm(f => ({ ...f, notes: e.target.value }))}
                         placeholder="optional" />
                </div>
                <div className={s.modalActions} style={{ marginTop: 12 }}>
                  <button type="button" className={s.btnGhost}
                          onClick={() => setGroupModal(null)} disabled={groupBusy}>Close</button>
                  <button type="submit" className={s.btnPrimary}
                          disabled={groupBusy || !groupForm.linkedStudentId}>
                    {groupBusy
                      ? "Adding…"
                      : `Add ${groupModal === "REFERRAL" ? "referral" : "sibling"}`}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {(() => {
          const formFields = (
            <>
          <div className={s.field}>
            <label className={s.label}>Discount type *</label>
            <select className={s.input} value={form.type}
                    onChange={e => setForm(f => ({
                      ...f,
                      type: e.target.value,
                      customName: e.target.value === "OTHERS" ? f.customName : "",
                      // Clear the linked-student pick when switching to a type
                      // that doesn't take one, so the form doesn't carry a
                      // ghost id that would only be sent if the admin flipped
                      // back. The save body filter already drops it, but
                      // clearing keeps the UI consistent.
                      linkedStudentId: isLinkedType(e.target.value) ? f.linkedStudentId : "",
                    }))}>
              {discountTypes.map(([v, l]) => {
                // Disable single-use types already present for this (student, SY).
                // Never disable the type the current form already has (edit case).
                const taken = !MULTI_USE_TYPES.has(v) && takenTypes.has(v) && v !== form.type;
                return (
                  <option key={v} value={v} disabled={taken}>
                    {l}{taken ? " · already added" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div className={s.field}>
            <label className={s.label}>Percentage (0-100) *</label>
            <input className={s.input} type="number" step="0.01" min="0" max="100"
                   value={form.percentage}
                   onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))}
                   placeholder="25" />
          </div>
          {form.type === "OTHERS" && (
            <div className={s.field} style={{ gridColumn: "1 / -1" }}>
              <label className={s.label}>Custom name *</label>
              <input className={s.input} type="text"
                     value={form.customName}
                     onChange={e => setForm(f => ({ ...f, customName: e.target.value }))}
                     placeholder='e.g. "Early Bird", "Staff Child"' />
              <div style={{ fontSize: "0.76rem", color: "#8a8070", marginTop: 4 }}>
                Required for Others — this is the label you&apos;ll see on the SOA
                to tell this one-off discount apart from another.
              </div>
            </div>
          )}
          {isLinkedType(form.type) && (
            <div className={s.field} style={{ gridColumn: "1 / -1" }}>
              <label className={s.label}>
                {form.type === "SIBLING" ? "Sibling (enrolled student) *" : "Referring student (enrolled) *"}
              </label>
              <select className={s.input} value={form.linkedStudentId}
                      onChange={e => setForm(f => ({ ...f, linkedStudentId: e.target.value }))}>
                <option value="">— select enrolled student —</option>
                {students
                  .filter(st => String(st.id) !== String(studentId))
                  .map(st => (
                    <option key={st.id} value={st.id}>
                      {st.lastName}, {st.firstName}
                      {st.studentNumber ? ` (${st.studentNumber})` : ""}
                    </option>
                  ))}
              </select>
              <div style={{ fontSize: "0.76rem", color: "#8a8070", marginTop: 4 }}>
                {form.type === "SIBLING"
                  ? "Confirms the sibling is actually enrolled in the school — picked from the roster, not free text."
                  : "Confirms the referring student is enrolled in the school — picked from the roster, not free text."}
              </div>
            </div>
          )}
          <div className={s.field} style={{ gridColumn: "1 / -1" }}>
            <label className={s.label}>Applies to *</label>
            <div style={{ display: "flex", gap: 16, padding: "8px 2px", flexWrap: "wrap" }}>
              {[
                ["appliesTuition", "Tuition"],
                ["appliesMisc",    "Miscellaneous"],
                ["appliesBooks",   "Books"],
              ].map(([key, lbl]) => (
                <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form[key]}
                         onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={s.field} style={{ gridColumn: "1 / -1" }}>
            <label className={s.label}>Notes</label>
            <input className={s.input} type="text"
                   value={form.notes}
                   onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                   placeholder="from public school" />
          </div>
            </>
          );

          // Add-new mode renders inline at the bottom of the manager. Edit
          // mode pops the same fields in a focused popup so the row list
          // stays clean and the user has unambiguous edit context.
          if (!editingId) {
            return (
              <form onSubmit={saveDiscount} className={s.form} style={{ marginTop: 16 }}>
                {formFields}
                <div className={s.modalActions} style={{ gridColumn: "1 / -1" }}>
                  <button type="button" className={s.btnGhost} onClick={onClose} disabled={busy}>Close</button>
                  <button type="submit" className={s.btnPrimary} disabled={busy}>
                    {busy ? "Saving…" : "Add discount"}
                  </button>
                </div>
              </form>
            );
          }

          const editingRow = localRows.find(r => r.id === editingId);
          const editingLbl = editingRow
            ? (() => {
                const l = (discountTypes.find(([v]) => v === editingRow.type) || [])[1] || editingRow.type;
                return editingRow.type === "OTHERS" && editingRow.customName ? `${l} · ${editingRow.customName}` : l;
              })()
            : "";
          // Portaled to <body> so the fixed-position overlay anchors to the
          // viewport, not the parent .modal (which has will-change: transform).
          // z-index 1100 sits above the base .modalOverlay z-index (1000).
          // stopPropagation on the backdrop prevents React's synthetic-event
          // bubbling from reaching the outer discount modal's onClose handler.
          return createPortal(
            <div className={s.modalOverlay}
                 style={{ zIndex: 1100 }}>
              <div className={s.modal}
                   style={{ maxWidth: 520 }}
                   onClick={e => e.stopPropagation()}>
                <div className={s.modalHead}>
                  <h3 className={s.modalTitle}>
                    Edit discount{editingLbl ? ` — ${editingLbl}` : ""}
                  </h3>
                  <button className={s.modalClose} type="button"
                          onClick={cancelEdit} disabled={busy} aria-label="Close">×</button>
                </div>
                {err && <div className={s.error}>{err}</div>}
                <form onSubmit={saveDiscount} className={s.form}>
                  {formFields}
                  <div className={s.modalActions} style={{ gridColumn: "1 / -1" }}>
                    <button type="button" className={s.btnGhost} onClick={cancelEdit} disabled={busy}>Cancel</button>
                    <button type="submit" className={s.btnPrimary} disabled={busy}>
                      {busy ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>,
            document.body
          );
        })()}

        {detailsRow && (() => {
          const r = detailsRow;
          const lbl = (discountTypes.find(([v]) => v === r.type) || [])[1] || r.type;
          const display = r.type === "OTHERS" && r.customName ? `${lbl} · ${r.customName}` : lbl;
          const linked = r.linkedStudentId != null ? studentByIdLocal[r.linkedStudentId] : null;
          const showLinked = isLinkedType(r.type);
          return (
            <div className={s.modalOverlay}
                 style={{ zIndex: 60 }}>
              <div className={s.modal}
                   style={{ maxWidth: 460 }}
                   onClick={e => e.stopPropagation()}>
                <div className={s.modalHead}>
                  <h3 className={s.modalTitle}>{display} · {Number(r.percentage) || 0}%</h3>
                  <button className={s.modalClose} type="button"
                          onClick={() => setDetailsRow(null)} aria-label="Close">×</button>
                </div>
                <div style={{ padding: "4px 2px", display: "grid", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: "0.78rem", color: "#8a8070", fontWeight: 600 }}>Notes</div>
                    <div style={{ marginTop: 4 }}>
                      {r.notes ? r.notes : <span style={{ color: "#a39880", fontStyle: "italic" }}>No notes.</span>}
                    </div>
                  </div>
                  {showLinked && (
                    <div>
                      <div style={{ fontSize: "0.78rem", color: "#8a8070", fontWeight: 600 }}>
                        {r.type === "SIBLING" ? "Sibling enrolled in school" : "Referring student"}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {linked ? (
                          <>
                            <div><b>{linked.lastName}, {linked.firstName}</b></div>
                            <div style={{ color: "#8a8070", fontSize: "0.85rem" }}>
                              ID: {linked.id}{linked.studentNumber ? ` · Student # ${linked.studentNumber}` : ""}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: "#c0392b", fontStyle: "italic" }}>
                            Linked student not found in roster (id {r.linkedStudentId ?? "—"}).
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className={s.modalActions} style={{ marginTop: 12 }}>
                  <button type="button" className={s.btnGhost} onClick={() => setDetailsRow(null)}>Close</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
